import React, { useState, useEffect, useRef, useCallback, useMemo, useSyncExternalStore } from 'react';
import { ChatWindow } from './components/ChatWindow';
import { NoticeBanner } from './components/NoticeBanner';
import { AttachmentDrawer } from './components/AttachmentDrawer';
import { Login } from './components/Login';
import { DownloadPage } from './components/DownloadPage';
import { HelpPage } from './components/help/HelpPage';
import { CreateGroupModal } from './components/CreateGroupModal';
import OnlineUsersPanel from './components/OnlineUsersPanel';
import { InviteMemberModal } from './components/InviteMemberModal';
import { MemberListModal } from './components/MemberListModal';
import { UserProfileModal } from './components/UserProfileModal';
import { AvatarSettingsModal } from './components/AvatarSettingsModal';
import { ChatDisclaimerModal } from './components/ChatDisclaimerModal';
import { ConnectionBanner } from './components/ConnectionBanner';
import { ConfirmDialog } from './components/ConfirmDialog';
import { RenameChannelModal } from './components/RenameChannelModal';
import { buildChannelMenuItems } from './components/channelContextMenu';
import { AdminConsole } from './components/AdminConsole';
import { AuditNotificationBanner } from './components/notifications/AuditNotificationBanner';
import { NotificationSettingsGuide } from './components/NotificationSettingsGuide';
import { TitleBar } from './components/TitleBar';
import { UpdateNotification } from './components/UpdateNotification';
import { PwaInstallBanner } from './components/PwaInstallBanner';
import { NotificationPermissionBanner } from './components/NotificationPermissionBanner';
import IncomingCallBanner from './components/IncomingCallBanner';
import VideoCallWindow from './components/VideoCallWindow';
import CallInviteModal from './components/CallInviteModal';
import { RemoteSupportRoot } from './components/remote/RemoteSupportRoot';
import { RemoteViewerWindow } from './components/remote/RemoteViewerWindow';
import { getRemoteViewerNonce } from './services/remoteViewerLink';
import { User, Message, Channel, Attachment, OnlineUser, IncomingCallInfo, ChannelCallStatus, MentionInput, ScheduledMessage, NotificationPrivacy, ActivityItem, PresenceManual, PresenceStatus, DEFAULT_AVATAR, DRAFT_CHANNEL_ID, isDraftChannel, handleAvatarError } from './types';
import { findDmPartner, STATUS_LABEL } from './utils/presence';
import { PresenceDot } from './components/PresenceDot';
import { resolveAvatarUrl } from './utils/serverUrl';
import { showWebNotification, syncChannelNotifications } from './utils/webNotification';
import { ChannelTypeIcon } from './components/ChannelListItem';
import { UserPlus, Users, Video, ChevronLeft, Search, X, FolderOpen, Menu } from 'lucide-react';
import { ContextMenu } from './components/ContextMenu';
import { MobileTabBar, MobileTab } from './components/MobileTabBar';
import { KeyboardViewportDebug } from './components/KeyboardViewportDebug';
import { MobileSettingsScreen } from './components/MobileSettingsScreen';
import { HomeScreen } from './components/HomeScreen';
import { ActivityPanel } from './components/ActivityPanel';
import { SystemFeedView } from './components/SystemFeedView';
import { CalendarScreen, CalendarFocusRequest } from './components/calendar/CalendarScreen';
import { CalendarModalsHost } from './components/calendar/CalendarModalsHost';
import { clearCalendarCache, getNoticeRef, onCalendarNavRequest, requestOpenEditor } from './services/calendarService';
import { todayKey } from './utils/calendarDate';
import { isSystemSourceTab } from './utils/systemSource';
import { activityService } from './services/activityService';
import { ForwardTargetModal } from './components/ForwardTargetModal';
import { ChatListScreen, ListFilter } from './components/ChatListScreen';
import { DesktopChatEmptyState } from './components/desktop/DesktopChatEmptyState';
import { PullToRefresh } from './components/PullToRefresh';
import { refreshApp } from './utils/appRefresh';
import { DesktopNavRail, DesktopTab } from './components/desktop/DesktopNavRail';
import { useIsMobile, isMobileViewport } from './hooks/useIsMobile';
import { useDesktopActivityHeartbeat } from './hooks/useDesktopActivityHeartbeat';
import { useMobileBackNav } from './hooks/useMobileBackNav';
import { useSoftKeyboardViewportFix } from './hooks/useSoftKeyboardViewportFix';
import { clearUserDirectoryCache, patchUserDirectoryAvatar } from './hooks/useAffiliateUserSearch';
import { clearChannelMemberIndex, invalidateChannelMemberIndex } from './hooks/useChannelMemberIndex';
import { clearAllDrafts } from './utils/chatDraft';
import { socketService } from './services/socketService';
import { apiFetch, dispatchUnauthorized, ApiError } from './services/apiClient';
import { subscribeToPush, unsubscribeFromPush, isWebKitPushDevice } from './services/webPushService';
import { useNotificationPermission } from './hooks/useNotificationPermission';
import { pickNotificationBanner } from './utils/pushSupport';
import { isWebNotifyEnabled } from './utils/webNotifyPref';
import { buildNotificationText, buildSystemNotificationText, normalizeNotificationPrivacy, DEFAULT_NOTIFICATION_PRIVACY } from './utils/notificationPrivacy';
import { callService } from './services/callService';
import { callController } from './services/callController';
import CallJoinBar from './components/CallJoinBar';
import { describeMediaNotice } from './utils/callMedia';
import { dbService } from './services/dbService';
import { scheduledMessageService, type ScheduledMessageBody } from './services/scheduledMessageService';
import { useDMChannel, channelService } from './services/channelService';
import {
  getDeviceId,
  getCachedDeviceId,
  getCachedNotifyEnabled,
  registerDevice,
  getPlatform,
} from './services/deviceService';
import { clearSavedPassword } from './services/credentialService';
import { isTauri, flashTaskbar, setTaskbarBadge, refreshTaskbarBadge } from './utils/tauri';
import { shouldShowDisclaimer, recordDisclaimerAgreement } from './utils/disclaimer';
import { authService, getClientType } from './services/authService';
import { clearResumeIdentity, loadResumeIdentity } from './services/deviceIdentity';
import { checkThisDevice } from './services/deviceTrustService';
import { fetchUploadPolicy, setUploadPolicy } from './services/uploadPolicyService';
import { fetchBlockedExtensions, setBlockedExtensions } from './services/blockedExtensionService';
import { useToast } from './contexts/ToastContext';

/**
 * JWT 토큰의 exp 클레임을 검사하여 만료 여부 반환.
 * - 토큰 없음/파싱 실패: 만료로 간주 (보수적)
 * - exp 없음: 만료 아님 (검증 불가)
 */
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * 앱 아이콘 배지(안읽음 숫자) 설정 — 웹/PWA는 홈 화면 아이콘, Tauri는 작업표시줄 아이콘.
 *
 * - 설치된 PWA에서만 실제로 보인다. 일반 브라우저 탭에서는 호출이 성공해도 표시 대상이 없다.
 * - iOS는 16.4+ & 홈 화면 추가 & 알림 권한 granted를 모두 만족해야 한다.
 * - Tauri(Windows)는 작업표시줄 아이콘 우하단 오버레이 배지로 같은 숫자를 칠한다.
 *   빨간 점멸(flash_taskbar)과 짝을 이루는 표시라 숫자와 점멸이 어긋나지 않게 함께 다룬다.
 * - 상한 처리를 하지 않는다 — 인앱 배지의 '99+'는 좁은 UI 폭 때문이고 OS 배지는 런처가 알아서 줄인다.
 *   (작업표시줄 오버레이만은 32px 안에 3자가 한계라 setTaskbarBadge가 '99+'로 접는다)
 */
function setAppBadgeCount(count: number): void {
  if (isTauri()) {
    void setTaskbarBadge(count);
    // 총계가 0이면 빨간 점멸도 함께 끈다 — 다른 기기에서 다 읽었는데 PC만 계속 깜빡이면
    // 숫자(없음)와 점멸이 어긋난다. clear_notifications가 총계 0에서만 토스트를 지우는 것과 같은 규칙.
    if (count === 0) void flashTaskbar(true);
    return;
  }
  if (typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return;
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  // 미설치 PWA·권한 미허용이면 reject된다 — 무시
  (count > 0 ? nav.setAppBadge?.(count) : nav.clearAppBadge?.())?.catch(() => {});
}

/**
 * 이미 읽은 대화의 시스템 알림을 알림 센터에서 치운다.
 *
 * - **웹/PWA**: 채널 단위로 정확히 지운다. SW가 띄운 웹푸시 알림도 페이지와 같은 registration이
 *   소유하므로 `syncChannelNotifications` 한 번에 함께 회수된다.
 * - **Tauri(Windows)**: 채널 단위로 지울 수 없다 — `tauri-winrt-notification 0.7`에 tag 세터가
 *   없어 띄운 토스트에 채널 식별자를 심을 방법이 없다. 앱 단위 일괄 삭제뿐이므로 **총계가 0일
 *   때만** 호출한다. 채널 A만 읽고 B가 남아 있으면 A의 토스트도 함께 남는다(의도된 한계).
 */
function clearReadNotifications(unread: Record<number, number>, totalUnread: number): void {
  if (isTauri()) {
    if (totalUnread > 0) return;
    import('@tauri-apps/api/core')
      .then(({ invoke }) => invoke('clear_notifications'))
      .catch(() => {});
    return;
  }
  void syncChannelNotifications(unread);
}

/**
 * 채널 목록 미리보기(lastMessage)의 단조성 가드.
 *
 * 미리보기가 "최신일 때도, 이전 메시지일 때도" 있던 버그의 핵심 수정이다. 예전에는
 * message:new / message:notify 두 경로가 **무조건 덮어써서**, 서로 다른 메시지에 대해
 * 이벤트가 엇갈려 도착하면 이전 메시지로 고착됐다. 이제 메시지 id가 더 클 때만 적용한다.
 *
 * id 비교는 반드시 Number()로 정규화한다 — messages.id는 PG BIGINT라 문자열로 온다.
 * 구버전 서버(lastMessageId 없음)에서는 기존값이 0으로 취급돼 항상 적용된다(fail-open).
 */
function applyLastMessage(
  ch: Channel,
  next: { id: number | string | null | undefined; text: string | null; at: string | null }
): Channel {
  const nextId = Number(next.id);
  if (!Number.isFinite(nextId)) return ch;
  if (nextId <= Number(ch.lastMessageId ?? 0)) return ch;
  return { ...ch, lastMessageId: String(nextId), lastMessage: next.text, lastMessageAt: next.at };
}

/**
 * 서버 채널 목록 스냅샷을 로컬 상태에 병합한다.
 *
 * 스냅샷(channel:list-sync·REST GET /api/channels)을 그대로 setChannels 하면, 서버가
 * 목록을 조회한 뒤 발송하기까지의 사이에 도착한 메시지가 **낡은 값에 지워진다**.
 * 탭을 다시 볼 때마다 user:online이 재발송되고 재연결 때는 REST와 소켓이 서로 다른 시점의
 * 스냅샷으로 경쟁하기 때문에, 어느 쪽이 이길지 비결정적이었다.
 *
 * 그래서 미리보기 3개 필드만 로컬이 더 최신이면 지키고, 나머지(안읽음·이름·역할·고정 등)는
 * 서버 값을 그대로 채택한다 — 안읽음은 서버가 권위라는 기존 규칙을 깨지 않는다.
 */
function mergeChannelSnapshot(incoming: Channel[], prev: Channel[]): Channel[] {
  if (prev.length === 0) return incoming;
  const prevById = new Map(prev.map((ch) => [String(ch.id), ch]));
  return incoming.map((ch) => {
    const local = prevById.get(String(ch.id));
    if (!local) return ch;
    if (Number(local.lastMessageId ?? 0) <= Number(ch.lastMessageId ?? 0)) return ch;
    return {
      ...ch,
      lastMessageId: local.lastMessageId,
      lastMessage: local.lastMessage,
      lastMessageAt: local.lastMessageAt,
    };
  });
}

// 새로고침 화면 복원용 localStorage 키. 모바일/데스크톱을 별도 키로 두는 이유는
// mobileTab·desktopTab 상호 참조 금지 규칙(767px 리사이즈 시 상태 오염 방지)과 같다 —
// 각 initializer는 자기 플랫폼 키만 읽는다. 검증 실패·파싱 실패는 전부 기본값으로 접는다.
const MOBILE_TAB_KEYS: readonly MobileTab[] = ['home', 'chat', 'calendar', 'org', 'settings'];
const DESKTOP_TAB_KEYS: readonly DesktopTab[] = ['home', 'chat', 'calendar', 'activity', 'org'];

function readMobileNav(): { tab: MobileTab; chatOpen: boolean } {
  try {
    const parsed = JSON.parse(localStorage.getItem('mobileNav') ?? 'null');
    const tab: MobileTab = MOBILE_TAB_KEYS.includes(parsed?.tab) ? parsed.tab : 'home';
    // 채팅방 전체화면 복원은 복원할 채널이 있을 때만 — 없으면 빈 전체화면이 뜬다
    const chatOpen = parsed?.chatOpen === true && !!localStorage.getItem('activeChannel');
    // openMobileChat()의 불변식(채팅방 열림 ⇒ 대화방 탭) 유지
    return { tab: chatOpen ? 'chat' : tab, chatOpen };
  } catch {
    return { tab: 'home', chatOpen: false };
  }
}

/**
 * 이 기기에 저장돼 있던 같은 계정의 알림 표시 수준. 로그인 응답에 그 필드가 없을 때(서버의
 * 프로필 조회 실패)만 쓴다 — 사번이 다르면 남의 설정이므로 물려받지 않는다.
 * 명시적 로그아웃 뒤에는 currentUser 키가 지워져 복구되지 않는다(그때는 서버 값이 권위).
 */
function readSavedNotifyPrivacy(userId: string): NotificationPrivacy | undefined {
  try {
    const saved = JSON.parse(localStorage.getItem('currentUser') ?? 'null');
    if (!saved || String(saved.id) !== userId) return undefined;
    const value = saved.notificationPrivacy;
    // 값이 없으면 "모름"을 그대로 유지한다 — 여기서 'full'로 접으면 저장된 적 없는 상태가
    // 기본값으로 굳어 다시 localStorage에 쓰인다. 알 수 없는 값도 되쓰지 않는다.
    return value === 'full' || value === 'sender' || value === 'hidden' ? value : undefined;
  } catch {
    return undefined;
  }
}

function readDesktopNav(): DesktopTab {
  try {
    const parsed = JSON.parse(localStorage.getItem('desktopNav') ?? 'null');
    return DESKTOP_TAB_KEYS.includes(parsed?.tab) ? parsed.tab : 'chat';
  } catch {
    return 'chat';
  }
}

const App: React.FC = () => {
  // 공개 다운로드 페이지: 로그인/인증 불필요. 모든 Hook 호출보다 먼저 분기
  // (pathname은 페이지 로드 동안 고정이므로 Hooks 순서가 항상 동일하게 유지됨)
  if (window.location.pathname === '/download') {
    return <DownloadPage />;
  }

  // 공개 사용 설명서: 로그인 없이도 열람 가능 (앱 안에서는 사이드바 물음표 버튼으로 오버레이 표시)
  if (window.location.pathname === '/help') {
    return <HelpPage />;
  }

  // 원격지원 담당자의 원격 전용 창(remoteViewerLink.ts). 로그인·소켓 없이 메인 창과 BroadcastChannel로만 이야기한다.
  // ⚠ 반드시 아래 훅·로그인 경로보다 앞 — 이 창에 RemoteSupportHost가 뜨면 부팅 1회 rs_session_end가
  //   같은 PC에서 진행 중인 직원 쪽 세션의 테두리·게이트를 걷어 버린다
  if (getRemoteViewerNonce()) {
    return <RemoteViewerWindow />;
  }

  const { showToast } = useToast();

  // 소프트 키보드가 layout viewport를 줄이지 못하는 브라우저(iOS 등)에서 헤더 밀림 보정.
  // 로그인 화면 입력에서도 동작해야 하므로 currentUser 분기와 무관한 최상위에 둔다.
  useSoftKeyboardViewportFix();

  // localStorage에서 사용자 정보 복원 (만료된 토큰은 미리 정리)
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const token = localStorage.getItem('authToken');
    if (isTokenExpired(token)) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('activeChannel');
      localStorage.removeItem('mobileNav');
      localStorage.removeItem('desktopNav');
      clearAllDrafts(); // 작성 중이던 초안은 세션 콘텐츠다 — 만료 세션의 것을 남기지 않는다
      return null;
    }
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  // 기기 신뢰: 토큰이 없거나 만료된 웹 기기는 로그인 화면을 띄우기 전에 기기 키로 세션 재개를 먼저 시도한다
  // (승인 웹 기기 토큰은 24시간이라 하루 넘게 안 연 폰은 토큰이 죽어 있다 — 재개가 기존 30일 UX를 잇는다).
  // 직접 로그아웃하면 재개 신원을 지우므로 여기서 걸리지 않는다.
  const [isResumingSession, setIsResumingSession] = useState<boolean>(
    () => !isTauri() && !localStorage.getItem('currentUser') && !!loadResumeIdentity()
  );
  const [activeChannel, setActiveChannel] = useState<Channel | null>(() => {
    const saved = localStorage.getItem('activeChannel');
    return saved ? JSON.parse(saved) : null;
  });
  const [channels, setChannels] = useState<Channel[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMore, setHasMore] = useState(true);
  // 채널별 '최신 창' 스냅샷. 재진입 시 서버 응답을 기다리는 동안 빈 화면 대신 이전 내용을 보여준다.
  // 과거 열람(날짜 점프) 창은 절대 넣지 않는다 — 넣으면 재진입 때 옛날 대화가 뜬다.
  const messagesCacheRef = useRef<Map<number, Message[]>>(new Map());
  const MESSAGE_CACHE_LIMIT = 10;
  const cacheMessageWindow = useCallback((channelId: number, window: Message[]) => {
    const cache = messagesCacheRef.current;
    cache.delete(channelId); // 재삽입으로 최근 사용 순서를 유지 (Map은 삽입 순서 보존)
    cache.set(channelId, window);
    while (cache.size > MESSAGE_CACHE_LIMIT) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
  }, []);
  // 채널에서 나가거나(강퇴 포함) 아카이브되면 그 방의 최신 창 스냅샷은 더 이상 신뢰할 수 없다.
  // DM은 나갔던 방을 같은 id로 재사용하므로(POST /channels/dm), 지우지 않으면 재입장 후
  // 서버가 joined_at으로 숨긴 과거 대화가 캐시에서 되살아난다.
  const dropMessageCache = useCallback((channelId: number) => {
    messagesCacheRef.current.delete(Number(channelId));
  }, []);
  // 날짜 점프: 과거 시점을 보고 있을 때만 아래(최신) 방향 페이징이 열린다
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  // nonce를 함께 담는 이유: 다른 날짜를 골랐는데 앵커가 같은 메시지로 수렴해도 스크롤이 다시 일어나야 한다
  const [jumpTarget, setJumpTarget] = useState<{ id: number; nonce: number } | null>(null);
  const jumpNonceRef = useRef(0);
  // 소켓 핸들러·포커스 핸들러에서 읽어야 하므로 state가 아닌 ref
  // (과거를 열람하는 동안에는 새 메시지 append와 읽음 처리를 막는다)
  const isViewingHistoryRef = useRef(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<number, number>>({});
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [dmPartnerName, setDmPartnerName] = useState<string | null>(() => {
    const saved = localStorage.getItem('activeChannel');
    if (saved) {
      const ch = JSON.parse(saved);
      return ch.type === 'dm' ? ch.name : null;
    }
    return null;
  });
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isMemberListModalOpen, setIsMemberListModalOpen] = useState(false);
  // 상대방 프로필 모달 (null = 닫힘). 사람을 누르는 모든 진입점이 openUserProfile로 들어온다
  const [profileUser, setProfileUser] = useState<User | null>(null);
  // window.confirm은 Tauri WebView2에서 동작하지 않으므로 자체 확인 모달 사용
  // 나가기 대상 채널 (null = 모달 닫힘) — 목록 컨텍스트 메뉴에서 임의 채널을 지정할 수 있다
  const [leaveTarget, setLeaveTarget] = useState<Channel | null>(null);
  // 이름 변경 대상 그룹 대화방 (null = 모달 닫힘) — leaveTarget과 같은 구조
  const [renameTarget, setRenameTarget] = useState<Channel | null>(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  // 로그아웃 후에도 웹푸시 알림을 유지할지 (모달 열릴 때마다 기본 켜짐, 공용 PC에서는 끄고 로그아웃)
  const [keepPushOnLogout, setKeepPushOnLogout] = useState(true);
  const [isAvatarSettingsOpen, setIsAvatarSettingsOpen] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  // 프로필 메뉴에서 안내를 다시 열어본 경우 — 동의 기록을 갱신하지 않고 닫기만 한다
  const [isDisclaimerReviewOpen, setIsDisclaimerReviewOpen] = useState(false);
  const [isAdminConsoleOpen, setIsAdminConsoleOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // 모바일 내비게이션: 홈/대화방/온라인/설정 4탭 + 채팅방 전체화면 여부 (데스크톱 렌더링은 이 state를 참조하지 않음)
  // '온라인'(조직도)은 예전 isMobileOrgOpen 오버레이가 아니라 탭이다 — 탭 전환이 곧 닫기라 별도 boolean이 없다
  //
  // 768px 미만(isNarrow)의 배치는 플랫폼으로 갈린다:
  // - 웹/PWA → 모바일 배치(isMobile): 하단 4탭 + 채팅방 전체화면, 아래 mobileTab/isMobileChatOpen state
  // - PC앱   → 컴팩트 배치(isCompactDesktop): 카톡 PC처럼 rail + 목록 **또는** 채팅 한 칸. state는 데스크톱 것
  //   (desktopTab/isDesktopChatOpen/systemFeed)을 그대로 쓰고 렌더만 한 칸으로 줄인다 — 넓은 창과 같은 state라
  //   창을 늘렸다 줄여도 열린 방·읽음 판정이 튀지 않는다. 판정은 compactShowsMain 하나(렌더부 주석)
  const isNarrow = useIsMobile();
  const isMobile = isNarrow && !isTauri();
  const isCompactDesktop = isNarrow && isTauri();
  // 콜백 identity를 지키려고 handleDesktopTabChange는 ref로 읽는다
  const isCompactDesktopRef = useRef(isCompactDesktop);
  isCompactDesktopRef.current = isCompactDesktop;
  // 새로고침 시 보던 화면 복원 — 저장은 아래 mobileNav/desktopNav 저장 effect, 정리는 handleLogout
  const [mobileTab, setMobileTab] = useState<MobileTab>(() => readMobileNav().tab);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(() => readMobileNav().chatOpen);
  // 데스크톱 3컬럼: rail 탭(가운데 목록 패널만 스왑) + 우측 채팅 영역 X 토글.
  // 모바일 state(mobileTab 등)와 완전 분리 — 상호 참조 금지 (767px 리사이즈 시 상태 오염 방지)
  const [desktopTab, setDesktopTab] = useState<DesktopTab>(() => readDesktopNav());
  // 좁은 PC앱 창으로 켜지면 마지막 대화방이 아니라 목록부터 보인다(카톡 PC와 같음). 넓은 창은 예전처럼 열림.
  // 홈 탭으로 켜지면 닫힌 채 시작한다 — 불변식 "홈 탭 ⇒ 채팅 닫힘"(handleDesktopTabChange). 열린 채 시작하면
  // 넓은 홈(homeWide)이 아니라 예전 3컬럼으로 뜬다.
  // 캘린더 탭도 홈과 같다 — rail + 캘린더 한 칸(calendarWide)이라 채팅을 접은 채 시작한다.
  // 알림 탭도 접은 채 시작한다 — 오른쪽은 누른 알림의 대화를 여는 자리다(handleDesktopTabChange)
  const [isDesktopChatOpen, setIsDesktopChatOpen] = useState(() => {
    const nav = readDesktopNav();
    return !(isTauri() && isMobileViewport()) && nav !== 'home' && nav !== 'calendar' && nav !== 'activity';
  });
  // 대화방 탭 복원 판정용 — 초안 DM은 id가 0이라 id로 판정하면 안 된다(객체 존재로 본다)
  const hasActiveChannelRef = useRef(!!activeChannel);
  hasActiveChannelRef.current = !!activeChannel;
  // 통합 알림함 (Phase 2): 모바일 전체화면 오버레이 — 조직도와 동일하게 depth 1로 취급
  const [isMobileActivityOpen, setIsMobileActivityOpen] = useState(false);
  // 출처별 봇 대화(전자결재 등 시스템 알림 타임라인) — 채널이 아닌 가상 뷰. 데스크톱은 우측 채팅 영역,
  // 모바일은 알림함 위 전체화면. channels/activeChannel/localStorage에 절대 넣지 않는다(SystemFeedView 주석).
  // seq는 같은 대화를 연 채로 다른 알림을 눌러도 다시 포커스하게 하는 카운터.
  const [systemFeed, setSystemFeed] = useState<{ source: string; focusId: number | null; seq: number } | null>(null);
  const systemFeedRef = useRef(systemFeed);
  systemFeedRef.current = systemFeed;
  const openSystemFeed = useCallback((source: string, focusId?: number | null) => {
    setSystemFeed(prev => ({ source, focusId: focusId ?? null, seq: (prev?.seq || 0) + 1 }));
    // 우측(모바일은 전체화면)이 피드로 바뀐다 = 채팅방은 비가시. 가시성 전환 effect가
    // emitChannelBlur·읽음 정지를 알아서 하고 안읽음 뱃지도 정상적으로 오른다(X 닫기와 같은 경로).
    setIsDesktopChatOpen(false);
    setIsMobileChatOpen(false);
  }, []);
  const closeSystemFeed = useCallback(() => setSystemFeed(null), []);
  // 모바일 헤더 뒤로가기는 history.back()으로 닫는다 — setState로 닫으면 useMobileBackNav가 go(-1)로 entry를
  // 회수하고, 도착한 popstate(depth 1)를 applyNavDepth가 '대화방 목록'으로 해석해 알림함까지 닫아 버린다.
  // popstate 경로로 닫으면 applyNavDepth의 봇 대화 분기가 알림함을 남긴다(하드웨어 뒤로가기와 같은 경로).
  const closeMobileSystemFeed = useCallback(() => {
    if (!isTauri() && (window.history.state as { inaNav?: unknown } | null)?.inaNav === 2) {
      window.history.back();
    } else {
      setSystemFeed(null);
    }
  }, []);
  const openMobileChat = useCallback(() => {
    setMobileTab('chat'); // 온라인/설정 탭에서 DM 진입 시 대화방 탭으로 자동 이동
    setIsMobileChatOpen(true);
    setIsMobileActivityOpen(false); // 알림함에서 항목 탭 → 채팅방 진입 시 알림함 자동 닫힘
    // 방에 들어가면 봇 대화를 닫는다 — 채팅방 진입 경로가 전부 여기를 지나므로 개별 경로에 심지 않는다
    setSystemFeed(null);
    // 채팅방 진입의 공용 훅이므로 데스크톱 X로 닫은 채팅 영역도 여기서 재열림
    // (모든 setActiveChannel 경로가 이 함수를 거친다는 CLAUDE.md 불변조건에 편승)
    setIsDesktopChatOpen(true);
    // 홈 탭은 채팅 영역을 접어 두는 화면이다 — 방에 들어가면 대화방 탭으로 빠져나온다.
    // 모바일의 "채팅방 열림 ⇒ 대화방 탭" 불변식(readMobileNav 주석)을 데스크톱에도 세우는 것.
    // 대화방/알림/조직도 탭은 그대로 둔다 — 조직도에서 여러 명에게 연달아 DM을 여는 흐름이 깨진다.
    // ⚠ 함수형 업데이터라 desktopTab을 deps로 끌어오지 않는다(콜백 identity 유지).
    // 캘린더 탭도 채팅을 접는 화면이라 같은 규칙이다(캘린더 상세의 '대화방에 공유' 뒤 방 열기 등)
    setDesktopTab(prev => (prev === 'home' || prev === 'calendar' ? 'chat' : prev));
  }, []);
  const handleMobileTabChange = useCallback((tab: MobileTab) => {
    setIsMobileActivityOpen(false);
    setMobileTab(tab);
  }, []);
  const goToMobileChats = useCallback(() => handleMobileTabChange('chat'), [handleMobileTabChange]);
  const openMobileOrg = useCallback(() => handleMobileTabChange('org'), [handleMobileTabChange]);
  const openMobileActivity = useCallback(() => setIsMobileActivityOpen(true), []);
  // 모바일 홈 '안읽음 보기' — 모바일 ChatListScreen은 CSS로만 숨어 계속 마운트돼 있어 initialFilter가
  // 다시 읽히지 않는다(데스크톱의 chatListFilterRequest와 다른 이유). seq로 같은 필터도 매번 새 요청이 된다
  const [mobileChatFilterRequest, setMobileChatFilterRequest] = useState<{ filter: ListFilter; seq: number } | null>(null);
  const showMobileUnreadChats = useCallback(() => {
    setMobileChatFilterRequest(prev => ({ filter: 'unread', seq: (prev?.seq || 0) + 1 }));
    handleMobileTabChange('chat');
  }, [handleMobileTabChange]);
  // rail 탭 전환. 홈은 rail + 홈 한 칸(homeWide)이라 우측 채팅 영역을 접는다.
  // 닫힘 = 비가시라 가시성 전환 effect가 emitChannelBlur까지 알아서 처리하고
  // 안읽음 뱃지도 정상적으로 오른다 — 여기서 따로 부를 것이 없다.
  // 대화방 탭에 들어오면 보던 대화를 다시 연다(Slack·Teams) — 홈이 채팅을 접으므로, 없으면 홈 → 대화방마다
  // 오른쪽이 빈 화면이었다. 다시 열리면 가시성 effect가 focus·읽음·catchUpLatestMessages를 그대로 처리한다.
  // 봇 대화가 떠 있었으면 닫는다(열린 채 두면 채팅이 '보임'으로 판정되는데 화면엔 봇 대화가 그려진다).
  // 컴팩트 배치(좁은 PC앱)는 한 칸뿐이라 어느 탭을 눌러도 채팅을 닫아 그 탭의 목록을 보여 준다.
  // 알림 탭도 들어올 때 채팅을 닫는다(Slack '활동') — 보던 방이 그대로 남으면 알림과 무관한 대화가 오른쪽을
  // 차지했다. 알림을 누르면 openMobileChat이 탭을 그대로 둔 채 오른쪽에 그 대화를 연다.
  const handleDesktopTabChange = useCallback((tab: DesktopTab) => {
    setDesktopTab(tab);
    if (tab === 'home' || tab === 'calendar' || tab === 'activity' || isCompactDesktopRef.current) {
      setIsDesktopChatOpen(false);
      setSystemFeed(null);
    } else if (tab === 'chat' && hasActiveChannelRef.current) {
      setIsDesktopChatOpen(true);
      setSystemFeed(null);
    }
  }, []);
  // 대화방 탭 진입 경로(rail·홈 즐겨찾기 '추가'·'전체 보기'·조직도 닫기)는 전부 위 함수를 거친다 — 복원 규칙이 한 곳에 있게
  const goToDesktopChats = useCallback(() => handleDesktopTabChange('chat'), [handleDesktopTabChange]);
  const closeDesktopOrg = goToDesktopChats;
  const openDesktopOrg = useCallback(() => handleDesktopTabChange('org'), [handleDesktopTabChange]);
  const openDesktopActivity = useCallback(() => handleDesktopTabChange('activity'), [handleDesktopTabChange]);
  // 넓은 홈 '안읽음 보기' — 대화방 탭을 '안읽음' 필터로 연다. 데스크톱 ChatListScreen은 탭 전환마다 새로
  // 마운트되므로 initialFilter로 충분하고, 마운트가 소비하면 아래 effect가 비운다(다음 진입은 다시 '전체')
  const [chatListFilterRequest, setChatListFilterRequest] = useState<ListFilter | null>(null);
  const showUnreadChats = useCallback(() => {
    setChatListFilterRequest('unread');
    handleDesktopTabChange('chat');
  }, [handleDesktopTabChange]);
  useEffect(() => {
    if (desktopTab === 'chat' && chatListFilterRequest) setChatListFilterRequest(null);
  }, [desktopTab, chatListFilterRequest]);
  // 알림함을 특정 탭으로 연다 — 시스템 알림(전자결재 등)의 토스트·웹푸시 클릭과 ?activity= 딥링크 공용.
  // seq는 같은 탭을 연달아 요청해도 ActivityPanel이 다시 전환하게 하는 카운터다.
  const [activityTabRequest, setActivityTabRequest] = useState<{ tab: string; seq: number } | null>(null);
  // 캘린더 탭을 특정 날짜(·일정)로 연다 — 홈 미니 캘린더 '더 보기'·상세의 '캘린더에서 보기'·일정 알림 클릭 공용.
  // 모바일·데스크톱 state를 둘 다 맞춘다(한 배치만 렌더되므로 상호 참조가 아니다 — openActivityTab과 같은 방식).
  // seq는 같은 날짜를 다시 요청해도 CalendarScreen이 다시 이동하게 하는 카운터다.
  const [calendarFocus, setCalendarFocus] = useState<CalendarFocusRequest | null>(null);
  const openCalendarAt = useCallback((date: string, eventId?: string | null) => {
    setCalendarFocus(prev => ({ date, eventId: eventId ?? null, seq: (prev?.seq || 0) + 1 }));
    handleDesktopTabChange('calendar');
    setIsMobileChatOpen(false);
    setIsMobileActivityOpen(false);
    setSystemFeed(null);
    setMobileTab('calendar');
  }, [handleDesktopTabChange]);
  const openCalendarTab = useCallback(() => {
    handleDesktopTabChange('calendar');
    setIsMobileActivityOpen(false);
    setMobileTab('calendar');
  }, [handleDesktopTabChange]);
  useEffect(() => onCalendarNavRequest((req) => openCalendarAt(req.date, req.eventId)), [openCalendarAt]);
  // 일정 알림 클릭 — 토스트·웹 알림·SW·알림함 행이 전부 알림 id만 들고 온다. 이동 날짜는 calendarService가
  // activity:new·알림함 목록에서 기억해 둔 ref에서 찾고, 모르면(SW 콜드 스타트) 오늘을 연다
  const openCalendarFromNotice = useCallback((focusId?: number | null) => {
    const ref = getNoticeRef(focusId ?? null);
    if (focusId) activityService.markRead(focusId).catch(() => {});
    openCalendarAt(ref?.date || todayKey(), ref?.eventId || null);
  }, [openCalendarAt]);
  const openActivityTab = useCallback((tab: string, focusId?: number | null) => {
    if (tab === 'calendar') {
      openCalendarFromNotice(focusId);
      return;
    }
    setActivityTabRequest(prev => ({ tab, seq: (prev?.seq || 0) + 1 }));
    // 알림 탭 진입 규칙(채팅 닫힘)을 rail 클릭과 같게 — 봇 대화는 아래에서 다시 연다(나중 setState가 이긴다)
    handleDesktopTabChange('activity');
    // 모바일 알림함 오버레이는 채팅방이 닫혀 있어야 보인다(렌더 조건 !isMobileChatOpen)
    setIsMobileChatOpen(false);
    setIsMobileActivityOpen(true);
    // 시스템 출처(전자결재 등) 알림 클릭은 그 출처의 봇 대화까지 연다 — 알림함은 그 아래(모바일: 뒤로가기 도착점)
    if (isSystemSourceTab(tab)) openSystemFeed(tab, focusId);
  }, [openSystemFeed, openCalendarFromNotice, handleDesktopTabChange]);
  // 알림함 시스템 알림 행 → 출처별 봇 대화. 일정 알림만은 캘린더로 간다
  const handleOpenSystemFeed = useCallback((source: string, focusId?: number | null) => {
    if (source === 'calendar') openCalendarFromNotice(focusId);
    else openSystemFeed(source, focusId);
  }, [openSystemFeed, openCalendarFromNotice]);
  // 안드로이드 하드웨어 뒤로가기: 채팅방 → 대화방 목록 → 홈 → 앱 종료 순으로 단계적 이동
  const applyNavDepth = useCallback((d: number) => {
    // 봇 대화(depth 2)에서 뒤로가기 → 대화만 닫고 그 아래 알림함으로 돌아간다
    if (systemFeedRef.current) {
      if (d >= 2) return;
      setSystemFeed(null);
      if (d === 1) {
        setIsMobileActivityOpen(true);
        return;
      }
    }
    // 채널이 닫힌 뒤 forward로 depth 2 entry에 도달하면 목록(1)으로 강등
    if (d >= 2 && activeChannelIdRef.current) {
      setMobileTab('chat');
      setIsMobileChatOpen(true);
    } else if (d >= 1) {
      setMobileTab('chat');
      setIsMobileChatOpen(false);
    } else {
      setMobileTab('home');
      setIsMobileChatOpen(false);
    }
    setIsMobileActivityOpen(false);
  }, []);
  useMobileBackNav({
    enabled: isMobile && !!currentUser, // isMobile은 이미 웹/PWA 전용(PC앱은 컴팩트 배치)
    depth: isMobileChatOpen || systemFeed ? 2 : (mobileTab !== 'home' || isMobileActivityOpen ? 1 : 0),
    applyDepth: applyNavDepth,
  });
  // 하단 탭바 '대화방' 배지: 가시 채널의 안읽음 합계
  // 알림 배너는 하나만 뜬다. 예전엔 두 배너가 무조건 형제로 렌더되고 각자 7일 쿨다운을 가져
  // 동시에 뜨거나, 둘 다 닫히면 일주일간 아무 안내도 남지 않았다.
  const { permission: notifyPermission, capability: pushCapability } = useNotificationPermission();
  // 세 번째 인자: "이 기기에서 알림 받기"를 끈 기기에는 권한·설치 배너로 조르지 않는다
  const notificationGate = pickNotificationBanner(pushCapability, notifyPermission, isWebNotifyEnabled());

  const totalUnread = useMemo(
    () => channels.reduce((sum, ch) => sum + (unreadCounts[ch.id] || 0), 0),
    [channels, unreadCounts]
  );

  // 말풍선 아바타의 접속 점 — 지금 보이는 메시지의 발신자만 추려 서명 문자열로 압축한다.
  // onlineUsers를 그대로 ChatWindow에 내리면 사내 누가 접속만 해도 React.memo(ChatWindow)가
  // 깨져 메시지 목록 전체가 리렌더된다(ChatWindow의 onSetNotice 주석과 같은 이유).
  // onlineUsers에는 오프라인이 들어오지 않으므로(onUserStatusChanged가 제거한다) 조회 실패가 곧 오프라인이다.
  const senderPresenceKey = useMemo(() => {
    const byId = new Map<string, string>();
    onlineUsers.forEach((u) => byId.set(String(u.userId), u.status));
    const seen = new Set<string>();
    const parts: string[] = [];
    messages.forEach((m) => {
      const id = String(m.senderId);
      if (seen.has(id)) return;
      seen.add(id);
      parts.push(`${id}:${byId.get(id) ?? 'offline'}`);
    });
    return parts.sort().join('|');
  }, [onlineUsers, messages]);

  // 서명이 같으면 같은 Map 참조를 유지한다 — 이 방과 무관한 접속 변화는 ChatWindow에 닿지 않는다
  const senderPresence = useMemo(() => {
    const map = new Map<string, OnlineUser['status']>();
    if (!senderPresenceKey) return map;
    senderPresenceKey.split('|').forEach((part) => {
      const i = part.lastIndexOf(':');
      map.set(part.slice(0, i), part.slice(i + 1) as OnlineUser['status']);
    });
    return map;
  }, [senderPresenceKey]);
  // 채널 목록을 이번 세션에서 한 번이라도 받았는가. 기동 직후엔 channels가 비어 totalUnread가 0이라
  // 이 게이트가 없으면 앱을 열자마자 서비스워커가 올려둔 배지를 지웠다가 동기화 후 되살리는
  // 깜빡임이 생긴다 (오프라인 기동이면 지워진 채로 남는다).
  const [channelsSynced, setChannelsSynced] = useState(false);
  // PWA 앱 아이콘 배지 — 인앱 탭바 배지와 같은 소스(totalUnread)를 써서 앱 밖 숫자와
  // 앱 안 숫자가 어긋나지 않게 한다. 안읽음 증감 경로가 10곳이 넘으므로 각 경로에 호출을
  // 심지 않고 파생 값에만 반응시킨다. 앱이 떠 있는 동안의 배지는 전적으로 이 effect가 책임지고,
  // 앱이 종료된 동안은 service-worker.js의 push 핸들러가 담당한다.
  useEffect(() => {
    if (!currentUser) return; // 로그아웃 클리어는 handleLogout이 직접 한다
    if (!channelsSynced) return;
    setAppBadgeCount(totalUnread);
  }, [currentUser, channelsSynced, totalUnread]);

  // 읽은 대화의 시스템 알림 정리 — 배지와 똑같은 이유로 파생값에만 반응시킨다.
  // 읽음 경로가 markChannelRead 8곳 + 다른 기기 읽음(channel:self-read) + list-sync 스냅샷으로
  // 흩어져 있어, 각 지점에 알림 닫기를 심으면 반드시 한두 곳이 누락된다.
  // channelsSynced 게이트는 여기서 배지보다 더 중요하다 — 기동 직후엔 unreadCounts가 비어
  // 모든 채널이 0으로 보이므로, 게이트가 없으면 앱을 여는 순간 안 읽은 알림까지 지워 버린다.
  // 게이트가 deps에 있어 false→true 전이가 곧 '부팅 시 1회 sweep'을 겸한다 — 다른 기기에서
  // 이미 읽어 0으로 동기화된 채널의 stale 알림이 앱을 여는 시점에 정리된다.
  useEffect(() => {
    if (!currentUser || !channelsSynced) return;
    clearReadNotifications(unreadCounts, totalUnread);
  }, [currentUser, channelsSynced, unreadCounts, totalUnread]);

  // 첨부 용량 정책·차단 확장자 최초 로드. 인증이 필요 없는 엔드포인트라 로그인 전 도움말에서도
  // 정확한 숫자·목록이 나온다. 이후 변경은 소켓 'config:*'가, 재연결 시 유실분은 onReconnect가 메운다.
  useEffect(() => {
    fetchUploadPolicy();
    fetchBlockedExtensions();
  }, []);

  // ─── 예약 메시지 (내 것, 대기+실패 — 관리자 공지 예약 제외) ───
  // 부팅·로그인 시 1회 fetch + 이후는 소켓 scheduled-message:* 이벤트가 유지한다.
  // 채널별로 나눠 받지 않는 이유: 채팅방을 열 때마다 왕복이 생겨 상시 비용이 된다(성능 가드레일).
  const [myScheduled, setMyScheduled] = useState<ScheduledMessage[]>([]);
  const upsertScheduled = useCallback((sm: ScheduledMessage) => {
    setMyScheduled(prev => {
      // PG BIGINT id는 문자열 — String 정규화 비교 규약
      const idx = prev.findIndex(x => String(x.id) === String(sm.id));
      if (idx === -1) return [...prev, sm];
      const next = [...prev];
      next[idx] = sm;
      return next;
    });
  }, []);
  const removeScheduled = useCallback((id: string) => {
    setMyScheduled(prev => prev.filter(x => String(x.id) !== String(id)));
  }, []);
  useEffect(() => {
    if (!currentUser) {
      setMyScheduled([]);
      return;
    }
    let cancelled = false;
    scheduledMessageService.listMine()
      .then(list => { if (!cancelled) setMyScheduled(list); })
      .catch(err => console.warn('[Scheduled] list fetch failed:', err));
    return () => { cancelled = true; };
  }, [currentUser]);

  // 통합 알림함 미읽음 뱃지 (Phase 2) — 거대 소켓 useEffect와 분리된 독립 CustomEvent 구독 (D6)
  const [activityUnread, setActivityUnread] = useState(0);
  // 알림함 항목 클릭 → 채널 전환 후 loadHistory 완료 시점에 소비하는 점프 보류분
  const pendingActivityJumpRef = useRef<{ channelId: number; messageId: number } | null>(null);
  // 시스템 알림 OS 알림 표시 — 아래(알림 설정 ref들이 선언된 뒤)에서 매 렌더 최신 함수로 교체된다
  const notifySystemActivityRef = useRef<(item: ActivityItem) => void>(() => {});
  useEffect(() => {
    if (!currentUser) {
      setActivityUnread(0);
      return;
    }
    let cancelled = false;
    activityService.getUnreadCount()
      .then(c => { if (!cancelled) setActivityUnread(c); })
      .catch(() => {});
    const onNew = (e: Event) => {
      setActivityUnread(v => v + 1);
      // 외부 시스템 알림(전자결재 등)은 메시지 경로가 없어 여기서 직접 알린다.
      // 멘션·답글은 메시지 경로(message:notify)가 이미 알리므로 건드리지 않는다.
      const item = (e as CustomEvent).detail as ActivityItem | undefined;
      if (item?.type === 'system') notifySystemActivityRef.current(item);
    };
    const onRead = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        id?: number; all?: boolean; tab?: string; count?: number; removedId?: number; ids?: number[];
      };
      if (Array.isArray(detail?.ids)) {
        // 대화방을 읽어 그 방 알림이 함께 읽음 처리됨 (서버 markChannelActivitiesRead)
        const n = typeof detail.count === 'number' ? detail.count : detail.ids.length;
        setActivityUnread(v => Math.max(0, v - n));
      } else if (detail?.all) {
        // 탭 단위 '모두 읽음'이면 그 탭 몫만 뺀다. 건수를 모르면(구버전 서버) 서버 재조회
        if (detail.tab && detail.tab !== 'all') {
          if (typeof detail.count === 'number') setActivityUnread(v => Math.max(0, v - detail.count!));
          else activityService.getUnreadCount().then(c => setActivityUnread(c)).catch(() => {});
        } else {
          setActivityUnread(0);
        }
      } else if (detail?.removedId) {
        // 회수(리액션 취소)된 행의 미읽음 여부를 클라이언트는 모름 → 서버 재조회로 보정
        activityService.getUnreadCount().then(c => setActivityUnread(c)).catch(() => {});
      } else if (detail?.id) {
        setActivityUnread(v => Math.max(0, v - 1));
      }
    };
    window.addEventListener('activity:new', onNew);
    window.addEventListener('activity:read', onRead);
    return () => {
      cancelled = true;
      window.removeEventListener('activity:new', onNew);
      window.removeEventListener('activity:read', onRead);
    };
  }, [currentUser]);
  // ?activity=<탭> 딥링크 — 앱이 떠 있지 않을 때 시스템 알림 웹푸시를 누르면 SW가 이 URL로 연다
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('activity');
    if (!tab) return;
    openActivityTab(tab);
    params.delete('activity');
    const rest = params.toString();
    // state 병합: 빈 객체로 덮으면 useMobileBackNav의 inaNav 태그가 지워진다
    window.history.replaceState({ ...(window.history.state || {}) }, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, [currentUser, openActivityTab]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // 사진·파일 서랍 (채널 전환 시 자동 닫힘)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // 모바일 채팅 헤더 햄버거 메뉴(검색 외 액션을 드롭다운으로 묶음). 데스크톱은 아이콘 그대로 노출
  const [headerMenu, setHeaderMenu] = useState<{ x: number; y: number } | null>(null);
  // 채팅방이 안 보이게 되면 그 방에 딸린 메뉴·서랍도 닫는다. 서랍은 activeChannel만 보고 렌더되는데
  // activeChannel은 홈 복귀 후에도 유지되므로(선택 ≠ 가시) 여기서 닫지 않으면 목록·홈 위에 남는다
  const isChatAreaVisible = isMobile ? isMobileChatOpen : isDesktopChatOpen;
  useEffect(() => {
    if (isChatAreaVisible) return;
    setHeaderMenu(null);
    setIsDrawerOpen(false);
  }, [isChatAreaVisible]);
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [activeChannel?.id]);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);
  // DM은 초안으로 열고 첫 메시지·통화 시점에 materializeDraftDm이 findOrCreateDM을 직접 호출한다.
  // 그래서 이 훅에는 openDM이 없다(클릭 즉시 생성 경로를 되살리지 말 것). self 채팅만 훅을 쓴다.
  const { openSelfChat } = useDMChannel();

  // 화상 통화 상태 — 진행 중인 통화는 callController가 동기로 들고 있고 App은 구독만 한다(여러 명 통화 규칙은 그 파일 머리 주석).
  // 예전 activeCallRef는 렌더 뒤 effect에서 갱신돼, 그 사이 도착한 offer를 버릴 수 있었다
  const callState = useSyncExternalStore(callController.subscribe, callController.getState);
  const activeCall = callState.call;
  const callMessage = callState.message;
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  // 소켓 콜백(한 번 등록)에서 지금 떠 있는 벨을 읽는다 — 같은 벨이 겹쳐 오면 알림을 다시 띄우지 않는다
  const incomingCallRef = useRef<IncomingCallInfo | null>(null);
  incomingCallRef.current = incomingCall;
  // 통화 중 초대 창 — 통화가 끝나면 함께 닫힌다(아래 effect)
  const [isCallInviteOpen, setIsCallInviteOpen] = useState(false);
  const activeCallId = activeCall?.callId;
  useEffect(() => {
    setIsCallInviteOpen(false);
  }, [activeCallId]);
  // 대화방별 진행 중 통화('통화 중 · 참여' 바) — 서버 call:status. 키는 String(channelId)
  const [channelCalls, setChannelCalls] = useState<Record<string, ChannelCallStatus>>({});
  const channelCallsRef = useRef(channelCalls);
  channelCallsRef.current = channelCalls;
  const callUserRef = useRef(currentUser);
  callUserRef.current = currentUser;
  useEffect(() => {
    callController.configure({
      media: callService,
      socket: {
        emit: (event, payload) => socketService.emitCall(event, payload),
        emitWithAck: (event, payload, timeoutMs) => socketService.emitCallWithAck(event, payload, timeoutMs),
      },
      getMe: () => {
        const u = callUserRef.current;
        return u ? { userId: String(u.id), userName: u.username, avatar: u.avatar } : null;
      },
      notify: (n) => showToast({ type: n.type, message: n.message, duration: n.duration }),
      // 카메라·마이크를 가능한 만큼 켠다. 못 켠 것이 있으면 어떻게 연결하는지 안내한다(실패가 아니라 info)
      onMediaAcquired: (acquired) => {
        const notice = describeMediaNotice(acquired, isTauri());
        if (notice) showToast({ type: 'info', message: notice, duration: 7000 });
      },
    });
  }, [showToast]);
  // 새로고침·창 닫기 — 서버 유예(20초)를 기다리지 않고 바로 나간다
  useEffect(() => {
    const onPageHide = () => callController.leaveOnUnload();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // iOS PWA safe-area 배경색: 로그인은 다크, 메인 앱은 흰색
  useEffect(() => {
    const color = !currentUser ? '#0f172a' : '#ffffff';
    document.documentElement.style.backgroundColor = color;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', color);
  }, [currentUser]);

  // 최신 activeChannel.id를 추적하기 위한 ref
  const activeChannelIdRef = useRef(activeChannel?.id ?? 0);
  // 초안 DM의 상대 정보 미러 — handleSendMessage/handleStartCall이 stale closure 없이 읽는다
  const draftPartnerRef = useRef<{ id: string; name: string } | null>(null);
  useEffect(() => {
    activeChannelIdRef.current = activeChannel?.id ?? 0;
    draftPartnerRef.current = isDraftChannel(activeChannel) ? (activeChannel?.draftPartner ?? null) : null;
    // 초안 DM은 저장하지 않는다 — 저장하면 부팅 시 localStorage 복원이 유령 방을 되살린다.
    // 지우지도 않는다: 초안을 열었다 새로고침하면 직전에 보던 실제 방으로 돌아가는 편이 자연스럽다.
    if (isDraftChannel(activeChannel)) return;
    // activeChannel 변경 시 localStorage에 저장
    if (activeChannel) {
      localStorage.setItem('activeChannel', JSON.stringify(activeChannel));
    } else {
      localStorage.removeItem('activeChannel');
    }
  }, [activeChannel]);

  // 새로고침 화면 복원용 내비 상태 저장 — 위 activeChannel 저장 effect와 같은 패턴.
  // currentUser 게이트: handleLogout이 removeItem한 직후 기본값이 재저장되는 것을 막는다.
  useEffect(() => {
    if (!currentUser) return;
    localStorage.setItem('mobileNav', JSON.stringify({ tab: mobileTab, chatOpen: isMobileChatOpen }));
  }, [currentUser, mobileTab, isMobileChatOpen]);
  useEffect(() => {
    if (!currentUser) return;
    localStorage.setItem('desktopNav', JSON.stringify({ tab: desktopTab }));
  }, [currentUser, desktopTab]);

  // 뮤트된 채널 ID 집합 (소켓 콜백은 등록 시점 클로저라 channels 직접 참조 시 stale — ref로 추적)
  const mutedChannelIdsRef = useRef<Set<number>>(new Set());
  // 최신 channels 스냅샷 — handleUserClick의 기존 DM 탐색, materializeDraftDm의 실제 채널 조회용
  const channelsRef = useRef<Channel[]>(channels);
  useEffect(() => {
    channelsRef.current = channels;
    mutedChannelIdsRef.current = new Set(
      channels.filter(ch => ch.isMuted).map(ch => ch.id)
    );
  }, [channels]);

  // 알림에 표시할 내용 수준(계정 설정). 위 mutedChannelIdsRef와 같은 이유로 ref에 미러링한다 —
  // message:notify 콜백은 등록 시점 클로저라 currentUser를 직접 읽으면 낡은 값이 박힌다.
  const notifyPrivacyRef = useRef<NotificationPrivacy>(DEFAULT_NOTIFICATION_PRIVACY);
  useEffect(() => {
    notifyPrivacyRef.current = normalizeNotificationPrivacy(currentUser?.notificationPrivacy);
  }, [currentUser]);

  // 초안 DM → 실제 채널 승격 직후의 채널 id. 초기 히스토리 조회가 소켓 에코를 덮어쓰지 않게 한다.
  const justCreatedChannelRef = useRef<number | null>(null);
  // 상대 사번 → 진행 중인 POST /channels/dm. 연타·동시 전송이 방을 2개 만들지 않게 합류시킨다.
  const dmCreateInFlightRef = useRef<Map<string, Promise<number | null>>>(new Map());

  // 윈도우 포커스 상태 추적 (백그라운드 알림용)
  const isWindowFocusedRef = useRef(document.hasFocus());
  // 데스크톱 활동 하트비트(PC 사용 중 → 서버가 이 계정의 모바일 푸시 억제). 30초 주기 + 포커스/연결 직후 즉시.
  const sendActivityNow = useDesktopActivityHeartbeat(!!currentUser);
  const sendActivityNowRef = useRef(sendActivityNow);
  sendActivityNowRef.current = sendActivityNow;
  const [flashBg, setFlashBg] = useState(false);
  const blinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 수신 벨 안전 시간 제한 — 서버가 45초에 벨을 끄지만(call:handled timeout), 종료 신호를 못 받으면 배너에는
  // 자체 시간 제한이 없어 영영 울렸다(2026-11-04 — 방 룸 밖 멤버가 그 경우였다). 60초면 서버 제한 뒤라 정상 흐름을 해치지 않는다
  useEffect(() => {
    if (!incomingCall) return;
    const callId = incomingCall.callId;
    const timer = setTimeout(() => {
      setIncomingCall(prev => (prev && prev.callId === callId ? null : prev));
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
        document.title = 'INA-TALK 인아 채팅 프로그램';
      }
      void flashTaskbar(true);
    }, 60000);
    return () => clearTimeout(timer);
  }, [incomingCall]);

  // 외부 시스템 알림(전자결재 등) OS 알림. 메시지 알림과 같은 규칙을 따른다:
  //  - 창이 포커스돼 있으면 띄우지 않는다(벨 배지가 오른다)
  //  - 웹은 화면에 보일 때만 페이지가 띄우고, 숨겨져 있으면 SW 웹푸시가 맡는다(같은 tag sys-{id}로 중복 흡수)
  //  - 뮤트 개념이 없다 — 결재는 항상 알린다(2026-09-23 결정)
  // 클릭하면 알림함을 그 출처 탭으로 연다. Tauri는 channelId 자리에 'activity:<탭>'을 실어 보낸다
  // (Rust는 문자열을 그대로 되돌려 줄 뿐이라 변경이 없다 — notification-clicked 리스너가 분기한다).
  notifySystemActivityRef.current = (item: ActivityItem) => {
    if (isWindowFocusedRef.current) {
      // 일정 알림("10분 뒤 · 주간 회의")은 앱을 보고 있어도 놓치면 안 된다 — OS 알림 대신 인앱 토스트로.
      // 결재 등 다른 출처는 예전처럼 생략한다(rail 알림 배지가 대신한다)
      if (item.source === 'calendar') showToast({ type: 'info', message: [item.title, item.preview].filter(Boolean).join(' — ') });
      return;
    }
    const tab = item.source || 'all';
    const notify = buildSystemNotificationText(notifyPrivacyRef.current, {
      label: item.actorName,
      title: item.title,
      subject: item.preview,
    });
    if (isTauri()) {
      import('@tauri-apps/api/core').then(({ invoke }) => {
        // 'activity:<탭>:<알림 id>' — 클릭 리스너가 탭과 id로 나눠 봇 대화를 그 알림 위치로 연다
        invoke('show_chat_notification', { title: notify.title, body: notify.body, channelId: `activity:${tab}:${item.id}` });
      }).catch(() => {});
      void flashTaskbar(false);
    } else if (document.visibilityState === 'visible') {
      void showWebNotification(
        notify.title,
        { body: notify.body, tag: `sys-${item.id}`, data: { kind: 'system', source: tab } },
        () => {
          window.focus();
          openActivityTab(tab, item.id);
        }
      );
    }
  };

  // 놓친 메시지 catch-up 콜백(정의는 아래 loadNewerMessages 근처). 소켓 연결 effect·가시성 effect·
  // visibilitychange 핸들러는 deps를 늘리면 소켓이 재연결되거나 리스너가 재등록되므로 ref로 호출한다.
  const catchUpLatestMessagesRef = useRef<(channelId: number) => Promise<void>>(async () => {});
  // 채널 변경 effect의 최신 창 조회가 진행 중인 채널 id (catch-up의 이중 조회 방지)
  const historyLoadingRef = useRef<number | null>(null);

  // 채팅방이 실제 화면에 보이는가 (데스크톱=isDesktopChatOpen(X 토글), 모바일=isMobileChatOpen).
  // 모바일 홈/대화방 목록·데스크톱 X 닫힘 상태는 미리보기일 뿐이므로 이 ref가 false인 동안에는
  // 읽음 계열 동작(markChannelRead / channel:focus / isCurrentChannel 판정)을 모두 막는다.
  // 소켓 콜백·타이머에서 읽으므로 isViewingHistoryRef와 같은 이유로 ref.
  const isChatVisibleRef = useRef(isMobile ? isMobileChatOpen : isDesktopChatOpen);
  const prevChatVisibleRef = useRef(isMobile ? isMobileChatOpen : isDesktopChatOpen);
  useEffect(() => {
    const visible = isMobile ? isMobileChatOpen : isDesktopChatOpen;
    isChatVisibleRef.current = visible; // 767px 경계 리사이즈 포함 항상 동기화
    if (prevChatVisibleRef.current === visible) return;
    prevChatVisibleRef.current = visible;
    if (!visible) {
      // 채팅방 이탈(모바일: 홈/목록 복귀, 데스크톱: X로 닫기). 서버 focused 상태를 해제해야
      // 서버측 자동 읽음(message:send 시 focused 사용자 last_read 갱신)과 alreadyRead가 막힌다
      socketService.emitChannelBlur();
      // 과거 열람 중에 나갔으면 플래그를 여기서 풀어 준다. 같은 채널 재진입은 채널 변경 effect가
      // 안 돌아 isViewingHistoryRef가 true로 고착되고 message:new append가 영영 스킵되기 때문.
      // 읽음 처리는 하지 않는다(returnToLatest는 markRead를 포함하므로 호출 금지). 재진입 시 아래
      // catch-up이 과거 창(최신분 100건 이상)을 감지해 최신 창으로 갈아끼운다.
      if (isViewingHistoryRef.current) {
        isViewingHistoryRef.current = false;
        setHasMoreNewer(false);
        setJumpTarget(null);
      }
    } else if (activeChannelIdRef.current) {
      // 채팅방 재진입 — 같은 채널 재진입은 채널 변경 effect가 안 돌므로 여기서 보강한다.
      // (1) 소켓이 끊긴 사이(모바일 백그라운드) 유실된 message:new를 REST로 메꾼다 — 목록의
      //     lastMessage는 list-sync로 이미 최신인데 방 안에는 그 메시지가 없는 불일치의 원인.
      catchUpLatestMessagesRef.current(activeChannelIdRef.current);
      // (2) 읽음 처리
      if (isWindowFocusedRef.current && !isViewingHistoryRef.current) {
        socketService.emitChannelFocus(activeChannelIdRef.current);
        socketService.markChannelRead(activeChannelIdRef.current);
        setUnreadCounts(prev => ({ ...prev, [activeChannelIdRef.current!]: 0 }));
      }
    }
  }, [isMobile, isMobileChatOpen, isDesktopChatOpen]);

  useEffect(() => {
    const onFocus = () => {
      isWindowFocusedRef.current = true;
      setFlashBg(false);
      // PC로 돌아온 순간 서버에 활동을 알려 다음 메시지부터 폰 알림이 멈추게 한다 (30초 하트비트를 기다리지 않음)
      sendActivityNowRef.current();
      // 앱 아이콘 배지는 여기서 지우지 않는다 — 창 포커스는 '모두 읽음'이 아니라서
      // 다른 채널 안읽음이 남아 있는데도 배지가 사라졌다. 아래 읽음 처리로 unreadCounts가
      // 갱신되면 totalUnread 파생 effect가 정확한 숫자로 다시 칠한다.
      // 웹 탭 제목 점멸 중지
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
        document.title = 'INA-TALK 인아 채팅 프로그램';
      }
      void flashTaskbar(true);
      // 트레이로 숨겼다 다시 열면 작업표시줄 버튼이 새로 만들어지면서 오버레이 배지가 사라진다.
      // 위 주석대로 '지우는' 것이 아니라 마지막 숫자를 그대로 되살리는 것이다.
      refreshTaskbarBadge();
      // 포커스 복귀 시 서버에 포커스 알림 + 읽음 처리 + unread 초기화
      // (과거 열람 중에는 읽음 처리 금지 — 창을 클릭했다는 이유만으로 안 읽은 최신 메시지가 사라지면 안 된다.
      //  모바일 홈/목록 화면도 채팅방을 보는 것이 아니므로 금지)
      if (activeChannelIdRef.current && !isViewingHistoryRef.current && isChatVisibleRef.current) {
        socketService.emitChannelFocus(activeChannelIdRef.current);
        socketService.markChannelRead(activeChannelIdRef.current);
        setUnreadCounts(prev => ({ ...prev, [activeChannelIdRef.current!]: 0 }));
      }
    };
    const onBlur = () => {
      isWindowFocusedRef.current = false;
      socketService.emitChannelBlur();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // 보고 있는 대화방에 들어온 알림함 알림(리액션·멘션·답글)은 바로 읽음 처리한다.
  // 리액션은 새 메시지를 만들지 않아 onMessageNew의 읽음이 나가지 않고, 멘션은 그 읽음이
  // 알림 행 INSERT(커밋 후 fire-and-forget)보다 먼저 서버에 닿을 수 있다. 서버 markChannelRead가
  // 그 방 알림까지 정리하므로 여기서는 방 읽음만 보낸다. 가드는 onMessageNew의 읽음과 같고,
  // 디바운스 타이머를 공유해 새 메시지·알림이 몰려도 읽음 요청은 한 번이다.
  useEffect(() => {
    const onNew = (e: Event) => {
      const item = (e as CustomEvent).detail as ActivityItem | undefined;
      if (!item || item.type === 'system' || !item.channelId) return;
      const channelId = Number(item.channelId);
      if (channelId !== Number(activeChannelIdRef.current)) return;
      if (!isChatVisibleRef.current || !isWindowFocusedRef.current || isViewingHistoryRef.current) return;
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = setTimeout(() => {
        socketService.markChannelRead(channelId);
      }, 300);
    };
    window.addEventListener('activity:new', onNew);
    return () => window.removeEventListener('activity:new', onNew);
  }, []);

  // 채널 포커스 하트비트.
  // 서버는 갱신이 끊긴 포커스를 일정 시간(FOCUS_TTL_MS, 기본 30초) 뒤 신뢰하지 않는다.
  // 절전이나 강제 종료로 blur 없이 사라진 세션이 계속 포커스를 주장하면 다른 기기의 알림까지
  // 삼키기 때문이다. 실제로 보고 있는 동안에는 그보다 짧은 주기로 갱신해 준다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (isWindowFocusedRef.current && activeChannelIdRef.current && isChatVisibleRef.current) {
        socketService.emitChannelFocus(activeChannelIdRef.current);
      }
    }, 20000);
    return () => clearInterval(timer);
  }, []);

  // 서비스워커 업데이트 감지 (웹 전용)
  useEffect(() => {
    if (isTauri()) return;
    const onSwUpdate = () => setSwUpdateAvailable(true);
    window.addEventListener('sw-update-available', onSwUpdate);
    return () => window.removeEventListener('sw-update-available', onSwUpdate);
  }, []);

  // PWA 백그라운드 진입 시 서버에 즉시 오프라인 알림 (웹 PWA 전용)
  // pingTimeout(5초) 대기 없이 다음 메시지부터 Web Push 분기로 진입시킴
  useEffect(() => {
    if (!currentUser) return;
    if (isTauri()) return;

    let offlineTimer: ReturnType<typeof setTimeout> | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // 짧은 백그라운드 전환(<3초)은 chattering 방지로 무시
        offlineTimer = setTimeout(() => {
          socketService.emitGoingOffline();
          offlineTimer = null;
        }, 3000);
      } else {
        if (offlineTimer) {
          clearTimeout(offlineTimer);
          offlineTimer = null;
        }
        socketService.emitGoingOnline();
        // 백그라운드 동안 서버가 pingTimeout으로 소켓을 끊었어도 클라이언트는 최대 ~30초 뒤에야
        // 알아채고 재연결한다. 그 구간을 기다리지 않고 활성 채널의 놓친 메시지를 REST로 즉시 메꾼다.
        if (activeChannelIdRef.current) {
          catchUpLatestMessagesRef.current(activeChannelIdRef.current);
        }
      }
    };

    const handlePageHide = () => {
      if (offlineTimer) {
        clearTimeout(offlineTimer);
        offlineTimer = null;
      }
      socketService.emitGoingOffline();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      if (offlineTimer) clearTimeout(offlineTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [currentUser]);

  // ----------------------------------------
  // 초안 DM 승격
  // ----------------------------------------

  /**
   * 초안 DM(id 0)을 실제 채널로 승격한다. 첫 메시지 전송과 통화 시작이 공유하는 유일한 경로.
   *
   * - 같은 상대에 대한 요청은 in-flight 프로미스에 합류시킨다(연타·동시 전송 시 방 2개 방지).
   * - 서버가 POST 응답 '전에' announceChannelToMembers → joinUserToChannel로 내 소켓을 이미
   *   channel:<id> 룸에 넣어 주므로, 반환 직후 message:send를 emit해도 io.to(room) 에코를
   *   정상 수신한다 — channel:join을 기다릴 필요가 없다.
   */
  const materializeDraftDm = useCallback(async (): Promise<Channel | null> => {
    const partner = draftPartnerRef.current;
    if (!currentUser || !partner) return null;

    let inflight = dmCreateInFlightRef.current.get(partner.id);
    if (!inflight) {
      inflight = channelService
        .findOrCreateDM({ userId: String(currentUser.id), targetUser: { id: partner.id, name: partner.name } })
        .then(r => r.id)
        .catch(err => { console.error('Failed to create DM channel:', err); return null; })
        .finally(() => { dmCreateInFlightRef.current.delete(partner.id); });
      dmCreateInFlightRef.current.set(partner.id, inflight);
    }
    const newId = await inflight;
    if (!newId) {
      showToast({ type: 'error', message: '대화방을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      return null;
    }

    // 서버 channel:new(소켓)가 HTTP 응답보다 먼저 오는 것이 정상 순서라 대개 완전한 객체가 이미 있다
    const real: Channel = channelsRef.current.find(c => c.id === newId)
      ?? { id: newId, name: partner.name, type: 'dm', dmPartnerId: partner.id };
    justCreatedChannelRef.current = newId;

    // 아직 초안을 보고 있을 때만 실제 방으로 갈아끼운다.
    // - 소켓 channel:new가 먼저 도착해 이미 같은 방으로 승격됐다면(onChannelNew) 서버가 준
    //   완전한 객체를 최소 폴백 객체로 덮어쓰지 않는다.
    // - 그 사이 사용자가 다른 방으로 옮겼다면 그 선택을 존중한다(메시지는 그대로 전송된다).
    if (activeChannelIdRef.current === DRAFT_CHANNEL_ID) {
      // ref를 즉시 갱신한다 — React 커밋 전에 message:new 에코가 도착하면
      // onMessageNew의 activeChannelIdRef 비교에서 탈락해 내 메시지가 화면에 안 뜬다
      activeChannelIdRef.current = newId;
      draftPartnerRef.current = null;
      setDmPartnerName(partner.name);
      setActiveChannel(real);
    }
    return real;
  }, [currentUser, showToast]);

  // ----------------------------------------
  // 화상 통화 핸들러
  // ----------------------------------------

  const handleStartCall = useCallback(async (channel: Channel) => {
    if (!currentUser) return;
    if (callController.getCall()) {
      showToast({ type: 'info', message: '이미 통화 중입니다.' });
      return;
    }
    // 이 방에 진행 중인 통화가 있으면 거기에 참가한다(한 방에 통화 하나 — 서버도 IN_PROGRESS로 돌려보낸다)
    const ongoing = channel.id ? channelCallsRef.current[String(channel.id)] : undefined;
    if (ongoing?.active) {
      await callController.join(channel.id, ongoing.callId, channel.name || '통화', { channelType: channel.type });
      return;
    }

    // 초안 DM이면 통화 전에 실제 방을 만든다 — callId가 채널 id에 묶여 있고
    // 서버 call:initiate가 채널 멤버십을 검사하므로 실 id가 반드시 필요하다.
    // (통화 종료 시 finalizeCall이 시스템 메시지를 남기므로 이 방은 빈 방으로 남지 않는다)
    let target = channel;
    if (!channel.id) {
      const real = await materializeDraftDm();
      if (!real) return; // 토스트는 materializeDraftDm이 이미 띄웠다
      target = real;
    }

    // 카메라·마이크가 없거나 쓸 수 없어도 통화한다 — 있는 것만 켜고, 둘 다 없으면 보기 전용
    await callController.start({ id: target.id, name: target.name || '통화', type: target.type });
  }, [currentUser, showToast, materializeDraftDm]);

  const handleAcceptCall = useCallback(async () => {
    if (!incomingCall || !currentUser) return;
    const info = incomingCall;
    setIncomingCall(null);
    await callController.accept(info);
  }, [incomingCall, currentUser]);

  const handleRejectCall = useCallback(() => {
    if (!incomingCall) return;
    callController.reject(incomingCall);
    setIncomingCall(null);
  }, [incomingCall]);

  // 대화방 위 '통화 중 · 참여' 바
  const handleJoinCall = useCallback(async (channelId: number) => {
    const st = channelCallsRef.current[String(channelId)];
    if (!st?.active) return;
    const ch = channelsRef.current.find(c => String(c.id) === String(channelId));
    await callController.join(channelId, st.callId, ch?.name || '통화', { channelType: ch?.type });
  }, []);

  const handleHangup = useCallback(() => {
    callController.hangup();
  }, []);

  // 로그인 핸들러
  const handleLoginSuccess = async (user: User, token: string) => {
    // 서버가 프로필 조회에 실패하면 notificationPrivacy 필드가 통째로 빠진다("모름").
    // 로그인은 currentUser를 교체하므로 그대로 두면 설정이 조용히 기본값('full')으로 돌아가,
    // DB 장애 한 번에 '숨김'을 쓰던 사람의 알림에 메시지 내용이 뜬다.
    // 이 기기에 남아 있던 같은 계정의 값을 물려받아 그 창을 막는다.
    const merged: User = user.notificationPrivacy === undefined
      ? { ...user, notificationPrivacy: readSavedNotifyPrivacy(String(user.id)) }
      : user;
    setCurrentUser(merged);
    // localStorage에 사용자 정보와 토큰 저장
    localStorage.setItem('currentUser', JSON.stringify(merged));
    localStorage.setItem('authToken', token);

    // 명시적 로그인 시에만 면책 동의 모달 표시 — 그중에서도 최초/문구 개정/30일 경과일 때만
    if (shouldShowDisclaimer(user.id)) {
      setIsDisclaimerOpen(true);
    }

    // Tauri 창 크기 조절은 tauri.conf.json에서 설정

    // 웹 알림 권한 요청 + Web Push 구독
    if (!isTauri() && 'Notification' in window) {
      const permission = Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
      if (permission === 'granted') {
        subscribeToPush();
      }
    }

    // Tauri: device 등록 (로그아웃 후 익명 알림 수신용)
    // 이전에 로그아웃 상태에서 살아있던 notify-only socket이 있으면 끊기
    if (isTauri()) {
      socketService.disconnectNotify();
      try {
        const deviceId = await getDeviceId();
        if (deviceId) {
          await registerDevice(deviceId, getPlatform());
          console.log('[Device] Registered for logout notifications');
        }
      } catch (err) {
        console.warn('[Device] Registration failed:', err);
      }

      // 전사 무인 배포 PC는 사용자가 설정 모달을 열지 않으면 자동 시작이 영영 안 걸린다
      // (AutoStartToggle은 모달 마운트 시에만 enable). 그래서 로그인 성공 직후 1회 등록한다.
      // 플래그(autostart-configured)는 AutoStartToggle과 공유 — 사용자가 직접 끈 경우(flag 있음)는
      // 존중하고 건드리지 않는다. 멱등: 이미 켜져 있으면 enable()이 무해하다.
      try {
        if (!localStorage.getItem('autostart-configured')) {
          const autostart = await import('@tauri-apps/plugin-autostart');
          if (!(await autostart.isEnabled())) await autostart.enable();
          localStorage.setItem('autostart-configured', 'true');
          console.log('[Autostart] Enabled on first login (silent-deploy default)');
        }
      } catch (err) {
        console.warn('[Autostart] First-login enable failed:', err);
      }
    }
  };

  // 로그아웃 핸들러
  // unsubscribePush를 명시적으로 켠 경우(모달 체크 해제)에만 웹푸시 구독을 해제한다.
  // 401/JWT 만료 자동 로그아웃 경로는 인자 없이 호출 → 구독 유지 (사용자가 의도한 이탈이 아님)
  const handleLogout = useCallback((opts?: { unsubscribePush?: boolean }) => {
    // 웹 푸시 구독 해제 (fire-and-forget)
    if (!isTauri() && opts?.unsubscribePush) {
      unsubscribeFromPush();
    }
    // 로그아웃 전 활성 채널 읽음 처리 (재로그인 시 안읽음 방지)
    // 모바일 홈/목록에서 로그아웃하면 안 본 채널이 읽음 처리되면 안 되므로 채팅방이 보일 때만
    if (activeChannelIdRef.current && isChatVisibleRef.current) {
      socketService.markChannelRead(activeChannelIdRef.current);
    }
    // 통화 중이면 나간다(소켓이 끊기기 전에 — 서버 유예를 기다리면 다른 참가자 화면에 20초 동안 내가 남는다)
    callController.hangup();
    setIncomingCall(null);
    setChannelCalls({});
    setCurrentUser(null);
    setUnreadCounts({});
    setChannelsSynced(false);
    // 수동 상태는 서버도 세션이 끊기면 잊는다. 여기서 비우지 않으면 다음 계정의 선택 UI에
    // 이전 사용자의 '다른 용무 중'이 체크된 채로 열린다.
    setMyPresenceManual(null);
    // 앱 아이콘 배지 즉시 제거 — 로그아웃 후 남의 숫자가 홈 화면에 남으면 안 된다.
    // (파생 effect는 channelsSynced 게이트에 막히므로 여기서 직접 지운다)
    setAppBadgeCount(0);
    // 알림 센터도 같은 이유로 비운다. 웹푸시 구독 자체는 유지되지만(로그아웃 후에도 알림 받기)
    // 이미 떠 있는 알림은 이전 사용자의 대화 상대 이름을 담고 있다.
    clearReadNotifications({}, 0);
    pendingNotifyChannelIdRef.current = null; // 보류된 알림 딥링크가 다음 로그인에 넘어가지 않게
    setIsLogoutConfirmOpen(false); // 401 자동 로그아웃 시 확인 모달 잔존 방지
    setProfileUser(null); // 이전 사용자가 보던 프로필이 로그인 화면 위에 남으면 안 된다
    socketService.disconnect();
    setIsConnected(false);
    // localStorage 정리
    // (disclaimer:{userId}는 일부러 남긴다 — 지우면 로그아웃할 때마다 이용 안내가 다시 떠서
    //  30일 주기 재동의 정책이 무의미해진다. 계정별 키라 다른 사용자에게 새지도 않는다)
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('activeChannel');
    localStorage.removeItem('mobileNav');
    localStorage.removeItem('desktopNav');
    // 내비 state도 기본값으로 리셋 — 안 하면 재로그인 순간 저장 effect가
    // 이전 사용자의 화면 상태(stale 메모리 값)를 다시 저장한다
    setMobileTab('home');
    setIsMobileChatOpen(false);
    setIsMobileActivityOpen(false);
    setSystemFeed(null);
    setDesktopTab('chat');
    setIsDesktopChatOpen(true);
    // 계열사 직원 명부 캐시 비우기 — 다른 계정으로 재로그인했을 때 남의 계열사 명부가 남으면 안 된다
    clearUserDirectoryCache();
    clearChannelMemberIndex();
    clearCalendarCache(); // 일정 캐시도 계정 격리 — 다음 계정 화면에 남의 일정이 스치면 안 된다
    messagesCacheRef.current.clear(); // 이전 계정의 대화 내용이 다음 로그인 화면에 스치면 안 된다
    // 작성 중이던 초안도 마찬가지다. 기기 설정(webNotifyEnabled·disclaimer)과 달리
    // 세션 콘텐츠라 공용 PC에서 다음 사람에게 보이면 안 된다
    clearAllDrafts();
    // 익명 알림 socket 전환은 currentUser 변화에 반응하는 useEffect가 일괄 처리
  }, []);

  // 사이드바 로그아웃 버튼 → 확인 모달만 연다 (실제 처리는 confirmLogout)
  // 401 세션만료·JWT 만료 경로는 handleLogout을 직접 호출해 확인 없이 즉시 로그아웃
  const requestLogout = useCallback(() => {
    setKeepPushOnLogout(true); // 열 때마다 기본값으로 리셋
    setIsLogoutConfirmOpen(true);
  }, []);
  const confirmLogout = useCallback(() => {
    setIsLogoutConfirmOpen(false);
    // 사용자가 직접 로그아웃하면 자동 로그인을 해제한다(저장된 비밀번호만 삭제, 아이디는 프리필용으로 유지).
    // 401/JWT 만료 자동 로그아웃 경로(handleLogout 직접 호출)는 그대로 두어 다음 실행 때 자동 재로그인된다
    clearSavedPassword();
    // 기기 키 세션 재개도 끈다 — 직접 로그아웃했는데 다음에 열면 저절로 들어오면 안 된다(자동 로그인과 같은 정책).
    // 서버의 재개 비밀값도 지운다(보안 감사 2026-11 C9 — 로컬 힌트만 지우면 같은 브라우저의 다음 사람이 되살릴 수
    // 있었다). logoutServer는 토큰을 먼저 읽어 가므로 아래 로컬 정리와 병행해도 되고, 실패해도 로컬 정리로 충분하다
    void authService.logoutServer();
    clearResumeIdentity();
    handleLogout({ unsubscribePush: !keepPushOnLogout });
  }, [handleLogout, keepPushOnLogout]);

  // 토큰 슬라이딩 갱신 상태 — 아래 refresh·만료 타이머 effect와 401 재개 경로가 함께 쓴다.
  // 갱신 성공 시 tokenRefreshCount를 올려 만료 타이머가 새 exp로 재무장된다.
  const [tokenRefreshCount, setTokenRefreshCount] = useState(0);
  const lastTokenRefreshRef = useRef(0);
  const resumeInFlightRef = useRef(false);

  // 기기 신뢰: 승인 웹 기기면 비밀번호 없이 세션을 다시 연다. 성공하면 true(호출부는 로그아웃하지 않는다).
  const tryResumeSession = useCallback(async (): Promise<boolean> => {
    if (isTauri() || resumeInFlightRef.current) return false;
    resumeInFlightRef.current = true;
    try {
      const r = await authService.resumeSession();
      if (r.success && r.token) {
        lastTokenRefreshRef.current = Date.now();
        setTokenRefreshCount((c) => c + 1);
        socketService.reconnectNow();
        return true;
      }
      return false;
    } finally {
      resumeInFlightRef.current = false;
    }
  }, []);

  // 401(세션 만료) 자동 감지 → 로그아웃 + 토스트
  // apiClient.ts가 'auth:unauthorized' 이벤트를 발행함(detail.code = 서버가 준 이유)
  useEffect(() => {
    const onUnauthorized = async (e: Event) => {
      // 이미 로그아웃 상태면 무시 (중복 토스트 방지)
      if (!localStorage.getItem('authToken')) return;
      const code = (e as CustomEvent<{ code?: string }>).detail?.code;
      // 토큰 만료·기기 묶음 없는 옛 토큰(enforce 전환 직후)은 승인 웹 기기라면 재개로 이어 간다.
      // 해제된 기기(DEVICE_REVOKED)·로그인 승인 해제(SESSION_REVOKED)·비밀번호 변경(CREDENTIALS_CHANGED)은
      // 재개해도 서버가 거부하므로 바로 로그아웃한다.
      const noResume = code === 'DEVICE_REVOKED' || code === 'SESSION_REVOKED' || code === 'CREDENTIALS_CHANGED';
      if (!noResume && await tryResumeSession()) return;
      if (!localStorage.getItem('authToken')) return;
      handleLogout();
      showToast({
        type: 'error',
        message: code === 'DEVICE_REVOKED'
          ? '이 기기의 사용 승인이 해제되었습니다. 관리자에게 문의해 주세요.'
          : code === 'SESSION_REVOKED'
            ? '접근 권한이 해제되었습니다. 관리자에게 문의해 주세요.'
          : code === 'CREDENTIALS_CHANGED'
            ? '계정 정보가 바뀌었습니다. 다시 로그인해 주세요.'
          : code === 'DEVICE_REQUIRED'
            ? '승인된 기기에서 다시 로그인해 주세요.'
            : code === 'DEVICE_PROOF_REQUIRED'
              ? '기기 확인에 실패했습니다. 다시 로그인해 주세요.'
              : '세션이 만료되었습니다. 다시 로그인해주세요.',
        duration: code ? 10000 : undefined,
      });
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [handleLogout, showToast, tryResumeSession]);

  // 로그아웃 상태로 부팅한 웹 기기 — 로그인 화면 전에 기기 키 재개를 1회 시도한다
  useEffect(() => {
    if (!isResumingSession) return;
    let cancelled = false;
    (async () => {
      const r = await authService.resumeSession();
      if (!cancelled && r.success && r.user && r.token) await handleLoginSuccess(r.user, r.token);
      if (!cancelled) setIsResumingSession(false);
    })();
    return () => { cancelled = true; };
    // 부팅 1회 — handleLoginSuccess identity와 무관
  }, []); // eslint-disable-line

  // 기기 신뢰(observe 단계): 기기가 안 묶인 토큰이면 이 기기를 서버에 알린다. PC 앱은 사내망이면 여기서
  // 자동 등록되고, 승인된 기기면 서명을 실은 refresh로 토큰에 기기를 묶는다(enforce 순간 튕기지 않게).
  // 웹 기기는 상태만 묻는다(승인 요청은 사용자가 '내 기기'에서 누를 때만).
  useEffect(() => {
    if (!currentUser || authService.tokenDeviceId() != null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await checkThisDevice(currentUser);
        if (cancelled || !r.needsRefresh) return;
        const res = await authService.refreshToken();
        if (!cancelled && res.refreshed) {
          lastTokenRefreshRef.current = Date.now();
          setTokenRefreshCount((c) => c + 1);
        }
      } catch (err) {
        console.warn('[DeviceTrust] this-device check failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // 계정이 바뀔 때만 — currentUser 객체는 아바타 등으로 자주 바뀐다
  }, [currentUser?.id, currentUser?.affiliateId]); // eslint-disable-line

  // 서버가 소켓 토큰 만료로 끊었다(기기 신뢰 enforce 소켓 스윕) — 갱신 후 다시 잇는다.
  // 서버가 끊은 연결은 socket.io가 자동 재연결하지 않는다.
  useEffect(() => {
    const onSessionExpired = async () => {
      if (!localStorage.getItem('authToken')) return;
      const r = await authService.refreshToken();
      if (r.refreshed) {
        lastTokenRefreshRef.current = Date.now();
        setTokenRefreshCount((c) => c + 1);
        socketService.reconnectNow();
        return;
      }
      if (await tryResumeSession()) return;
      dispatchUnauthorized();
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, [tryResumeSession]);

  // Tauri 비로그인 상태에서 익명 알림 socket 자동 연결
  // - 앱 부팅 직후 (재부팅 후 autostart로 로그인 화면만 표시된 경우 포함)
  // - 로그아웃 직후 (handleLogout이 setCurrentUser(null) 호출 → 이 useEffect 재실행)
  // 사전 조건: 이번 PC에서 한 번 이상 로그인 → device_id + notifyEnabled 캐시 존재
  useEffect(() => {
    if (!isTauri()) return;
    if (currentUser) {
      // 로그인 상태로 전환되면 익명 socket 정리 (handleLogin의 disconnectNotify와 보완)
      socketService.disconnectNotify();
      return;
    }
    const deviceId = getCachedDeviceId();
    const notifyEnabled = getCachedNotifyEnabled();
    if (!deviceId || !notifyEnabled) return;

    socketService.setCallbacks({
      onAnonymousNotify: ({ channelId }) => {
        import('@tauri-apps/api/core').then(({ invoke }) => {
          invoke('show_chat_notification', {
            title: 'INA-TALK',
            body: '새 메시지가 도착했습니다',
            channelId: String(channelId),
          });
        });
        // 비로그인 상태라 unreadCounts가 없다 — 숫자 배지 없이 점멸만 한다
        void flashTaskbar(false);
      },
    });
    socketService.connectNotifyOnly(deviceId);
    console.log('[Bootstrap] Notify-only socket connected (currentUser=null)');

    return () => {
      socketService.disconnectNotify();
    };
  }, [currentUser]);

  // 내 아바타를 로컬에 반영하는 유일한 지점 — 직접 변경(handleAvatarChange)·다른 기기에서의
  // 변경(user:avatar-changed 자기 수신)·refresh 응답 복구 셋 다 여기를 지난다.
  // ⚠ 서버가 준 원본 값을 그대로 넣는다. resolveAvatarUrl 결과(절대 URL)를 넣지 말 것 —
  //   서버 주소는 계열사 빌드마다 달라서, 저장하면 다른 클라이언트로 전파된다.
  // (아래 refresh effect의 deps가 렌더 시점에 이 콜백을 읽으므로 effect보다 앞에 선언해야 한다)
  const applyMyAvatarLocally = useCallback((avatarUrl: string | null) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, avatar: avatarUrl };
      localStorage.setItem('currentUser', JSON.stringify(next));
      return next;
    });
    // 소켓 재연결 시 최신 아바타가 전송되도록 동기화
    socketService.updateUserAvatar(avatarUrl);
  }, []);

  // 내 알림 표시 수준을 로컬에 반영하는 유일한 지점 — 설정 화면에서의 직접 변경·다른 기기에서의
  // 변경(user:notify-privacy-changed)·refresh 응답 복구 셋 다 여기를 지난다.
  // 서버 저장은 호출 전에 이미 끝나 있어야 한다(먼저 반영하면 실패가 localStorage에만 남는다).
  const applyMyNotifyPrivacyLocally = useCallback((privacy: NotificationPrivacy) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      if (prev.notificationPrivacy === privacy) return prev; // 값이 같으면 no-op(불필요 재렌더 방지)
      const next = { ...prev, notificationPrivacy: privacy };
      localStorage.setItem('currentUser', JSON.stringify(next));
      return next;
    });
  }, []);

  // 내가 직접 고른 접속 상태(null = 자동 판정). **접속 중에만 유지되는 값**이라
  // localStorage에 저장하지 않는다 — 저장하면 재접속 시 서버가 이미 잊은 값이 유령으로 되살아난다.
  // 서버가 접속 직후 현재 값을 1회 보내 주므로 부팅 복원도 필요 없다.
  const [myPresenceManual, setMyPresenceManual] = useState<PresenceManual>(null);

  // 수동 상태를 로컬에 반영하는 유일한 지점 — 설정 화면·rail 팝오버에서의 직접 변경과
  // 다른 기기에서의 변경(user:presence-manual) 둘 다 여기를 지난다.
  // 서버 저장은 호출 전에 이미 끝나 있어야 한다.
  const applyMyPresenceManualLocally = useCallback((manual: PresenceManual) => {
    setMyPresenceManual(prev => (prev === manual ? prev : manual));
  }, []);

  // 표시용 내 상태. 서버가 io.emit으로 전원에게 보내므로 내 항목도 onlineUsers에 들어온다.
  // 목록에 없는 순간(접속 직후·재연결 중)은 online으로 본다 — 내 화면에서 내가 회색으로
  // 보이면 연결이 끊긴 것처럼 읽힌다. 실제 끊김은 rail이 isConnected로 따로 판정한다.
  const myStatus = useMemo<PresenceStatus>(() => {
    if (!currentUser) return 'online';
    return onlineUsers.find(u => String(u.userId) === String(currentUser.id))?.status ?? 'online';
  }, [onlineUsers, currentUser]);

  // rail 팝오버·설정 모달이 공유하는 저장 경로. 실패 시 토스트를 띄우고 선택을 되돌린다
  // (PresenceStatusSetting은 자체 낙관적 처리를 하므로 rail 쪽만 이 함수를 쓴다).
  const handlePresenceSelect = useCallback(async (status: 'auto' | 'away' | 'busy') => {
    const next: PresenceManual = status === 'auto' ? null : status;
    if (next === myPresenceManual) return;
    try {
      await dbService.updatePresenceStatus(status);
      applyMyPresenceManualLocally(next);
    } catch {
      showToast({ type: 'error', message: '상태를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  }, [myPresenceManual, applyMyPresenceManualLocally, showToast]);

  // 토큰 슬라이딩 갱신: 부팅(세션 복원)·로그인 직후 1회 + 백그라운드 복귀 시(1시간 스로틀) 재발급.
  // 모바일은 30일 토큰이라 앱을 한 달에 한 번만 열어도 만료가 계속 뒤로 밀린다 (자동 로그아웃 사실상 제거).
  // 갱신 성공 시 tokenRefreshCount를 올려 아래 만료 타이머가 새 exp로 재무장된다(상태는 401 재개 경로 위에 선언).
  useEffect(() => {
    if (!currentUser) return;
    const REFRESH_THROTTLE_MS = 60 * 60 * 1000;
    const refresh = async () => {
      if (Date.now() - lastTokenRefreshRef.current < REFRESH_THROTTLE_MS) return;
      lastTokenRefreshRef.current = Date.now();
      const result = await authService.refreshToken();
      if (result.refreshed) {
        setTokenRefreshCount((c) => c + 1);
        // 서버 DB가 아바타의 권위 — 소켓이 끊긴 동안 다른 기기에서 바꾼 값을 여기서 복구한다
        // (모바일 PWA는 30일 토큰이라 재로그인이 없어 이 경로가 유일하다).
        // undefined = 구 서버/조회 실패 → 기존 값 유지. 값이 같으면 no-op(불필요 재렌더 방지).
        if (result.avatar !== undefined && result.avatar !== (currentUser.avatar ?? null)) {
          applyMyAvatarLocally(result.avatar);
        }
        // 알림 표시 수준도 같은 편승·같은 규칙 — undefined는 "모름"이라 기존 값을 유지한다
        if (result.notificationPrivacy !== undefined
            && result.notificationPrivacy !== currentUser.notificationPrivacy) {
          applyMyNotifyPrivacyLocally(result.notificationPrivacy);
        }
      }
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [currentUser, applyMyAvatarLocally, applyMyNotifyPrivacyLocally]);

  // JWT 만료 처리 — 만료 5분 전에 먼저 갱신하고, 그래도 만료되면 401 경로(웹 기기는 키 재개 → 실패 시 로그아웃)로 넘긴다.
  // 기기 신뢰가 켜지면 웹 토큰은 24시간이라 화면을 계속 켜 둔 폰도 하루에 한 번은 여기를 지난다.
  // 기존 401 리스너가 dispatchUnauthorized 수신 시 handleLogout 호출하므로
  // 익명 socket 전환은 위의 useEffect가 자동으로 처리.
  useEffect(() => {
    if (!currentUser) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return;
      const msUntilExpiry = payload.exp * 1000 - Date.now();
      if (msUntilExpiry <= 0) {
        dispatchUnauthorized();
        return;
      }
      const RENEW_LEAD_MS = 5 * 60 * 1000;
      const msUntilRenew = msUntilExpiry - RENEW_LEAD_MS;
      // setTimeout 지연 상한(2^31-1 ≈ 24.8일) 초과 시 즉시 발화해 버리므로 타이머를 걸지 않는다
      // (모바일 30일 토큰이 여기 해당 — 만료가 임박해지면 이후 재무장 시점에 타이머가 걸린다)
      if (msUntilRenew > 2147483647) return;
      let cancelled = false;
      const timer = setTimeout(async () => {
        const r = await authService.refreshToken();
        if (cancelled) return;
        if (r.refreshed) {
          lastTokenRefreshRef.current = Date.now();
          setTokenRefreshCount((c) => c + 1);
          return;
        }
        // 갱신 실패(네트워크 등) — 실제 만료 시각에 401 경로로
        const left = payload.exp * 1000 - Date.now();
        setTimeout(() => {
          if (cancelled) return;
          console.log('[Auth] Token expired (proactive detection)');
          dispatchUnauthorized();
        }, Math.max(0, left));
      }, Math.max(0, msUntilRenew));
      return () => { cancelled = true; clearTimeout(timer); };
    } catch {
      // 파싱 실패 → 다음 API 호출의 401에 의존
    }
  }, [currentUser, tokenRefreshCount]);

  // 나와의 채팅 열기 (처음 클릭 시 생성)
  const handleSelfChatClick = useCallback(async () => {
    if (!currentUser) return;

    const selfChannelId = await openSelfChat(String(currentUser.id));

    if (selfChannelId) {
      const selfChannel: Channel = {
        id: selfChannelId,
        name: '나와의 채팅',
        type: 'self',
      };

      setChannels(prev => {
        if (prev.some(ch => ch.id === selfChannelId)) {
          return prev;
        }
        return [...prev, selfChannel];
      });

      setDmPartnerName('나와의 채팅');
      setActiveChannel(selfChannel);
      openMobileChat();
    }
  }, [currentUser, openMobileChat]);

  // 채널 선택 핸들러
  const handleChannelSelect = useCallback((channel: Channel) => {
    if (channel.type === 'self') {
      setDmPartnerName('나와의 채팅');
    } else if (channel.type === 'dm') {
      setDmPartnerName(channel.name); // DM의 경우 name에 상대방 이름이 저장됨
    } else {
      setDmPartnerName(null);
    }
    setActiveChannel(channel);
    // 읽지 않은 메시지 수 리셋
    setUnreadCounts(prev => ({ ...prev, [channel.id]: 0 }));
    setIsSearchOpen(false);
    // 사용자가 직접 다른 채널을 선택하면 알림함 점프 보류분은 폐기
    pendingActivityJumpRef.current = null;
    openMobileChat();
  }, [openMobileChat]);

  // 사용자 클릭 → DM 열기. 기존 대화 이력이 있으면 그 방을, 없으면 서버 호출 없이 '초안 DM'을 연다.
  // 초안은 첫 메시지 전송·통화 시작 시점에 materializeDraftDm이 실제 방으로 승격한다.
  // (반환 boolean은 CreateGroupModal의 1명 선택 분기가 모달 닫기 판단에 쓴다)
  const handleUserClick = useCallback(async (user: User): Promise<boolean> => {
    if (!currentUser) return false;
    const partnerId = String(user.id);
    if (partnerId === String(currentUser.id)) return false;

    // 1) 이미 대화 이력이 있으면 실제 방을 연다.
    //    사번 매칭 우선 + 이름 폴백 (presence.ts의 findDmPartner와 같은 규칙)
    const list = channelsRef.current;
    const existing =
      list.find(c => c.type === 'dm' && c.dmPartnerId != null && String(c.dmPartnerId) === partnerId)
      ?? list.find(c => c.type === 'dm' && c.dmPartnerId == null && c.name === user.username);
    if (existing) {
      handleChannelSelect(existing);
      return true;
    }

    // 2) 없으면 초안 — 서버를 호출하지 않으므로 상대 목록에는 아무것도 뜨지 않는다.
    //    channels 배열에는 절대 넣지 않는다(목록·홈·검색·부팅 복원이 유령 방을 되살린다).
    setDmPartnerName(user.username);
    setActiveChannel({
      id: DRAFT_CHANNEL_ID,
      name: user.username, // DM은 상대방 이름을 name으로 쓴다
      type: 'dm',
      dmPartnerId: partnerId,               // 헤더 부제·온라인 점이 첫 메시지 전에도 동작
      dmPartnerAvatar: user.avatar || null,
      memberCount: 2,
      draftPartner: { id: partnerId, name: user.username },
    });
    setIsSearchOpen(false);
    pendingActivityJumpRef.current = null;
    openMobileChat();
    return true;
  }, [currentUser, handleChannelSelect, openMobileChat]);

  // 사람 클릭 → 프로필 모달. 말풍선 아바타·1:1 대화방 헤더·멤버 목록·조직도·동료 검색이 전부 여기로 들어오고,
  // 대화방은 모달의 '1:1 대화' 버튼이 handleUserClick으로 연다(초안 DM 규칙 그대로).
  // ChatWindow는 React.memo라 참조가 흔들리면 안 된다 — handleUserClick과 같은 deps
  const openUserProfile = useCallback((user: User) => {
    if (!currentUser || String(user.id) === String(currentUser.id)) return;
    setProfileUser(user);
  }, [currentUser]);

  const startDmFromProfile = useCallback((user: User) => {
    setProfileUser(null);
    handleUserClick(user);
  }, [handleUserClick]);

  // 그룹 채팅방 생성 완료 핸들러
  // 서버도 생성자 본인에게 channel:new를 보내므로(다른 세션 동기화용) 여기 추가는 멱등이어야 한다.
  // 소켓 이벤트가 REST 응답보다 먼저 도착하는 것이 정상 순서라, 가드가 없으면 매번 중복된다.
  const handleGroupCreated = useCallback((channel: Channel) => {
    setChannels(prev => prev.some(ch => ch.id === channel.id) ? prev : [...prev, channel]);
    setActiveChannel(channel);
    setDmPartnerName(null);
    openMobileChat();
  }, [openMobileChat]);

  // 사이드바/패널/모달 열고 닫기 (인라인 화살표 전달 시 매 렌더 새 참조 → memo 무력화 방지)
  const openCreateGroupModal = useCallback(() => setIsCreateGroupModalOpen(true), []);
  const openAvatarSettings = useCallback(() => setIsAvatarSettingsOpen(true), []);
  const openAdminConsole = useCallback(() => setIsAdminConsoleOpen(true), []);
  const openHelp = useCallback(() => setIsHelpOpen(true), []);
  // 특정 장으로 여는 별도 콜백 — 넓은 홈의 팁 카드·바닥글용. ⚠ openHelp에 인자를 붙이지 말 것:
  // onHelpClick={openHelp}처럼 클릭 핸들러로 바로 넘기는 곳이 있어 MouseEvent가 장 id로 들어간다
  const [helpSectionId, setHelpSectionId] = useState<string | undefined>(undefined);
  const openHelpAt = useCallback((sectionId?: string) => {
    setHelpSectionId(sectionId);
    setIsHelpOpen(true);
  }, []);
  const closeHelp = useCallback(() => {
    setIsHelpOpen(false);
    setHelpSectionId(undefined);
  }, []);
  // 채팅 이용 안내 재열람 — 프로필 설정 모달에서 진입한 경우 겹치지 않게 그쪽을 먼저 닫는다
  const openDisclaimerReview = useCallback(() => {
    setIsAvatarSettingsOpen(false);
    setIsDisclaimerReviewOpen(true);
  }, []);
  const closeDisclaimerReview = useCallback(() => setIsDisclaimerReviewOpen(false), []);
  // 동의 기록은 '동의 후 시작'을 누른 경우에만 남긴다 (재열람은 갱신하지 않음)
  const handleDisclaimerAgree = useCallback(() => {
    if (currentUser) recordDisclaimerAgreement(currentUser.id);
    setIsDisclaimerOpen(false);
  }, [currentUser]);

  // 아바타 변경 핸들러 — 저장이 끝난 뒤에 로컬에 반영한다(낙관적 반영 금지).
  // 먼저 반영하면 PUT이 실패했을 때 localStorage에만 남아 재로그인 시 조용히 되돌아간다.
  // alreadySaved: 사진 업로드(POST /api/profile/avatar/image)는 라우트가 이미
  //   applyAvatarChange(UPSERT + 세션 캐시 + io.emit)를 끝냈다. 여기서 PUT을 또 부르면
  //   UPSERT와 user:avatar-changed 전역 브로드캐스트가 2배가 되고, 두 요청의 순서가
  //   보장되지 않아 사진을 연달아 바꿀 때 DB가 이미 지워진 파일을 가리킬 수 있다.
  const handleAvatarChange = useCallback(async (
    avatarUrl: string,
    opts?: { alreadySaved?: boolean },
  ): Promise<void> => {
    if (!opts?.alreadySaved) {
      await dbService.updateAvatar(avatarUrl); // 실패는 throw — 모달이 받아서 토스트를 띄운다
    }
    applyMyAvatarLocally(avatarUrl);
  }, [applyMyAvatarLocally]);

  // 채팅방 나가기 핸들러 — 대화방 목록 컨텍스트 메뉴에서 호출, 확인 모달을 먼저 띄운다
  const handleLeaveChannel = useCallback((channel: Channel) => {
    if (!currentUser) return;
    if (!channel.id) return; // 초안 DM은 서버에 없다 (메뉴에서도 숨기지만 이중 안전)
    // group 또는 dm 타입만 나가기 가능
    if (channel.type !== 'group' && channel.type !== 'dm') return;
    setLeaveTarget(channel);
  }, [currentUser]);

  // 확인 모달에서 '나가기' 선택 시 실제 처리
  const confirmLeaveChannel = async () => {
    const target = leaveTarget;
    setLeaveTarget(null);
    if (!currentUser || !target) return;

    try {
      // 소켓 통지(member:removed)와 시스템 메시지는 서버 DELETE 라우트가 발행
      await channelService.leaveChannel(target.id, String(currentUser.id));

      // 나간 방의 최신 창 스냅샷은 버린다 — 남겨두면 같은 id로 재입장했을 때
      // 서버가 숨긴 과거 대화가 화면에 되살아난다
      dropMessageCache(target.id);

      // 채널 목록에서 제거
      const remaining = channels.filter(ch => ch.id !== target.id);
      setChannels(remaining);

      // 나간 방이 현재 열린 방일 때만 다른 방으로 전환
      if (activeChannel?.id === target.id) {
        // 첫 번째 채널로 이동 (헤더의 DM 상대 이름 state도 함께 동기화 — onChannelArchived 패턴)
        const next = remaining[0] ?? null;
        setDmPartnerName(next?.type === 'dm' ? next.name : null);
        setActiveChannel(next);
        // 모바일: 나간 방 대신 다른 방이 소리 없이 뜨는 것보다 목록으로 복귀가 자연스럽다
        setIsMobileChatOpen(false);
      }
    } catch (error) {
      console.error('Failed to leave channel:', error);
      showToast({ type: 'error', message: '채팅방을 나가지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  // 채널 이름·설명 패치 — 목록(channels)과 열린 방(activeChannel, localStorage 복원본 포함)을 함께 갱신.
  // REST 성공 직후와 소켓 channel:info-updated 에코 양쪽이 쓰므로 멱등이어야 한다.
  // 헤더 제목은 channels.find를 우선하지만 부팅 복원은 activeChannel을 그대로 그리므로 둘 다 패치한다.
  const applyChannelInfo = useCallback((channelId: number, updates: { name?: string | null; description?: string | null; nameAuto?: boolean }) => {
    const patch: Partial<Channel> = {};
    if (updates.name !== undefined) patch.name = updates.name;
    if (updates.description !== undefined) patch.description = updates.description ?? undefined;
    // 그룹방 자동 이름 여부 — 이름을 정하면 false, 멤버 변경으로 이름이 다시 계산되면 true로 온다
    if (updates.nameAuto !== undefined) patch.nameAuto = updates.nameAuto;
    if (Object.keys(patch).length === 0) return;
    setChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, ...patch } : ch));
    setActiveChannel(prev => (prev && prev.id === channelId ? { ...prev, ...patch } : prev));
  }, []);

  // 대화방 이름 변경 — 컨텍스트 메뉴에서 호출, 입력 모달을 띄운다 (leaveTarget 패턴)
  const handleRenameChannel = useCallback((channel: Channel) => {
    if (!channel.id) return; // 초안 DM은 서버에 없다 (메뉴에서도 숨기지만 이중 안전)
    if (channel.type !== 'group') return; // dm/self는 이름이 없고 public은 관리자 콘솔 소관
    setRenameTarget(channel);
  }, []);

  // 모달에서 '변경' 선택 시 실제 처리. 서버 성공 뒤에만 로컬 반영(낙관적 갱신 없음) —
  // 먼저 반영하면 실패가 localStorage에만 남아 다음 list-sync 때 조용히 되돌아간다.
  // 응답은 PG row(snake_case)라 state에 덮지 않고 요청한 이름으로 패치한다.
  const submitRename = useCallback(async (name: string): Promise<boolean> => {
    const target = renameTarget;
    if (!target) return false;
    try {
      await channelService.updateChannel(target.id, { name });
      applyChannelInfo(target.id, { name, nameAuto: false });
      setRenameTarget(null);
      showToast({ type: 'success', message: '대화방 이름을 변경했습니다.' });
      return true;
    } catch (err) {
      console.error('Failed to rename channel:', err);
      // 권한 거부(403)·검증 실패(400)는 서버 문구가 원인을 정확히 말한다("채널을 수정할 권한이 없습니다." 등).
      // 그 외(네트워크·5xx)는 재시도 안내
      const clientError = err instanceof ApiError && (err.status === 400 || err.status === 403) ? err.message : null;
      showToast({
        type: 'error',
        message: clientError ?? '대화방 이름을 변경하지 못했습니다. 잠시 후 다시 시도해 주세요.',
      });
      return false; // 모달 유지 — 입력한 이름을 잃지 않는다
    }
  }, [renameTarget, applyChannelInfo, showToast]);

  // 채팅방 고정/해제 (낙관적 업데이트, 실패 시 롤백)
  const handleTogglePin = useCallback(async (channel: Channel) => {
    if (!channel.id) return; // 초안 DM은 서버에 없다
    const next = !channel.isPinned;
    const nowIso = new Date().toISOString();
    setChannels(prev => prev.map(ch =>
      ch.id === channel.id ? { ...ch, isPinned: next, pinnedAt: next ? nowIso : null } : ch
    ));
    try {
      await channelService.setPinned(channel.id, next);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setChannels(prev => prev.map(ch =>
        ch.id === channel.id
          ? { ...ch, isPinned: channel.isPinned ?? false, pinnedAt: channel.pinnedAt ?? null }
          : ch
      ));
    }
  }, []);

  // 채팅방별 알림 뮤트/해제 (낙관적 업데이트, 실패 시 롤백)
  const handleToggleMute = useCallback(async (channel: Channel) => {
    if (!channel.id) return; // 초안 DM은 서버에 없다
    const next = !channel.isMuted;
    setChannels(prev => prev.map(ch =>
      ch.id === channel.id ? { ...ch, isMuted: next } : ch
    ));
    try {
      await channelService.setMuted(channel.id, next);
    } catch (err) {
      console.error('Failed to toggle mute:', err);
      setChannels(prev => prev.map(ch =>
        ch.id === channel.id ? { ...ch, isMuted: channel.isMuted ?? false } : ch
      ));
    }
  }, []);

  // 채널 계열사 가시성 가드 (defense-in-depth)
  const isChannelVisibleToCurrentUser = useCallback((ch: Channel): boolean => {
    if (ch.type !== 'public') return true;
    if (ch.affiliateId == null) return true;
    return ch.affiliateId === currentUser?.affiliateId;
  }, [currentUser]);

  /**
   * 서버 채널 목록을 받아 사이드바 상태를 동기화한다.
   * 로그인 직후뿐 아니라 재연결 복구에서도 호출하므로 콜백으로 분리했다.
   * @param opts.preserveActive 활성 채널 자동 선택을 건너뛴다. 재연결 때 켜지 않으면
   *   사용자가 보고 있던(타이핑 중이던) 채널에서 튕겨나간다.
   */
  const refreshChannels = useCallback(async (opts?: { preserveActive?: boolean }) => {
    if (!currentUser) return;

    try {
      const userChannels = await channelService.getChannels(String(currentUser.id));
      // 진단 로그 (1회): 서버 응답에 다른 계열사 공개 채널이 섞여 있는지 확인용
      const leaked = userChannels.filter(ch =>
        ch.type === 'public' && ch.affiliateId != null && ch.affiliateId !== currentUser.affiliateId
      );
      if (leaked.length > 0) {
        console.warn('[ChannelVisibility] Server leaked other-affiliate channels:',
          leaked.map(ch => ({ id: ch.id, name: ch.name, affiliateId: ch.affiliateId })));
      }
      const filtered = userChannels.filter(isChannelVisibleToCurrentUser);
      // 통째 교체가 아니라 병합 — REST 스냅샷과 소켓 channel:list-sync가 재연결 때 동시에
      // 도착하며 서로 다른 시점의 값을 들고 경쟁한다(둘 다 여기를 지난다)
      setChannels(prev => mergeChannelSnapshot(filtered, prev));
      setChannelsSynced(true); // 앱 아이콘 배지 게이트 해제

      // 서버 값이 권위. 비웠다 채우면 화면이 깜빡이고 그 사이 도착한 알림이 유실되므로
      // 한 번에 교체한다.
      setUnreadCounts(prev => {
        const next = { ...prev };
        filtered.forEach(ch => { next[ch.id] = ch.unreadCount ?? 0; });
        return next;
      });

      if (opts?.preserveActive) return;
      // 초안 DM을 열어 둔 상태면 activeChannel을 건드리지 않는다 — 초안은 서버 목록에 없으므로
      // 아래 "저장된 채널이 목록에 없으면 첫 번째로 이동" 분기가 매번 초안을 튕겨낸다
      if (draftPartnerRef.current) return;

      if (userChannels.length > 0) {
        // 알림 클릭으로 열린 경우 URL ?channel= 파라미터 우선 처리
        const urlParams = new URLSearchParams(window.location.search);
        const notifyChannelId = urlParams.get('channel');
        if (notifyChannelId) {
          const target = userChannels.find(ch => ch.id === Number(notifyChannelId));
          if (target) {
            setActiveChannel(target);
            setDmPartnerName(target.type === 'dm' ? target.name : null);
            setUnreadCounts(prev => ({ ...prev, [target.id]: 0 }));
            openMobileChat();
          } else {
            // 목록에 아직 없는 채널(방금 생성된 DM 등) — 보류해 두면 목록 갱신 시 소비 effect가 연다
            pendingNotifyChannelIdRef.current = Number(notifyChannelId);
          }
          // URL 파라미터 정리 (히스토리에 남기지 않음)
          // state 병합: 빈 객체로 덮으면 useMobileBackNav의 inaNav 태그가 지워진다
          window.history.replaceState({ ...(window.history.state || {}) }, '', window.location.pathname);
        } else if (activeChannelIdRef.current) {
          // 저장된 채널이 목록에 있는지 확인, 없으면 첫 번째로 이동
          const exists = userChannels.some(ch => ch.id === activeChannelIdRef.current);
          if (!exists) {
            setActiveChannel(userChannels[0]);
            setDmPartnerName(userChannels[0].type === 'dm' ? userChannels[0].name : null);
          }
        } else {
          setActiveChannel(userChannels[0]);
          setDmPartnerName(userChannels[0].type === 'dm' ? userChannels[0].name : null);
        }
      }
    } catch (error) {
      console.error('Failed to load channels:', error);
    }
  }, [currentUser, isChannelVisibleToCurrentUser, openMobileChat]);

  // ─── 알림 클릭 → 채널 열기 공통 경로 ───
  // 웹푸시(SW postMessage)·Tauri·브라우저 Notification·?channel= 딥링크가 모두 이 경로를 탄다.
  // 채널 목록이 아직 로드 전이면(콜드 스타트 직후 SW 메시지 등) 보류해 두고,
  // 목록이 채워지는 시점에 아래 소비 effect가 마저 연다. 누락 시 "알림 눌렀는데 홈만 뜸".
  const pendingNotifyChannelIdRef = useRef<number | null>(null);

  const applyChannelOpen = useCallback((target: Channel) => {
    setActiveChannel(target);
    setUnreadCounts(counts => ({ ...counts, [target.id]: 0 }));
    setDmPartnerName(target.type === 'dm' ? target.name : null);
    openMobileChat();
  }, [openMobileChat]);

  const openChannelFromNotification = useCallback((channelId: number) => {
    if (!Number.isFinite(channelId)) return;
    setChannels(prev => {
      const target = prev.find(ch => ch.id === channelId);
      if (target) {
        pendingNotifyChannelIdRef.current = null;
        applyChannelOpen(target);
      } else {
        pendingNotifyChannelIdRef.current = channelId;
        refreshChannels({ preserveActive: true });
      }
      return prev;
    });
  }, [applyChannelOpen, refreshChannels]);

  // 보류된 알림 채널 소비: 채널 목록이 갱신될 때마다 확인
  useEffect(() => {
    const pendingId = pendingNotifyChannelIdRef.current;
    if (pendingId == null) return;
    const target = channels.find(ch => ch.id === pendingId);
    if (target) {
      pendingNotifyChannelIdRef.current = null;
      applyChannelOpen(target);
    }
  }, [channels, applyChannelOpen]);

  // 채널 목록 로딩 (로그인 상태일 때)
  useEffect(() => {
    if (!currentUser) return;
    refreshChannels();
  }, [currentUser, refreshChannels]);

  // 소켓 연결 (로그인 상태일 때만)
  useEffect(() => {
    if (!currentUser) return;

    // Web Push 구독 (앱 복원 시에도 재등록)
    if (!isTauri() && 'Notification' in window && Notification.permission === 'granted') {
      subscribeToPush();
    }

    // 1. 소켓 연결
    const socket = socketService.connect(currentUser);

    // 2. 콜백 설정
    socketService.setCallbacks({
      onConnect: () => {
        setIsConnected(true);
        console.log('Socket connected');
        // (재)연결 직후 활동을 바로 보고 — 끊긴 사이 서버는 이 세션의 활동을 잊었으므로 30초 공백을 없앤다
        sendActivityNowRef.current();
      },
      onDisconnect: () => {
        setIsConnected(false);
      },
      onReconnect: () => {
        console.log('[App] Socket reconnected - resyncing channels');
        // 끊긴 사이의 call:status는 유실됐다 — 비우면 user:online 직후 서버가 진행 중인 통화를 다시 보낸다
        setChannelCalls({});
        // 진행 중인 통화가 있으면 같은 자리에 다시 묶는다(서버 유예 20초 안이면 그대로, 지났으면 새로 참가)
        void callController.onSocketReconnected();
        // 채널 목록을 다시 받는다. 오프라인 동안 초대되었거나 삭제된 채널이 반영되지 않으면
        // 전체 새로고침 전까지 사이드바가 어긋난 채로 남는다.
        // preserveActive: 보고 있던 채널에서 튕겨나가지 않도록.
        // 서버도 user:online에 응답해 channel:list-sync를 보내지만, 재연결은 두 경로 중
        // 하나가 불안정한 시점이라 양쪽 모두 둔다 (둘 다 멱등).
        refreshChannels({ preserveActive: true });
        // 끊긴 사이 'config:*' emit을 놓쳤을 수 있다. 채널 목록과 같은 이유로 다시 받는다.
        fetchUploadPolicy();
        fetchBlockedExtensions();
        // 끊긴 사이의 scheduled-message:* 이벤트(다른 세션의 등록·취소, 워커의 발송·실패)도 유실됐다
        scheduledMessageService.listMine()
          .then(list => setMyScheduled(list))
          .catch(() => {});
        // 끊긴 사이 room 브로드캐스트(message:new)는 유실됐다. 채널 캐시는 통째로 신뢰할 수 없으니
        // 비우고, 활성 채널은 마지막 id 이후분을 REST로 받아 붙인다 (catch-up 안에서 화면에
        // 실제로 채워진 메시지만 읽음 처리한다 — 보지도 않은 메시지를 먼저 읽음 처리하지 않도록).
        messagesCacheRef.current.clear();
        if (activeChannelIdRef.current) {
          const channelId = activeChannelIdRef.current;
          catchUpLatestMessagesRef.current(channelId).finally(() => {
            // 현재 활성 채널이 보이는 중이면 읽음 처리 재전송
            if (activeChannelIdRef.current === channelId && isChatVisibleRef.current) {
              socketService.markChannelRead(channelId);
            }
          });
        }
      },
      // 서버가 보내는 채널 목록 스냅샷 (접속·재연결 시)
      onChannelListSync: ({ channels: syncedChannels }) => {
        const visible = (syncedChannels || []).filter(isChannelVisibleToCurrentUser);
        // 통째 교체가 아니라 병합 — 스냅샷을 뜬 뒤 발송하기까지 사이에 도착한 메시지를 지우지 않는다
        setChannels(prev => mergeChannelSnapshot(visible, prev));
        setChannelsSynced(true); // 앱 아이콘 배지 게이트 해제
        setUnreadCounts(prev => {
          const next = { ...prev };
          visible.forEach(ch => { next[ch.id] = ch.unreadCount ?? 0; });
          return next;
        });
      },
      // 내가 다른 기기에서 채널을 읽었다 → 이 세션의 안읽음 뱃지도 사라져야 한다
      onChannelSelfRead: ({ channelId }) => {
        setUnreadCounts(prev => (
          prev[channelId] ? { ...prev, [channelId]: 0 } : prev
        ));
      },
      // 온라인 사용자 목록 수신
      onOnlineList: (users) => {
        setOnlineUsers(users);
      },
      // 사용자 상태 변경
      onUserStatusChanged: (payload) => {
        setOnlineUsers((prev) => {
          if (payload.status === 'offline') {
            return prev.filter((u) => u.userId !== payload.userId);
          }
          const index = prev.findIndex((u) => u.userId === payload.userId);
          if (index >= 0) {
            const updated = [...prev];
            // avatar/depart는 payload에 있을 때만 갱신 (구버전 서버는 status만 보냄)
            updated[index] = {
              ...updated[index],
              status: payload.status,
              ...(payload.avatar !== undefined ? { avatar: payload.avatar } : {}),
              ...(payload.depart !== undefined ? { depart: payload.depart } : {}),
            };
            return updated;
          }
          return [...prev, { userId: payload.userId, userName: payload.userName, status: payload.status, depart: payload.depart, affiliateId: payload.affiliateId, avatar: payload.avatar ?? null }];
        });
      },
      onMessageRead: (payload) => {
        if (Number(payload.channelId) === activeChannelIdRef.current) {
          setMessages((prev) => {
            if (prev.length === 0) return prev;
            return prev.map((msg) => {
              const newCount = payload.unreadCounts[String(msg.id)];
              if (newCount !== undefined) {
                return { ...msg, unreadCount: newCount };
              }
              return msg;
            });
          });
        }
      },
      onMessageNew: (message) => {
        // 홈 화면 미리보기용: 활성 채널 여부와 무관하게 lastMessage 갱신
        // (재연결 시에는 channel:list-sync가 서버 권위값으로 덮어씀)
        setChannels(prev => prev.map(ch => ch.id === message.channelId
          ? applyLastMessage(ch, { id: message.id, text: message.text || '(첨부파일)', at: message.timestamp })
          : ch));
        const incoming: Message = {
          id: message.id,
          channelId: message.channelId,
          text: message.text,
          senderId: message.senderId,
          senderName: message.senderName,
          senderAvatar: (message as any).senderAvatar || DEFAULT_AVATAR,
          timestamp: message.timestamp,
          msgType: message.msgType,
          replyToId: (message as any).replyToId ?? null,
          attachments: (message as any).attachments || [],
          mentions: (message as any).mentions || [],
          forwardedFrom: (message as any).forwardedFrom || null,
          unreadCount: (message as any).unreadCount ?? 0,
          // 이미 수집된 URL만 여기 실린다. 처음 보는 링크는 message:link-preview로 뒤따라온다.
          linkPreview: (message as any).linkPreview || null,
          // 일정 공유 카드 — 빠지면 서버가 보내도 새로고침 전까지 대체 텍스트 말풍선으로만 보인다
          calendarEvent: (message as any).calendarEvent || null,
        };
        // 채널별 '최신 창' 캐시도 같이 최신화한다. 안 하면 재진입 때 "새 메시지가 빠진 옛 창"이 먼저
        // 그려지고 서버 응답이 올 때까지 방금 온 메시지가 안 보인다 (활성/비활성 채널 모두).
        const cachedWindow = messagesCacheRef.current.get(message.channelId);
        if (cachedWindow && !cachedWindow.some(m => String(m.id) === String(incoming.id))) {
          messagesCacheRef.current.set(message.channelId, [...cachedWindow, incoming].slice(-100));
        }
        // ref를 사용하여 최신 activeChannel.id 참조
        // 과거 시점을 열람 중이면 append하지 않는다 (2019년 대화 옆에 오늘 메시지가 붙는 것 방지).
        // 놓친 메시지는 '최근 대화로' 복귀 시 최신 창을 다시 받아오면서 채워진다.
        if (message.channelId === activeChannelIdRef.current && !isViewingHistoryRef.current) {
          // catch-up REST 응답과 소켓 이벤트가 겹칠 수 있으므로 id 중복은 버린다
          setMessages((prev) => (
            prev.some(m => String(m.id) === String(incoming.id)) ? prev : [...prev, incoming]
          ));
          // 활성 채널에서 새 메시지 수신 시 읽음 처리 (포커스 + 채팅방이 실제로 보일 때만)
          if (isWindowFocusedRef.current && isChatVisibleRef.current) {
            if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
            markReadTimerRef.current = setTimeout(() => {
              socketService.markChannelRead(message.channelId, [message.id]);
            }, 300);
          }
        }
      },
      // 다른 사용자가 삭제한 메시지 실시간 반영.
      // id 비교는 반드시 String()으로 정규화한다 — messages.id는 PG BIGINT라 문자열로 오는데
      // 이 payload는 REST/소켓 두 경로로 오므로 한쪽만 숫자여도 map이 조용히 아무것도 안 바꾼다.
      onMessageLinkPreview: (payload) => {
        const channelId = Number(payload.channelId);
        // 캐시 갱신은 활성 채널 가드 '밖'에 둔다 — onMessageDeleted와 같은 이유로,
        // 다른 방의 카드가 캐시에 없으면 재진입 때 카드 없는 창이 먼저 그려진다.
        const cachedWindow = messagesCacheRef.current.get(channelId);
        if (cachedWindow) {
          messagesCacheRef.current.set(channelId, cachedWindow.map((msg) =>
            String(msg.id) === String(payload.messageId)
              ? { ...msg, linkPreview: payload.preview }
              : msg
          ));
        }
        if (channelId === activeChannelIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              String(msg.id) === String(payload.messageId)
                ? { ...msg, linkPreview: payload.preview }
                : msg
            )
          );
        }
      },
      // 공유한 일정이 삭제됨 — 방 단위로 오므로 그 방에서 같은 일정을 가리키는 카드를 전부 고친다.
      // payload에는 제목이 없다(공유 뒤 들어온 멤버에게도 가는 룸 전파라) — 이미 가진 제목을 남긴다.
      onMessageCalendarCard: (payload) => {
        const channelId = Number(payload.channelId);
        const eventId = String(payload.eventId);
        const patch = (list: Message[]) => {
          let changed = false;
          const next = list.map((msg) => {
            if (!msg.calendarEvent || String(msg.calendarEvent.id) !== eventId) return msg;
            changed = true;
            const card = payload.card.deleted
              ? { ...payload.card, title: payload.card.title || msg.calendarEvent.title }
              : payload.card;
            return { ...msg, calendarEvent: card };
          });
          return changed ? next : list;
        };
        // 캐시 갱신은 활성 채널 가드 '밖'에 둔다 — onMessageLinkPreview와 같은 이유
        const cachedWindow = messagesCacheRef.current.get(channelId);
        if (cachedWindow) messagesCacheRef.current.set(channelId, patch(cachedWindow));
        if (channelId === activeChannelIdRef.current) setMessages(patch);
      },
      onMessageDeleted: (payload) => {
        const channelId = Number(payload.channelId);
        // 캐시 갱신은 활성 채널 가드 '밖'에 둔다. 다른 방에서 삭제된 메시지가 캐시에 남으면
        // 그 방에 재진입할 때 캐시된 창이 삭제 전 원문을 먼저 그린다(서버 응답이 덮기 전까지).
        const cachedWindow = messagesCacheRef.current.get(channelId);
        if (cachedWindow) {
          messagesCacheRef.current.set(channelId, cachedWindow.map((msg) =>
            String(msg.id) === String(payload.messageId)
              ? { ...msg, deletedAt: payload.deletedAt }
              : msg
          ));
        }
        if (channelId === activeChannelIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              String(msg.id) === String(payload.messageId)
                ? { ...msg, deletedAt: payload.deletedAt }
                : msg
            )
          );
        }
      },
      // 다른 사용자(또는 내 다른 세션)가 수정한 메시지 실시간 반영 — 서버가 발신자 포함 전체 에코(io.to)
      onMessageEdited: (payload) => {
        // 수정된 것이 그 채널의 마지막 메시지면 목록 미리보기도 바뀌어야 한다.
        // id가 그대로라 applyLastMessage(단조성 가드)로는 통과하지 못하므로 직접 갈아끼운다.
        setChannels((prev) => prev.map(ch =>
          String(ch.lastMessageId ?? '') === String(payload.messageId)
            ? { ...ch, lastMessage: payload.content }
            : ch
        ));
        if (Number(payload.channelId) === activeChannelIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              String(msg.id) === String(payload.messageId)
                ? {
                    ...msg,
                    text: payload.content,
                    updatedAt: payload.updatedAt,
                    mentions: (payload as any).mentions ?? msg.mentions,
                    // 필드가 없으면(구버전 서버) '모름'이므로 기존 값을 유지하고,
                    // null이면 링크를 지운 것이므로 카드를 없앤다
                    linkPreview: (payload as any).linkPreview !== undefined
                      ? (payload as any).linkPreview
                      : msg.linkPreview,
                  }
                : msg
            )
          );
        }
      },
      // 리액션 업데이트
      onReactionUpdated: (payload) => {
        if (payload.channelId === activeChannelIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              String(msg.id) === String(payload.messageId)
                ? { ...msg, reactions: payload.reactions }
                : msg
            )
          );
        }
      },
      // 로그인 시 보류 알림 배치 (채널별 개별 이벤트 대신 1회 상태 갱신으로 리렌더 최소화)
      onMessageNotifyBatch: (payload) => {
        const isBackground = !isWindowFocusedRef.current;

        // 가시성 필터: 아카이브 채널 + 다른 계열사 공개 채널 차단 (defense-in-depth)
        const visible = payload.notifications.filter(n => {
          if (n.channel?.isArchived) return false;
          if (n.channel && n.channel.type === 'public'
              && n.channel.affiliateId != null
              && n.channel.affiliateId !== currentUser?.affiliateId) {
            console.warn('[ChannelVisibility] Blocked pending notification for other-affiliate channel:',
              { id: n.channel.id, name: n.channel.name, affiliateId: n.channel.affiliateId });
            return false;
          }
          return true;
        });
        if (visible.length === 0) return;

        // 채널 목록에 없는 채널을 한 번에 추가
        setChannels(prev => {
          const missing = visible.filter(n => !prev.some(ch => ch.id === n.channelId));
          if (missing.length === 0) return prev;
          const newChannels: Channel[] = missing.map(n => ({
            id: n.channel.id,
            name: n.channel.name,
            type: n.channel.type,
            description: n.channel.description,
            affiliateId: n.channel.affiliateId,
            isArchived: n.channel.isArchived,
          } as Channel));
          console.log(`[App] ${newChannels.length} channels added from message:notify-batch`);
          return [...prev, ...newChannels];
        });

        // 읽지 않은 수를 한 번에 반영 (서버가 계산한 절대값 사용)
        setUnreadCounts(prev => {
          const next = { ...prev };
          let changed = false;
          for (const n of visible) {
            const isCurrentChannel = n.channelId === activeChannelIdRef.current;
            if ((!isCurrentChannel || isBackground) && n.unreadCount > 0) {
              next[n.channelId] = n.unreadCount;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      // 실시간 메시지 알림 (채널 정보 포함)
      onMessageNotify: (payload) => {
        const { channelId, channel, senderId, isPendingNotification, unreadCount } = payload as any;

        // 로그인 시 보류 알림: 신버전 서버는 message:notify-batch로 먼저 일괄 처리됨.
        // 여기서는 멱등 폴백만 수행 (구버전 서버 호환; 배치 처리 후에는 상태 변화 없음 → 리렌더 없음)
        if (isPendingNotification) {
          if (channel?.isArchived) return;
          if (channel && channel.type === 'public'
              && channel.affiliateId != null
              && channel.affiliateId !== currentUser?.affiliateId) return;

          setChannels(prev => {
            if (prev.some(ch => ch.id === channelId)) return prev;
            return [...prev, {
              id: channel.id,
              name: channel.name,
              type: channel.type,
              description: channel.description,
              affiliateId: channel.affiliateId,
              isArchived: channel.isArchived,
            } as Channel];
          });

          // 과거 열람 중이면 그 채널을 '보고 있는' 것이 아니므로 뱃지가 올라야 한다
          const isCurrentChannel = channelId === activeChannelIdRef.current && !isViewingHistoryRef.current;
          const isBackground = !isWindowFocusedRef.current;
          if ((!isCurrentChannel || isBackground) && unreadCount > 0) {
            setUnreadCounts(prev =>
              prev[channelId] === unreadCount ? prev : { ...prev, [channelId]: unreadCount }
            );
          }
          return;
        }

        // 자신이 보낸 메시지면 무시
        if (senderId === String(currentUser?.id)) return;

        // 아카이브(삭제)된 채널은 주입/알림 무시 (defense-in-depth: 서버가 이미 필터하지만 이중 방어)
        if (channel?.isArchived) return;

        // 계열사 가시성 가드 (defense-in-depth): 다른 계열사 공개 채널 알림 차단
        if (channel && channel.type === 'public'
            && channel.affiliateId != null
            && channel.affiliateId !== currentUser?.affiliateId) {
          console.warn('[ChannelVisibility] Blocked message:notify for other-affiliate channel:',
            { id: channel.id, name: channel.name, affiliateId: channel.affiliateId });
          return;
        }

        // 채널 목록에 없으면 추가
        setChannels(prev => {
          if (prev.some(ch => ch.id === channelId)) {
            return prev;
          }
          // 새 채널 추가
          const newChannel: Channel = {
            id: channel.id,
            name: channel.name,
            type: channel.type,
            description: channel.description,
            affiliateId: channel.affiliateId,
            isArchived: channel.isArchived,
          };
          console.log(`[App] New channel added from message:notify - ${channel.name} (${channel.type})${isPendingNotification ? ' (pending)' : ''}`);
          return [...prev, newChannel];
        });

        // 홈 화면 미리보기용 lastMessage 갱신 (message:new를 못 받는 경로 대비 멱등 보강)
        setChannels(prev => prev.map(ch => ch.id === channelId
          ? applyLastMessage(ch, {
              id: (payload as any).id,
              text: payload.text || '(첨부파일)',
              at: (payload as any).timestamp || new Date().toISOString(),
            })
          : ch));

        // 같은 계정의 다른 세션이 이 채널을 보고 있다고 서버가 판단한 경우.
        // 채널 주입은 위에서 이미 끝났으므로 뱃지만 정리하고 알림은 건너뛴다.
        // (구버전 서버·클라이언트는 이 필드가 없어 기존 동작 그대로다.)
        if ((payload as any).alreadyRead) {
          setUnreadCounts(prev => (prev[channelId] ? { ...prev, [channelId]: 0 } : prev));
          return;
        }

        // 과거 열람 중이거나 모바일 홈/목록 화면이면 그 채널을 '보고 있는' 것이 아니므로 뱃지가 올라야 한다
        const isCurrentChannel = channelId === activeChannelIdRef.current && !isViewingHistoryRef.current && isChatVisibleRef.current;
        const isBackground = !isWindowFocusedRef.current;

        // 푸시 알림 표시 헬퍼
        const showPushNotification = (title: string, body: string, chId: number, shouldFlash = false) => {
          if (isTauri()) {
            import('@tauri-apps/api/core').then(({ invoke }) => {
              invoke('show_chat_notification', { title, body, channelId: String(chId) });
            }).catch(() => {});
            // 숫자 배지는 여기서 건드리지 않는다 — 아래 setUnreadCounts가 totalUnread를 바꾸면
            // 파생 effect(setAppBadgeCount)가 정확한 총계로 칠한다.
            if (shouldFlash) void flashTaskbar(false);
            setFlashBg(true);
            setTimeout(() => setFlashBg(false), 1200);
          } else if (document.visibilityState === 'visible' && !isWebKitPushDevice()) {
            // 알림 소유권 규칙: 페이지는 화면에 보일 때만 시스템 알림을 만든다.
            // hidden(백그라운드·화면잠금)이면 SW 웹푸시가 담당 — 여기서도 만들면 같은 메시지에 알림이 2번 울린다.
            // (SW 쪽 대칭 규칙: 포커스된 클라이언트가 있으면 push 알림 skip — service-worker.js)
            // ⚠ Apple 웹푸시 기기(아이폰)는 예외로 SW가 단독 소유한다. WebKit은 보이는 창이 있어도 푸시마다
            //   알림을 띄우게 강제해서(안 띄우면 3회째에 구독 삭제) SW가 건너뛸 수 없고, tag로 합치지도 않는다.
            // 권한 검사와 Android Chrome의 new Notification() 금지는 showWebNotification이 흡수한다 —
            // 예전에는 여기서 던진 예외가 아래 탭 제목 점멸까지 막았다.
            void showWebNotification(
              title,
              { body, tag: `ch-${chId}`, data: { channelId: chId } },
              () => {
                window.focus();
                openChannelFromNotification(chId);
              }
            );
          }
          // 웹 탭 제목 점멸 (작업표시줄 반영)
          if (!isTauri() && !isWindowFocusedRef.current) {
            if (!blinkIntervalRef.current) {
              let show = true;
              blinkIntervalRef.current = setInterval(() => {
                document.title = show ? `[새 메시지] ${title}` : 'INA-TALK 인아 채팅 프로그램';
                show = !show;
              }, 1000);
            }
          }
        };

        // 채팅방별 알림 뮤트: 토스트/깜빡임만 억제, 안읽음 배지는 계속 증가
        // 단, 내가 멘션 대상이면(@) 뮤트를 우회한다 — 서버 OS 푸시 필터와 동일 정책
        const isMutedChannel = mutedChannelIdsRef.current.has(channelId) && payload.mentionsMe !== true;

        // 알림에 표시할 제목·본문 조립. 계정 설정('전체 표시'/'보낸 사람만'/'전부 숨김')을 반영하며
        // 계산은 한 번만 한다 — 두 벌로 두면 갈라진다(첨부 폴백은 헬퍼가 흡수).
        // ⚠ 휴대폰 웹푸시는 이 경로를 타지 않는다. 서버 payload에 본문이 아예 없어서
        //   '전체 표시'여도 폰에서는 발신자까지만 보이고, 서버는 '전부 숨김'일 때 발신자를 뺄 뿐이다.
        const notify = buildNotificationText(notifyPrivacyRef.current, {
          channelName: channel?.name,
          senderName: payload.senderName,
          text: payload.text,
          // 그룹 대화방은 제목이 방 이름이라 본문 앞에 발신자를 붙여야 누가 보냈는지 알 수 있다.
          // 1:1은 제목이 이미 상대 이름이라 붙이지 않는다 — 판정은 헬퍼가 한다.
          channelType: channel?.type,
        });

        // 백그라운드면 현재 채널이어도 알림 표시 (작업표시줄 깜빡임 포함)
        if (isBackground && !isMutedChannel) {
          showPushNotification(notify.title, notify.body, channelId, true);
        }

        // 현재 활성 채널이 아니거나 백그라운드면 읽지 않은 수 증가 + 푸시 알림
        if (!isCurrentChannel || isBackground) {
          // 푸시 알림 (포그라운드에서 다른 채널일 때)
          if (!isBackground && !isMutedChannel) {
            showPushNotification(notify.title, notify.body, channelId);
          }

          // 실시간 알림: 기존 로직대로 1씩 증가
          setUnreadCounts(prev => ({
            ...prev,
            [channelId]: (prev[channelId] || 0) + 1
          }));
        }
      },
      // 내가 다른 기기에서 알림 표시 수준을 바꿨다 (서버는 내 세션에만 보낸다)
      onNotifyPrivacyChanged: ({ privacy }) => {
        applyMyNotifyPrivacyLocally(normalizeNotificationPrivacy(privacy));
      },
      // 내 수동 상태 — 다른 기기에서의 변경 + 접속 직후 서버가 보내 주는 현재 값
      onPresenceManualChanged: ({ manual }) => {
        applyMyPresenceManualLocally(manual === 'away' || manual === 'busy' ? manual : null);
      },
      // 다른 사용자 아바타 변경
      onUserAvatarChanged: (payload) => {
        const { userId, avatarUrl } = payload;
        // 현재 표시된 메시지들의 아바타 업데이트
        setMessages((prev) =>
          prev.map((msg) =>
            String(msg.senderId) === String(userId)
              ? { ...msg, senderAvatar: avatarUrl }
              : msg
          )
        );
        // 접속자 목록(조직도)의 아바타 갱신
        setOnlineUsers((prev) =>
          prev.map((u) =>
            String(u.userId) === String(userId) ? { ...u, avatar: avatarUrl } : u
          )
        );
        // DM 채널 목록의 상대방 아바타 갱신 (모바일 대화방 목록)
        setChannels((prev) =>
          prev.map((ch) =>
            ch.type === 'dm' && ch.dmPartnerId && String(ch.dmPartnerId) === String(userId)
              ? { ...ch, dmPartnerAvatar: avatarUrl }
              : ch
          )
        );
        // 계열사 명부 캐시(lazy 래치, TTL 없음)의 아바타도 패치 — 안 하면 PWA처럼 문서가
        // 몇 주 사는 환경에서 홈 검색·멘션·초대 목록이 영구히 옛 사진을 보여준다
        patchUserDirectoryAvatar(userId, avatarUrl);
        // 내가 다른 기기에서 아바타를 바꾼 경우. 이걸 반영하지 않으면 사이드바 프로필 사진이
        // 재로그인 전까지 옛 이미지로 남는다. 직접 변경과 같은 헬퍼를 써서 부수효과를 맞춘다.
        if (currentUser && String(userId) === String(currentUser.id)) {
          applyMyAvatarLocally(avatarUrl);
        }
      },
      // 새 채널에 초대되었을 때
      onChannelNew: (payload) => {
        const ch = payload.channel;
        // 계열사 가시성 가드 (defense-in-depth): public 채널은 NULL/자기 계열사만 허용
        if (ch.type === 'public' && ch.affiliateId != null && ch.affiliateId !== currentUser?.affiliateId) {
          console.warn('[ChannelVisibility] Blocked channel:new for other-affiliate channel:',
            { id: ch.id, name: ch.name, affiliateId: ch.affiliateId });
          return;
        }
        // 새 방이 생겼으니 대화방 검색의 참여자 인덱스를 다음 오픈에 다시 받는다
        invalidateChannelMemberIndex();
        setChannels((prev) => {
          // 이미 있는 채널이면 서버가 준 미리보기만 반영한다 — 예전에는 통째로 무시해서
          // channel:new에 실려온 최신 lastMessage를 버렸다 (단조성 가드가 역행은 막아준다)
          if (prev.some(c => c.id === ch.id)) {
            return prev.map(c => c.id === ch.id
              ? applyLastMessage(c, { id: ch.lastMessageId, text: ch.lastMessage ?? null, at: ch.lastMessageAt ?? null })
              : c);
          }
          return [...prev, ch];
        });
        // 초안을 열어 둔 상대가 먼저 방을 만들었다면(그쪽이 먼저 말을 걸었다) 조용히 실제 방으로
        // 승격한다. 없으면 내가 보낼 때까지 상대 메시지가 화면에 안 나타나 오해를 부른다.
        if (ch.type === 'dm' && activeChannelIdRef.current === DRAFT_CHANNEL_ID
            && draftPartnerRef.current && String(ch.dmPartnerId) === String(draftPartnerRef.current.id)) {
          draftPartnerRef.current = null;
          activeChannelIdRef.current = ch.id;
          setDmPartnerName(ch.name);
          setActiveChannel(ch);
        }
      },

      // 멤버 초대 — 목록 갱신은 불요(memberCount는 channel:info-updated 경로).
      // 대화방 검색의 참여자 인덱스만 무효화해 다음 검색창 오픈에 새 멤버가 잡히게 한다
      onMemberAdded: () => {
        invalidateChannelMemberIndex();
      },

      // 멤버 퇴장(나가기/강퇴) — 본인이면 모든 세션에서 채널 제거 (요청 보낸 세션의 에코는 멱등)
      onMemberRemoved: (payload) => {
        // 타인 퇴장도 참여자 인덱스를 바꾸므로 아래 early return보다 앞에서 무효화한다
        invalidateChannelMemberIndex();
        if (String(payload.userId) !== String(currentUser?.id)) {
          // 타인 퇴장: 멤버 목록은 MemberListModal이 열릴 때 재조회하므로 별도 처리 불요
          return;
        }
        // 내가 빠진 방의 최신 창 스냅샷은 버린다 (confirmLeaveChannel과 같은 이유 —
        // 다른 기기에서 나갔을 때는 이 경로만 탄다)
        dropMessageCache(payload.channelId);
        setChannels((prev) => {
          const remaining = prev.filter(ch => ch.id !== payload.channelId);
          // 해당 채널을 보고 있었다면 첫 번째 남은 채널로 전환 (onChannelArchived 패턴)
          setActiveChannel((cur) => {
            if (!cur || cur.id !== payload.channelId) return cur;
            const next = remaining[0] ?? null;
            setDmPartnerName(next?.type === 'dm' ? next.name : null);
            return next;
          });
          return remaining;
        });
        setUnreadCounts(prev => {
          if (!(payload.channelId in prev)) return prev;
          const { [payload.channelId]: _removed, ...rest } = prev;
          return rest;
        });
      },

      // 채널이 아카이브(삭제)되었을 때 사이드바에서 즉시 제거
      onChannelArchived: (payload) => {
        dropMessageCache(payload.channelId);
        setChannels((prev) => {
          const remaining = prev.filter(ch => ch.id !== payload.channelId);
          // 아카이브된 채널을 보고 있었다면 첫 번째 남은 채널로 전환 (handleLeaveChannel 패턴)
          setActiveChannel((cur) => {
            if (!cur || cur.id !== payload.channelId) return cur;
            const next = remaining[0] ?? null;
            setDmPartnerName(next?.type === 'dm' ? next.name : null);
            return next;
          });
          return remaining;
        });
      },

      // 다른 기기에서 채팅방 고정/해제 시 실시간 동기화 (발신 기기의 에코는 멱등 반영)
      onChannelPinChanged: (payload) => {
        setChannels((prev) => prev.map(ch =>
          ch.id === payload.channelId
            ? { ...ch, isPinned: payload.isPinned, pinnedAt: payload.pinnedAt }
            : ch
        ));
      },

      // 다른 기기에서 채팅방 알림 뮤트/해제 시 실시간 동기화
      onChannelMuteChanged: (payload) => {
        setChannels((prev) => prev.map(ch =>
          ch.id === payload.channelId ? { ...ch, isMuted: payload.isMuted } : ch
        ));
      },

      // 마지막 메시지가 삭제됐을 때 서버가 보내는 미리보기 정정.
      // 이 경로만 applyLastMessage의 단조성 가드를 우회한다 — 삭제는 lastMessageId가
      // 내려가야 하는 유일한 경우라 가드를 태우면 영영 반영되지 않는다.
      onChannelLastMessage: (payload) => {
        setChannels((prev) => prev.map(ch =>
          String(ch.id) === String(payload.channelId)
            ? {
                ...ch,
                lastMessageId: payload.lastMessageId,
                lastMessage: payload.lastMessage,
                lastMessageAt: payload.lastMessageAt,
              }
            : ch
        ));
      },

      // 채널 공지 등록/해제 실시간 반영 — 배너는 channels에서 파생되므로 여기만 갱신하면 된다
      onChannelNoticeChanged: (payload) => {
        setChannels((prev) => prev.map(ch =>
          ch.id === payload.channelId ? { ...ch, notice: payload.notice } : ch
        ));
      },

      // 대화방 이름·설명 변경 실시간 반영 — PUT /api/channels/:id가 방 룸 전체(내 다른 세션 포함)에 전파.
      // 룸은 user:online에서 보이는 채널 전부에 조인되므로 방을 열어두지 않은 멤버도 받는다.
      // 자동 이름 그룹방은 멤버가 들고 날 때 서버가 사람마다 자기 기준 이름을 따로 보낸다(nameAuto: true)
      onChannelUpdated: (payload) => {
        applyChannelInfo(payload.channelId, {
          name: payload.updates.name,
          description: payload.updates.description,
          nameAuto: payload.nameAuto,
        });
      },

      // 예약 메시지 동기화 (내 계정 전 세션 대상 — 다른 세션의 등록·취소와 워커의 발송·실패).
      // 관리자 공지 예약(isAdminNotice)은 채팅방 예약 목록이 아니라 관리자 콘솔 소관이라 걸러낸다
      onScheduledMessageCreated: (sm) => { if (!sm.isAdminNotice) upsertScheduled(sm); },
      onScheduledMessageUpdated: (sm) => { if (!sm.isAdminNotice) upsertScheduled(sm); },
      onScheduledMessageCanceled: (sm) => removeScheduled(sm.id),
      onScheduledMessageSent: (sm) => {
        // 발송된 메시지 본문은 워커의 message:new가 정상 경로로 가져온다 — 여기선 목록만 정리
        removeScheduled(sm.id);
      },
      onScheduledMessageFailed: (sm) => {
        if (!sm.isAdminNotice) upsertScheduled(sm);
        showToast({ type: 'error', message: '예약 메시지를 보내지 못했습니다. 예약 목록에서 사유를 확인해 주세요.' });
      },

      // 화상 통화 이벤트
      onCallIncoming: (payload) => {
        // 자신이 발신자면 무시
        if (payload.callerId === String(currentUser?.id)) return;
        // 이 세션이 이미 통화 중이면 이 기기에서만 무시한다.
        // 예전에는 여기서 대신 거절해버려, 같은 계정의 다른 기기가 받을 기회를 뺏었고
        // 그 거절이 채널 룸을 타고 돌아와 진행 중이던 내 통화까지 끊었다.
        // 같은 벨이 겹쳐 오면(접속 catch-up + 실시간) 두 번째는 버린다 — 벨 회차(ringId)까지 같을 때 3초 창만(통화 중 초대는 회차가 다르다)
        if (!callController.shouldRing(payload.callId, payload.ringId)) return;
        if (incomingCallRef.current?.callId === payload.callId && !payload.invited) return;
        const guest = !!payload.guest;
        // ★ 수신 배너를 알림보다 **먼저** 띄운다. 알림 표시가 실패해도 전화는 받을 수 있어야 한다.
        //   예전에는 이 호출이 알림 코드 뒤에 있었고, Android Chrome이 new Notification()에서 던지는
        //   Illegal constructor 예외가 여기까지 도달하지 못하게 막아 배너·링톤·탭 점멸이 통째로
        //   사라졌다(안드로이드에서 전화벨이 안 울린 실제 원인). 순서를 되돌리지 말 것.
        setIncomingCall({
          channelId: payload.channelId,
          // 서버가 중계하는 channelName은 거는 사람 화면의 이름이다 — 자동 이름 그룹방은 사람마다 이름이
          // 달라(거는 사람 기준이면 내 이름이 방 이름으로 보인다) 내 목록의 이름을 먼저 쓴다.
          // 게스트는 그 방이 내 목록에 없고 서버가 '○○님의 통화'를 준다
          channelName: (!guest && channelsRef.current.find(c => String(c.id) === String(payload.channelId))?.name)
            || payload.channelName,
          callId: payload.callId,
          callerId: payload.callerId,
          callerName: payload.callerName,
          callerAvatar: payload.callerAvatar,
          channelType: payload.channelType,
          invited: !!payload.invited,
          guest,
        });
        // 푸시 알림 + 작업표시줄 점멸
        const notifTitle = payload.callerName;
        const notifBody = payload.invited ? '화상통화에 초대했습니다' : '화상통화 수신 중...';
        if (isTauri()) {
          // 게스트는 그 방을 열 수 없다 — 토스트를 누르면 창만 꺼낸다('call')
          import('@tauri-apps/api/core').then(({ invoke }) => {
            invoke('show_chat_notification', { title: notifTitle, body: notifBody, channelId: guest ? 'call' : String(payload.channelId) });
          }).catch(() => {});
          void flashTaskbar(false);
          setFlashBg(true);
          setTimeout(() => setFlashBg(false), 1200);
        } else {
          // tag는 서비스워커와 같은 `call-{callId}` — 웹푸시로 온 벨과 서로를 갈아끼워 하나만 남는다.
          // data.type/callId가 있어야 SW notificationclick의 통화 분기(isRingingCall)가 산다.
          void showWebNotification(notifTitle, {
            body: notifBody,
            tag: `call-${payload.callId}`,
            data: guest
              ? { type: 'call', callId: payload.callId, guest: true }
              : { type: 'call', callId: payload.callId, channelId: payload.channelId },
            requireInteraction: true,
            vibrate: [400, 200, 400, 200, 400],
          });
        }
        // 웹 탭 제목 점멸
        if (!isTauri()) {
          if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
          let show = true;
          blinkIntervalRef.current = setInterval(() => {
            document.title = show ? `[화상통화] ${notifTitle}` : 'INA-TALK 인아 채팅 프로그램';
            show = !show;
          }, 800);
        }
      },

      // 통화 이벤트는 callController가 처리한다(내 통화의 것인지 callId로 먼저 확인한다)
      onCallAccepted: (payload) => callController.onAccepted(payload),
      onCallRejected: (payload) => callController.onRejected(payload),
      onCallEnded: (payload) => {
        // 진행 중 통화와 수신 대기 벨을 각각 판정한다 (서로 다른 통화일 수 있다).
        callController.onEnded(payload);
        setIncomingCall(prev => (prev && prev.callId === payload.callId ? null : prev));
      },
      onCallReconnectRequest: (payload) => callController.onReconnectRequest(payload),
      // 통화 중 초대 벨('호출 중') — callController가 내 통화의 것인지 확인한다
      onCallRinging: (payload) => callController.onRinging(payload),
      onCallStatus: (payload) => {
        setChannelCalls(prev => {
          const key = String(payload.channelId);
          if (!payload.active) {
            if (!prev[key] || prev[key].callId !== payload.callId) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          }
          return { ...prev, [key]: payload };
        });
      },

      // 같은 계정의 다른 세션이 이 통화를 받거나 거절했다 → 이 기기의 벨을 끈다
      onCallHandled: (payload) => {
        if (payload.bySocketId === socketService.getSocketId()) return;
        setIncomingCall(prev => (prev && prev.callId === payload.callId ? null : prev));
        if (blinkIntervalRef.current) {
          clearInterval(blinkIntervalRef.current);
          blinkIntervalRef.current = null;
          document.title = 'INA-TALK 인아 채팅 프로그램';
        }
        // 통화 벨 정지 전용 — 안읽음 숫자 배지는 건드리지 않는다(통화는 안읽음과 무관하다)
        void flashTaskbar(true);
      },

      // 통화 실패 사유 표시 (기존에는 완전 무음)
      onCallError: (payload) => {
        const reasonText: Record<string, string> = {
          not_member: '이 채널의 멤버가 아니어서 통화할 수 없습니다.',
          no_session: '이미 종료된 통화입니다.',
        };
        showToast({ type: 'error', message: reasonText[payload.reason] || '통화를 연결할 수 없습니다.' });
      },

      // 채널 입장 실패 (권한 없음/삭제됨) — 기존에는 클릭해도 아무 반응이 없었다
      onChannelJoinError: (payload) => {
        const reasonText: Record<string, string> = {
          forbidden: '이 채널에 접근할 권한이 없습니다.',
          not_found: '채널을 찾을 수 없습니다.',
          unauthorized: '로그인이 필요합니다.',
        };
        showToast({ type: 'error', message: reasonText[payload.error] || '채널에 입장할 수 없습니다.' });
      },

      // 메시지 전송/삭제/수정 실패 (공지 채널 쓰기 거부 포함) — 기존에는 완전 무음
      onMessageError: (payload) => {
        showToast({ type: 'error', message: payload.error || '메시지를 보내지 못했습니다.' });
      },

      // 로그인 승인 해제 — 이 계정의 모든 세션을 즉시 로그아웃시킨다.
      // 저장된 비밀번호는 지우지 않는다(401 자동 로그아웃과 같은 철학) — 다시 승인받으면
      // 그대로 로그인할 수 있어야 하고, 자격증명이 틀린 것이 아니기 때문이다.
      onAuthRevoked: (payload) => {
        if (!localStorage.getItem('authToken')) return; // 이미 로그아웃 상태면 중복 토스트 방지
        // 기기 신뢰 enforce 전환 직후 기기가 안 묶인 소켓 — 승인 웹 기기면 401 경로가 키 재개로 이어 간다
        if (payload?.code === 'DEVICE_REQUIRED') {
          dispatchUnauthorized('DEVICE_REQUIRED');
          return;
        }
        handleLogout();
        showToast({
          type: 'error',
          message: payload?.reason || '접근 권한이 해제되었습니다. 관리자에게 문의해 주세요.',
          duration: 10000,
        });
      },

      // 권한 변경. JWT에 role이 담겨 있어 재로그인해야 실제로 적용된다.
      onUserRoleChanged: () => {
        showToast({ type: 'info', message: '권한이 변경되었습니다. 다시 로그인하면 적용됩니다.', duration: 8000 });
      },
      onUserAdminChanged: () => {
        showToast({ type: 'info', message: '관리자 권한이 변경되었습니다. 다시 로그인하면 적용됩니다.', duration: 8000 });
      },

      // 첨부 용량 정책 변경. 사용자가 요청한 변경이 아니고 전 사용자에게 동시에 뜨므로 토스트는 띄우지
      // 않는다 — 다음에 파일을 고를 때 새 한도가 조용히 적용되면 된다.
      onUploadPolicyChanged: (payload) => {
        setUploadPolicy(payload);
      },

      // 차단 확장자 변경. 용량 정책과 같은 이유로 토스트는 띄우지 않는다 — 다음에 파일을 고를 때
      // 새 목록이 조용히 적용된다. payload는 관리 목록만 담고 절대 차단 목록은 그대로 유지된다.
      onBlockedExtensionsChanged: (payload) => {
        setBlockedExtensions(payload);
      },

      onCallParticipantLeft: (payload) => callController.onParticipantLeft(payload),

      // 시그널은 callId 대조 후 피어에 넘긴다(옛 피어 인스턴스의 늦은 시그널은 callService가 거른다)
      onRtcSignal: (payload) => callController.onSignal(payload),
    });

    // Web Push 알림 클릭 시 채널 이동 (Service Worker → postMessage)
    const handleSWMessage = (event: MessageEvent) => {
      // 통화 알림 클릭. 수신 배너 자체는 서버의 접속 시 catch-up(user:online → call:incoming)이
      // 만든다 — 알림이 도착한 시점엔 이 세션에 소켓이 없어서 원래 call:incoming을 놓쳤기 때문이다.
      // 여기서는 채팅방만 열어 두고, 통화가 아직 살아 있으면 곧 벨이 울린다.
      // 게스트 통화(초대받은 남의 방)는 channelId가 없다 — 방을 열지 않고 앱만 띄운다(벨은 catch-up이 울린다)
      if (event.data?.type === 'CALL_NOTIFICATION_CLICK') {
        event.ports[0]?.postMessage({ ok: true });
        if (event.data.channelId && !event.data.guest) openChannelFromNotification(Number(event.data.channelId));
        return;
      }
      // 시스템 알림(전자결재 등) 클릭 — 채널이 없으므로 알림함을 그 출처 탭으로 연다
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.activityTab) {
        event.ports[0]?.postMessage({ ok: true });
        openActivityTab(String(event.data.activityTab));
        return;
      }
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.channelId) {
        // ack: SW는 일정 시간 내 응답이 없으면 앱이 안 떠 있다고 보고 client.navigate 폴백을 탄다
        event.ports[0]?.postMessage({ ok: true });
        openChannelFromNotification(Number(event.data.channelId));
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    // Tauri 알림 클릭 시 채널 이동 리스너
    let unlistenNotification: (() => void) | null = null;
    if (isTauri()) {
      import('@tauri-apps/api/event').then(({ listen }) => {
        listen<string>('notification-clicked', (event) => {
          // 시스템 알림 토스트는 channelId 자리에 'activity:<탭>'을 싣는다(notifySystemActivityRef)
          const payload = String(event.payload || '');
          // 원격지원 요청 토스트 — 수락 카드는 앱 안에 떠 있다. 창은 Rust(show_main_window)가 이미 꺼냈다
          if (payload === 'remote-support') return;
          // 게스트 통화 초대 토스트 — 그 방은 내 목록에 없다. 수신 배너가 앱 안에 떠 있다
          if (payload === 'call') return;
          if (payload.startsWith('activity:')) {
            // 'activity:<탭>' 또는 'activity:<탭>:<알림 id>'
            const [tab, rawId] = payload.slice('activity:'.length).split(':');
            const focusId = Number(rawId);
            openActivityTab(tab || 'all', Number.isFinite(focusId) && focusId > 0 ? focusId : null);
            return;
          }
          openChannelFromNotification(Number(event.payload));
        }).then(unlisten => {
          unlistenNotification = unlisten;
        });
      }).catch(() => {});
    }

    return () => {
      socketService.disconnect();
      unlistenNotification?.();
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
    // refreshChannels·isChannelVisibleToCurrentUser·openChannelFromNotification 모두
    // currentUser에서만 파생되고 showToast는 컨텍스트에서 안정적으로 제공되며
    // dropMessageCache는 useCallback([])이라 영구 안정적이므로,
    // 이 effect의 재실행 빈도는 기존과 같다 (소켓이 불필요하게 재연결되지 않는다).
  }, [currentUser, refreshChannels, isChannelVisibleToCurrentUser, showToast, openMobileChat, openChannelFromNotification, dropMessageCache, handleLogout, applyMyNotifyPrivacyLocally, openActivityTab]);

  // 채널 변경 시 메시지 로딩 및 채널 입장
  useEffect(() => {
    if (!currentUser || !activeChannel) return;
    const channelId = activeChannel.id;

    // 초안 DM(id 0)은 서버에 존재하지 않는다 — join·히스토리 조회·읽음 처리를 모두 건너뛰고
    // 빈 화면 상태만 정확히 맞춘다. hasMore를 내려야 ChatWindow의 "첫 대화입니다" 안내가 뜬다
    // (캐시 미스 분기는 hasMore를 리셋하지 않아 직전 방 값이 그대로 남는다).
    if (!channelId) {
      isViewingHistoryRef.current = false;
      setMessages([]);
      setHasMore(false);
      setHasMoreNewer(false);
      setJumpTarget(null);
      return; // cleanup 없음 → leaveChannel(0)도 보내지 않는다
    }
    // 다른 채널로 옮겨가면 승격 병합 플래그는 버린다 (병합은 승격 직후 1회만)
    if (justCreatedChannelRef.current !== null && justCreatedChannelRef.current !== channelId) {
      justCreatedChannelRef.current = null;
    }

    // 이전 채널 퇴장, 새 채널 입장
    socketService.joinChannel(channelId);
    // 포커스 상태 + 채팅방이 보이는 중이면 서버에 알림 (읽음 처리용)
    if (isWindowFocusedRef.current && isChatVisibleRef.current) {
      socketService.emitChannelFocus(channelId);
    }

    // 채널이 바뀌면 날짜 점프 상태는 항상 해제하고 최신 창으로 시작한다
    isViewingHistoryRef.current = false;
    setHasMoreNewer(false);
    setJumpTarget(null);

    // 이전에 본 적 있는 채널이면 캐시된 창을 먼저 그린다 — 재진입 시 빈 화면 대기가 사라진다.
    // fetch 자체를 건너뛰지는 않는다(stale-while-revalidate): 비활성 채널의 messages는
    // 소켓으로 갱신되지 않아 캐시에는 그 사이 도착한 메시지와 최신 unreadCount가 빠져 있다.
    // 승격 직후(justCreatedChannelRef)에는 캐시된 창을 쓰지 않는다 — 새로 만든 방이거나
    // 나갔다 재입장한 방이라, 옛 스냅샷은 서버 가시성(joined_at 이후만 조회)과 어긋난다.
    const cachedWindow = justCreatedChannelRef.current === channelId
      ? undefined
      : messagesCacheRef.current.get(channelId);
    if (cachedWindow && cachedWindow.length > 0) {
      setMessages(cachedWindow);
      setHasMore(cachedWindow.length >= 100);
    } else {
      // 캐시 미스 — 비우지 않으면 서버 응답이 올 때까지 직전 채널의 대화가 새 방 헤더 밑에 남는다
      setMessages([]);
    }

    // 채널을 빠르게 훑을 때 이전 채널의 조회를 실제로 끊는다.
    // (지금까지는 응답을 끝까지 받은 뒤 activeChannelIdRef 비교로 버리기만 했다)
    const controller = new AbortController();

    // DB에서 대화 기록 불러오기 (최신 100개)
    const loadHistory = async () => {
      historyLoadingRef.current = channelId; // catch-up이 같은 창을 이중 조회하지 않도록
      try {
        const history = await dbService.getMessages(channelId, 100, undefined, undefined, controller.signal);
        // 응답을 기다리는 사이 채널이 바뀌었으면 폐기 (늦게 온 응답이 새 채널 화면을 덮는 것 방지)
        if (activeChannelIdRef.current !== channelId) return;
        // 초안 승격 직후 1회는 교체가 아니라 병합한다 — 이 조회가 INSERT 커밋 전 스냅샷이고
        // 소켓 에코가 먼저 붙었다면, 통째로 교체하면 방금 보낸 첫 메시지가 화면에서 사라진다.
        // (플래그 해제를 updater 밖에서 해야 StrictMode 이중 호출에도 결과가 같다)
        const shouldMerge = justCreatedChannelRef.current === channelId;
        if (shouldMerge) justCreatedChannelRef.current = null;
        setMessages(prev => {
          if (!shouldMerge) return history;
          const seen = new Set(history.map((m: any) => String(m.id)));
          // 살리는 것은 '서버 창보다 새로운 것'뿐이다. prev 전체를 살리면 나갔다 재입장한
          // DM에서 서버가 joined_at으로 숨긴 과거 대화(캐시 잔재)까지 되살아난다.
          const maxHistoryId = history.reduce((mx: number, m: any) => Math.max(mx, Number(m.id) || 0), 0);
          const extras = prev.filter(m =>
            Number(m.channelId) === channelId
            && !seen.has(String(m.id))
            && Number(m.id) > maxHistoryId
          );
          return extras.length ? [...history, ...extras] : history;
        });
        setHasMore(history.length === 100);
        cacheMessageWindow(channelId, history);
        // 알림함 점프 보류분 소비 — 로드된 최신 창에 있으면 스크롤만, 없으면 서버 점프 (Phase 2)
        const pendingJump = pendingActivityJumpRef.current;
        if (pendingJump && pendingJump.channelId === Number(channelId)) {
          pendingActivityJumpRef.current = null;
          const loadedMsg = history.find((m: any) => String(m.id) === String(pendingJump.messageId));
          if (loadedMsg) {
            jumpNonceRef.current += 1;
            setJumpTarget({ id: loadedMsg.id, nonce: jumpNonceRef.current });
          } else {
            jumpToMessage(pendingJump.messageId);
          }
        }
        // 메시지 로딩 후 읽음 처리 (메시지 ID 목록 전달)
        // 모바일 부팅 시 localStorage 복원·자동 첫 채널 선택으로 이 effect가 홈 화면에서도 돌므로
        // 실행 시점에 채팅방이 실제로 보이는지 재확인한다
        const msgIds = history.map((m: any) => m.id);
        setTimeout(() => {
          if (isChatVisibleRef.current) {
            socketService.markChannelRead(channelId, msgIds);
          }
        }, 100);
      } catch (error) {
        // 채널 전환으로 취소된 요청은 정상 흐름이다
        if ((error as { name?: string })?.name === 'AbortError') return;
        console.error('Failed to load history:', error);
      } finally {
        if (historyLoadingRef.current === channelId) historyLoadingRef.current = null;
      }
    };

    loadHistory();

    return () => {
      controller.abort();
      socketService.leaveChannel(channelId);
    };
  }, [activeChannel?.id, currentUser]);

  // useCallback 내부에서 최신 messages를 stale closure 없이 읽기 위한 ref
  const messagesRef = useRef<Message[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // 이전 메시지 로드 (페이지네이션)
  const loadOlderMessages = useCallback(async () => {
    const channelId = activeChannelIdRef.current;
    const current = messagesRef.current;
    if (!channelId || current.length === 0) return;
    const oldestId = current[0].id;
    try {
      const older = await dbService.getMessages(channelId, 100, oldestId);
      if (activeChannelIdRef.current !== channelId) return;
      setMessages(prev => [...older, ...prev]);
      setHasMore(older.length === 100);
    } catch (error) {
      console.error('Failed to load older messages:', error);
    }
  }, []);

  // 다음 메시지 로드 (최신 방향) — 날짜 점프로 과거를 보고 있을 때만 노출된다
  const loadNewerMessages = useCallback(async () => {
    const channelId = activeChannelIdRef.current;
    const current = messagesRef.current;
    if (!channelId || current.length === 0) return;
    const newestId = current[current.length - 1].id;
    try {
      const newer = await dbService.getMessages(channelId, 100, undefined, newestId);
      if (activeChannelIdRef.current !== channelId) return;
      const reachedLatest = newer.length < 100;
      setMessages(prev => [...prev, ...newer]);
      setHasMoreNewer(!reachedLatest);
      // 최신까지 따라잡았으면 정상 모드로 복귀 (소켓 새 메시지 append 재개)
      if (reachedLatest) isViewingHistoryRef.current = false;
    } catch (error) {
      console.error('Failed to load newer messages:', error);
    }
  }, []);

  /**
   * 놓친 메시지 catch-up — 활성 채널의 마지막 id 이후분을 REST(`after` 커서)로 받아 붙인다.
   *
   * 왜 필요한가: `messages`의 갱신 경로는 소켓 `message:new` append와 채널 변경 effect의 fetch 둘뿐이다.
   * 모바일 PWA가 백그라운드/절전으로 멈추면 서버가 pingTimeout으로 소켓을 끊고 그 사이 room 브로드캐스트는
   * 유실되는데, 채널 목록(lastMessage)은 `channel:list-sync`로 복구되는 반면 `messages`는 같은 채널을
   * 다시 열어도(activeChannel 유지 → 채널 변경 effect 미실행) 아무도 다시 받아오지 않았다.
   * → "목록엔 새 메시지가 보이는데 방에 들어가면 없다"의 원인. 재연결·visible 복귀·채팅방 재진입 때 호출.
   *
   * - 과거 열람 중이면 아무것도 안 한다 (returnToLatest가 최신 창 복원을 담당)
   * - 채널별 in-flight 가드: 세 트리거가 겹쳐도 요청은 1회
   * - 100건 이상 밀렸으면 커서 append 대신 최신 창 전체를 갈아끼운다
   * - 소켓 이벤트와 REST 응답이 겹칠 수 있으므로 id(String 정규화 — PG BIGINT는 문자열)로 중복 제거
   */
  const catchUpInFlightRef = useRef<Set<number>>(new Set());
  const catchUpLatestMessages = useCallback(async (channelId: number) => {
    if (!channelId || isViewingHistoryRef.current) return;
    const inFlight = catchUpInFlightRef.current;
    if (inFlight.has(channelId)) return;
    inFlight.add(channelId);
    try {
      // 같은 commit에서 채널 변경 effect가 뒤이어 도는 경우(모바일: 목록에서 다른 방 탭 → activeChannel과
      // isMobileChatOpen이 함께 바뀜) 그쪽 loadHistory 등록을 기다린 뒤 판단해야 이중 조회를 피한다.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (activeChannelIdRef.current !== channelId || isViewingHistoryRef.current) return;
      if (historyLoadingRef.current === channelId) return; // 최신 창 전체 조회가 이미 진행 중

      const loadFullWindow = async () => {
        const history = await dbService.getMessages(channelId, 100);
        if (activeChannelIdRef.current !== channelId || isViewingHistoryRef.current) return;
        setMessages(history);
        setHasMore(history.length === 100);
        cacheMessageWindow(channelId, history);
        if (isChatVisibleRef.current && isWindowFocusedRef.current) {
          socketService.markChannelRead(channelId, history.map(m => m.id));
        }
      };

      const current = messagesRef.current;
      const last = current.length > 0 ? current[current.length - 1] : null;
      if (!last || String(last.channelId) !== String(channelId)) {
        // 이 채널의 창이 아예 없다 (부팅 시 오프라인이라 첫 조회가 실패했던 경우 등)
        await loadFullWindow();
        return;
      }

      const newer = await dbService.getMessages(channelId, 100, undefined, last.id);
      if (activeChannelIdRef.current !== channelId || isViewingHistoryRef.current) return;
      if (newer.length === 0) return;
      if (newer.length >= 100) {
        await loadFullWindow();
        return;
      }
      const known = new Set(messagesRef.current.map(m => String(m.id)));
      const added = newer.filter(m => !known.has(String(m.id)));
      if (added.length === 0) return;
      setMessages(prev => {
        const ids = new Set(prev.map(m => String(m.id)));
        const fresh = added.filter(m => !ids.has(String(m.id)));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      // 캐시도 최신 창으로 맞춘다 (재진입 시 옛 창이 먼저 그려지지 않도록)
      cacheMessageWindow(channelId, [...messagesRef.current, ...added].slice(-100));
      if (isChatVisibleRef.current && isWindowFocusedRef.current) {
        socketService.markChannelRead(channelId, added.map(m => m.id));
      }
    } catch (error) {
      console.error('Failed to catch up messages:', error);
    } finally {
      inFlight.delete(channelId);
    }
  }, [cacheMessageWindow]);
  useEffect(() => {
    catchUpLatestMessagesRef.current = catchUpLatestMessages;
  }, [catchUpLatestMessages]);

  /**
   * 날짜 점프 — 선택한 날짜(로컬 자정) 이후 첫 메시지 위치로 이동한다.
   * 과거 열람 모드로 전환되며, 여기서는 읽음 처리를 하지 않는다
   * (과거를 들춰봤다는 이유로 안 읽은 최신 메시지가 읽음으로 사라지면 안 되기 때문).
   */
  const jumpToDate = useCallback(async (dateStr: string) => {
    const channelId = activeChannelIdRef.current;
    if (!channelId) return;
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return;
    const fromIso = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
    try {
      const result = await dbService.jumpToDate(channelId, fromIso);
      if (activeChannelIdRef.current !== channelId) return;
      if (result.anchorId === null || result.messages.length === 0) {
        showToast({ type: 'info', message: '선택한 날짜 이후의 대화가 없습니다.' });
        return;
      }
      // 최신까지 다 담겼다면(hasMoreNewer=false) 과거 열람 모드로 볼 이유가 없다
      isViewingHistoryRef.current = result.hasMoreNewer;
      setMessages(result.messages);
      setHasMore(result.hasMoreOlder);
      setHasMoreNewer(result.hasMoreNewer);
      jumpNonceRef.current += 1;
      setJumpTarget({ id: result.anchorId, nonce: jumpNonceRef.current });
    } catch (error) {
      console.error('Failed to jump to date:', error);
      showToast({ type: 'error', message: '해당 날짜의 대화를 불러오지 못했습니다.' });
    }
  }, [showToast]);

  /**
   * 메시지 점프 — 특정 메시지 위치로 이동한다 (공지 배너·서랍 원문 보기).
   * 이미 로드된 메시지면 스크롤만, 아니면 서버에서 주변 창을 받아 날짜 점프와 동일하게 과거 열람 모드로 전환.
   */
  const jumpToMessage = useCallback(async (messageId: number) => {
    const channelId = activeChannelIdRef.current;
    if (!channelId) return;
    // 인메모리 우선: 화면에 이미 있으면 히스토리 모드 진입 없이 스크롤만.
    // BIGINT id는 런타임에 문자열일 수 있어 String 비교 + messageRefs 키와 일치하도록 실제 m.id 사용
    const loaded = messagesRef.current.find(m => String(m.id) === String(messageId));
    if (loaded) {
      jumpNonceRef.current += 1;
      setJumpTarget({ id: loaded.id, nonce: jumpNonceRef.current });
      return;
    }
    try {
      const result = await dbService.jumpToMessage(channelId, messageId);
      if (activeChannelIdRef.current !== channelId) return;
      if (result.anchorId === null || result.messages.length === 0) {
        showToast({ type: 'info', message: '메시지를 찾을 수 없습니다.' });
        return;
      }
      isViewingHistoryRef.current = result.hasMoreNewer;
      setMessages(result.messages);
      setHasMore(result.hasMoreOlder);
      setHasMoreNewer(result.hasMoreNewer);
      jumpNonceRef.current += 1;
      setJumpTarget({ id: result.anchorId, nonce: jumpNonceRef.current });
    } catch (error) {
      console.error('Failed to jump to message:', error);
      showToast({ type: 'error', message: '메시지를 불러오지 못했습니다.' });
    }
  }, [showToast]);

  // ─── 메시지 전달 (Phase 2 Forward) ───
  // 전달 원본 메시지들 — null이면 모달 닫힘. 화면 반영은 서버 전파(message:new/notify)가 담당 (보강 emit 금지)
  const [forwardSource, setForwardSource] = useState<Message[] | null>(null);
  // 단건 모달 → "여러 개 선택" 전환 요청 (nonce 변경 시 ChatWindow가 선택 모드 진입 + preselect)
  const [forwardSelectRequest, setForwardSelectRequest] = useState<{ ids: number[]; nonce: number } | null>(null);
  const handleOpenForward = useCallback((msgs: Message[]) => {
    setForwardSource(msgs);
  }, []);
  const handleForwardSelectMore = useCallback(() => {
    setForwardSource(source => {
      if (source) {
        setForwardSelectRequest(prev => ({
          ids: source.map(m => Number(m.id)),
          nonce: (prev?.nonce || 0) + 1,
        }));
      }
      return null;
    });
  }, []);
  const handleForwardConfirm = useCallback(async (targetChannelIds: number[]) => {
    const source = forwardSource;
    setForwardSource(null);
    if (!source || source.length === 0 || targetChannelIds.length === 0) return;
    try {
      const { results } = await dbService.forwardMessages(
        source.map(m => Number(m.id)).sort((a, b) => a - b),
        targetChannelIds
      );
      const okCount = results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok);
      if (failed.length === 0) {
        showToast({ type: 'success', message: `${okCount}개 채널로 전달했습니다.` });
      } else if (okCount > 0) {
        showToast({ type: 'info', message: `${okCount}개 채널로 전달, ${failed.length}개 실패 (${failed[0].error || '권한 없음'})` });
      } else {
        showToast({ type: 'error', message: `전달 실패: ${failed[0].error || '권한이 없습니다.'}` });
      }
    } catch (error) {
      console.error('Failed to forward messages:', error);
      const message = error instanceof Error && error.message ? error.message : '메시지 전달에 실패했습니다.';
      showToast({ type: 'error', message });
    }
  }, [forwardSource, showToast]);

  /**
   * 알림함 항목 클릭 → 채널 이동 + 원문 점프 (Phase 2)
   * 활성 채널이면 즉시 점프, 아니면 보류분을 남기고 openChannelFromNotification 경로로
   * 채널을 연다 (openMobileChat 포함 — CLAUDE.md 규칙). 보류분은 loadHistory 완료 시 소비.
   */
  const handleOpenActivityTarget = useCallback((channelId: number, messageId: number) => {
    if (activeChannelIdRef.current === Number(channelId)) {
      // 선택된 방이라도 화면에 안 보일 수 있다(알림 탭 진입·X 닫기로 접힘, 모바일 알림함 오버레이) —
      // 점프만 하면 보이지 않는 창 안에서 스크롤하고 끝났다. 열기는 멱등이고 알림 탭은 그대로 둔다.
      // 다시 보이면 가시성 effect가 catch-up·읽음을 하고, ChatWindow는 마운트 시 jumpTarget을 소비한다
      openMobileChat();
      jumpToMessage(messageId);
      return;
    }
    pendingActivityJumpRef.current = { channelId: Number(channelId), messageId };
    openChannelFromNotification(Number(channelId));
  }, [jumpToMessage, openChannelFromNotification, openMobileChat]);

  // 채널 공지 등록(messageId)/해제(null) — 상태 반영은 서버의 channel:notice-changed 전파가 담당
  const handleSetNotice = useCallback(async (messageId: number | null) => {
    const channelId = activeChannelIdRef.current;
    if (!channelId) return;
    try {
      await channelService.setChannelNotice(channelId, messageId);
      showToast({ type: 'success', message: messageId != null ? '공지로 등록했습니다.' : '공지를 내렸습니다.' });
    } catch (error) {
      console.error('Failed to set notice:', error);
      const message = error instanceof Error && error.message ? error.message : '공지 설정에 실패했습니다.';
      showToast({ type: 'error', message });
    }
  }, [showToast]);

  // 최신 대화로 복귀 (날짜 점프 해제) — 이때 비로소 읽음 처리를 수행한다
  const returnToLatest = useCallback(async () => {
    const channelId = activeChannelIdRef.current;
    if (!channelId) return;
    try {
      const history = await dbService.getMessages(channelId, 100);
      if (activeChannelIdRef.current !== channelId) return;
      isViewingHistoryRef.current = false;
      setMessages(history);
      setHasMore(history.length === 100);
      cacheMessageWindow(channelId, history); // 최신 창으로 복귀했으므로 캐시 갱신
      setHasMoreNewer(false);
      setJumpTarget(null);
      socketService.markChannelRead(channelId, history.map(m => m.id));
      setUnreadCounts(prev => ({ ...prev, [channelId]: 0 }));
    } catch (error) {
      console.error('Failed to return to latest:', error);
    }
  }, [cacheMessageWindow]);

  // 반환값 false = 전송 실패 → ChatWindow가 입력창·첨부를 비우지 않고 재시도할 수 있게 둔다
  const handleSendMessage = useCallback(async (text: string, attachments?: Attachment[], replyToId?: number | null, mentions?: MentionInput[]): Promise<boolean> => {
    if (!text.trim() || !currentUser) return false;

    let channelId = activeChannelIdRef.current;
    if (!channelId) {
      // 초안 DM의 첫 메시지 — 지금 실제 방을 만든다 (토스트는 승격 함수가 띄운다)
      if (!draftPartnerRef.current) return false;
      const real = await materializeDraftDm();
      if (!real) return false;
      channelId = real.id;
    }

    // 과거를 열람하던 중 전송하면 최신 창으로 먼저 복귀한다.
    // (복귀 전에 보내면 onMessageNew가 과거 열람 가드에 걸려 내가 보낸 메시지가 화면에 안 나타난다)
    if (isViewingHistoryRef.current) {
      await returnToLatest();
    }

    // 첨부파일이 있으면 msgType을 'file'로 설정
    const msgType = attachments && attachments.length > 0 ? 'file' : 'text';

    // 소켓으로 메시지 전송 (서버에서 DB 저장 및 브로드캐스트)
    socketService.sendMessage(channelId, text, msgType, replyToId ?? undefined, attachments, mentions);
    return true;
  }, [currentUser, returnToLatest, materializeDraftDm]);

  // ─── 예약 메시지 핸들러 (ChatWindow에 내려줌) ───
  // REST 응답으로도 즉시 패치하지만 소켓 scheduled-message:* 이벤트와 id 기준 멱등이라 겹쳐도 무해하다.
  // body.attachments는 예약 창이 방금 올린 파일 — 첨부만 있으면 본문은 ATTACHMENT_ONLY_TEXT(일반 전송과 같다)
  const handleScheduleMessage = useCallback(async (body: ScheduledMessageBody): Promise<boolean> => {
    if (!body.content.trim() || !currentUser) return false;
    let channelId = activeChannelIdRef.current;
    if (!channelId) {
      // 초안 DM에서의 예약 — 첫 메시지와 동일하게 지금 실제 방을 만든다
      if (!draftPartnerRef.current) return false;
      const real = await materializeDraftDm();
      if (!real) return false;
      channelId = real.id;
    }
    try {
      const created = await scheduledMessageService.create(channelId, body);
      upsertScheduled(created);
      showToast({ type: 'success', message: '예약했습니다.' });
      return true;
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : '메시지를 예약하지 못했습니다. 잠시 후 다시 시도해 주세요.';
      showToast({ type: 'error', message });
      return false;
    }
  }, [currentUser, materializeDraftDm, upsertScheduled, showToast]);

  const handleUpdateScheduledMessage = useCallback(async (
    id: string,
    body: ScheduledMessageBody
  ): Promise<boolean> => {
    try {
      const updated = await scheduledMessageService.update(id, body);
      upsertScheduled(updated);
      showToast({ type: 'success', message: '저장했습니다.' });
      return true;
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : '예약을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.';
      showToast({ type: 'error', message });
      return false;
    }
  }, [upsertScheduled, showToast]);

  const handleCancelScheduledMessage = useCallback(async (item: ScheduledMessage): Promise<void> => {
    try {
      await scheduledMessageService.cancel(item.id);
      removeScheduled(item.id);
      showToast({ type: 'success', message: item.failedAt ? '예약을 삭제했습니다.' : '예약을 취소했습니다.' });
    } catch (error) {
      // 이미 전송(409) 등 — 목록 상태는 소켓 이벤트·재조회가 맞춘다
      const message = error instanceof Error && error.message
        ? error.message
        : '예약을 취소하지 못했습니다. 잠시 후 다시 시도해 주세요.';
      showToast({ type: 'error', message });
    }
  }, [removeScheduled, showToast]);

  const handleReactionToggle = useCallback((messageId: number, emoji: string) => {
    const channelId = activeChannelIdRef.current;
    if (!currentUser || !channelId) return;
    socketService.toggleReaction(messageId, channelId, emoji);
  }, [currentUser]);

  // 메시지 삭제 낙관적 반영 (ChatWindow에서 서버 삭제 성공 후 호출)
  const handleDeleteMessage = useCallback((messageId: number) => {
    setMessages(prev => prev.map(m =>
      String(m.id) === String(messageId) ? { ...m, deletedAt: new Date().toISOString() } : m
    ));
  }, []);

  // 로그인하지 않은 경우 로그인 화면 표시
  // (웹 기기 키 재개를 시도하는 동안은 로그인 폼 대신 잠깐 대기 화면 — 폼이 깜빡였다 사라지지 않게)
  if (!currentUser && isResumingSession) {
    return (
      <div className="flex flex-col h-full bg-slate-900">
        <div className="flex-1 flex items-center justify-center" role="status" aria-live="polite">
          <img src="/icon.png" alt="INA-TALK" className="h-14 w-14 rounded-2xl shadow-lg animate-pulse" />
          <span className="sr-only">로그인 중</span>
        </div>
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="flex flex-col h-full bg-slate-900">
        <TitleBar />
        <UpdateNotification />
        <KeyboardViewportDebug />
        <Login onLoginSuccess={handleLoginSuccess} onHelpClick={openHelp} />
        {isHelpOpen && <HelpPage onClose={closeHelp} />}
      </div>
    );
  }

  // 컴팩트 배치(좁은 PC앱)에서 목록 대신 <main>(채팅·봇 대화)을 보이는가. <main>의 렌더 분기와 같은 조건이어야
  // 한다 — activeChannel 없이 isDesktopChatOpen만 보면 "대화방을 선택하면…" 빈 화면이 목록을 가린다.
  // ChatWindow는 activeChannel && isDesktopChatOpen일 때만 마운트되므로 CSS로 숨긴 채 마운트되는 일이 없다.
  const compactShowsMain = !!systemFeed || (isDesktopChatOpen && !!activeChannel);
  // 넓은 홈 — 데스크톱 홈 탭은 rail + 홈 한 칸(2컬럼). 목록 패널이 남은 폭을 다 쓰고 <main>은 숨는다.
  // 홈 탭이면 채팅은 언제나 닫혀 있지만(handleDesktopTabChange·초기값) 혹시 열려 있으면 예전 3컬럼으로 떨어진다 —
  // 채팅이 '보임'으로 판정되는데 화면에 없는 상태(안 보이는 읽음 처리)를 만들지 않기 위한 안전장치.
  // 컴팩트(좁은 PC앱)·모바일 웹은 한 줄 배치(stack) 그대로다.
  const homeWide = !isMobile && !isCompactDesktop && desktopTab === 'home' && !isDesktopChatOpen && !systemFeed;
  // 넓은 캘린더 — 홈과 같은 2컬럼(rail + 캘린더 한 칸). 캘린더 탭도 채팅을 접는 화면이다(handleDesktopTabChange·초기값).
  // ⚠ 목록 패널 div는 그대로 두고 폭 조건만 listWide로 일반화한다 — div를 바꾸면 탭 안 화면이 리마운트된다
  const calendarWide = !isMobile && !isCompactDesktop && desktopTab === 'calendar' && !isDesktopChatOpen && !systemFeed;
  const listWide = homeWide || calendarWide;

  return (
    <div className={`flex flex-col h-full w-full bg-white overflow-hidden${flashBg ? ' flash-bg' : ''}`}>
      <TitleBar />
      <ConnectionBanner isConnected={isConnected} />
      <UpdateNotification />
      <KeyboardViewportDebug />
      {/* 두 배너는 항상 마운트하고 표시만 게이트가 고른다 —
          조건부 마운트로 바꾸면 PwaInstallBanner가 beforeinstallprompt를 놓친다. */}
      <PwaInstallBanner suppressed={notificationGate !== 'install'} />
      <NotificationPermissionBanner onGranted={subscribeToPush} suppressed={notificationGate !== 'permission'} />
      {/* 서비스워커 업데이트 알림 (웹 전용) */}
      {swUpdateAvailable && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 bg-brand-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-3 text-sm">
          <span>새 버전이 있습니다.</span>
          <button
            onClick={() => { void refreshApp(); }}
            className="bg-white text-brand-600 px-3 py-1 rounded font-medium hover:bg-brand-50 transition-colors"
          >
            새로고침
          </button>
          <button
            onClick={() => setSwUpdateAvailable(false)}
            aria-label="닫기"
            className="text-white/70 hover:text-white ml-1 p-1"
          >
            ✕
          </button>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden relative">
      {/* 모바일 '대화방' 탭: 카카오톡식 목록 전체폭 페이지 (데스크톱은 아래 목록 패널에서 별도 마운트) */}
      {isMobile && (
        <div className={`md:hidden ${
          mobileTab === 'chat' && !isMobileChatOpen ? 'block w-full' : 'hidden'
        }`}>
          <ChatListScreen
            channels={channels}
            unreadCounts={unreadCounts}
            currentUser={currentUser}
            onlineUsers={onlineUsers}
            onChannelSelect={handleChannelSelect}
            onTogglePin={handleTogglePin}
            onToggleMute={handleToggleMute}
            onRenameChannel={handleRenameChannel}
            onLeaveChannel={handleLeaveChannel}
            onCreateGroup={openCreateGroupModal}
            onSelfChatClick={handleSelfChatClick}
            channelsSynced={channelsSynced}
            isVisible={mobileTab === 'chat' && !isMobileChatOpen}
            filterRequest={mobileChatFilterRequest}
          />
        </div>
      )}
      {/* 데스크톱 3컬럼 ①: 세로 rail (홈/대화방/알림/조직도 + 관리자/도움말/프로필) */}
      <DesktopNavRail
        activeTab={desktopTab}
        onTabChange={handleDesktopTabChange}
        badges={{ chat: totalUnread, activity: activityUnread }}
        currentUser={currentUser}
        isConnected={isConnected}
        myStatus={myStatus}
        myPresenceManual={myPresenceManual}
        onPresenceSelect={handlePresenceSelect}
        onAdminConsoleClick={openAdminConsole}
        onHelpClick={openHelp}
        onSettingsClick={openAvatarSettings}
        onRulesClick={openDisclaimerReview}
        onLogout={requestLogout}
        alwaysVisible={isCompactDesktop}
      />
      {/* 데스크톱 3컬럼 ②: 목록 패널 — rail 탭에 따라 내용만 스왑, 우측 ChatWindow는 유지.
          컴팩트 배치에서는 채팅이 열려 있지 않을 때만 남은 폭 전체를 쓴다.
          넓은 홈(homeWide)이면 같은 div가 남은 폭 전체를 쓴다 — div를 바꾸지 않아야 HomeScreen이 리마운트되지 않는다 */}
      <div className={`${
        isCompactDesktop
          ? (compactShowsMain ? 'hidden' : 'flex flex-col flex-1 min-w-0')
          : listWide
            ? 'hidden md:flex md:flex-col flex-1 min-w-0'
            : 'hidden md:flex md:flex-col shrink-0 md:w-72 lg:w-80 xl:w-96 border-r border-slate-200'
      } bg-slate-50 overflow-hidden`}>
        {desktopTab === 'home' && (
          <div className="flex-1 overflow-y-auto bg-slate-50">
            <HomeScreen
              currentUser={currentUser}
              channels={channels}
              unreadCounts={unreadCounts}
              onlineUsers={onlineUsers}
              onChannelSelect={handleChannelSelect}
              /* onSettingsClick 미전달 — 데스크톱은 rail 아바타 팝오버에 프로필 설정이 있어 홈 아바타는 장식만 */
              onUserClick={openUserProfile}
              onGoToChats={goToDesktopChats}
              channelsSynced={channelsSynced}
              onTogglePin={handleTogglePin}
              onToggleMute={handleToggleMute}
              onRenameChannel={handleRenameChannel}
              onLeaveChannel={handleLeaveChannel}
              onActivityClick={openDesktopActivity}
              activityUnread={activityUnread}
              layout={homeWide ? 'wide' : 'stack'}
              /* 넓은 홈 머리의 '대화방 개설'. 한 줄 배치에 넘기면 컴팩트 PC앱에 모바일용 FAB가 뜬다 */
              onCreateChat={homeWide ? openCreateGroupModal : undefined}
              onShowUnread={showUnreadChats}
              onOpenOrg={openDesktopOrg}
              onOpenHelp={openHelpAt}
              onOpenRules={openDisclaimerReview}
              onOpenCalendar={openCalendarTab}
            />
          </div>
        )}
        {/* ⚠ 모바일 배치(웹/PWA)에서는 마운트하지 않는다 — 이 패널은 CSS로만 숨어 모바일 캘린더 탭과 두 벌이 되고,
            이동 요청(focusRequest)을 숨은 쪽이 먼저 소비하면 보이는 쪽이 그 날짜로 가지 않는다 */}
        {desktopTab === 'calendar' && !isMobile && (
          <div className="flex-1 min-h-0">
            <CalendarScreen layout={calendarWide ? 'wide' : 'stack'} focusRequest={calendarFocus} />
          </div>
        )}
        {desktopTab === 'chat' && (
          <ChatListScreen
            channels={channels}
            unreadCounts={unreadCounts}
            currentUser={currentUser}
            onlineUsers={onlineUsers}
            onChannelSelect={handleChannelSelect}
            onTogglePin={handleTogglePin}
            onToggleMute={handleToggleMute}
            onRenameChannel={handleRenameChannel}
            onLeaveChannel={handleLeaveChannel}
            onCreateGroup={openCreateGroupModal}
            onSelfChatClick={handleSelfChatClick}
            /* 오른쪽에 열려 있을 때만 강조 — X로 닫은 뒤에도 강조가 남으면 '열려 있는 대화방이 없습니다'와 어긋난다 */
            activeChannelId={isDesktopChatOpen ? activeChannel?.id ?? null : null}
            channelsSynced={channelsSynced}
            initialFilter={chatListFilterRequest ?? 'all'}
          />
        )}
        {desktopTab === 'activity' && (
          <ActivityPanel
            onOpenTarget={handleOpenActivityTarget}
            requestedTab={activityTabRequest}
            onOpenSystemFeed={handleOpenSystemFeed}
          />
        )}
        {desktopTab === 'org' && (
          <OnlineUsersPanel
            onlineUsers={onlineUsers}
            currentUser={currentUser}
            onUserClick={openUserProfile}
            onClose={closeDesktopOrg}
          />
        )}
      </div>
      <main className={`${
        isMobile && !isMobileChatOpen ? 'hidden md:flex'
          : isCompactDesktop && !compactShowsMain ? 'hidden'
          : listWide ? 'hidden'
          : 'flex'
      } flex-1 flex-col h-full min-w-0 bg-slate-50 relative`}>
        {/* 데스크톱: X로 채팅 영역을 닫은 상태 — 헤더 포함 전체를 안내 화면으로 대체.
            닫힘=비가시로 isChatVisibleRef에 반영되어 읽음/포커스가 멈추고 안읽음 뱃지가 정상 상승한다.
            열려 있어도 동기화가 끝났는데 activeChannel이 없으면(대화방 0개) 같은 안내 — 예전엔 "채널을 불러오는 중..."이 영원히 남았다.
            넓은 홈·컴팩트에서는 <main>이 숨어 있으므로 그리지 않는다 */}
        {!isMobile && systemFeed ? (
          // 출처별 봇 대화(가상 뷰) — 채팅 영역은 닫힌(비가시) 상태라 읽음·포커스가 멈춰 있다
          <SystemFeedView
            key={systemFeed.source}
            source={systemFeed.source}
            focusId={systemFeed.focusId}
            focusSeq={systemFeed.seq}
            onClose={closeSystemFeed}
            /* 컴팩트 배치는 한 칸이라 X(닫기) 대신 <(목록으로) 모양 — 이 prop은 버튼 모양만 바꾼다 */
            isMobile={isCompactDesktop}
          />
        ) : !isMobile && (!isDesktopChatOpen || (!activeChannel && channelsSynced)) ? (
          listWide || isCompactDesktop ? null : desktopTab === 'activity'
            ? <DesktopChatEmptyState variant="activity" />
            : <DesktopChatEmptyState onCreateGroup={openCreateGroupModal} />
        ) : (
        <>
        <header className="h-14 md:h-16 border-b border-slate-200 bg-white flex items-center px-3 md:px-6 justify-between shrink-0">
          <div className="flex items-center min-w-0">
            {/* 모바일·컴팩트(좁은 PC앱): 대화방 목록으로 뒤로가기. 넓은 데스크톱은 CSS로 숨고 X가 그 역할 */}
            <button
              className="md:hidden p-2 -ml-1 mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => (isMobile ? setIsMobileChatOpen(false) : setIsDesktopChatOpen(false))}
              aria-label="대화방 목록으로"
            >
              <ChevronLeft size={22} />
            </button>
            {activeChannel ? (() => {
              // 검색·조직도로 방금 연 DM은 activeChannel에 dmPartnerId/memberCount가 없을 수 있어
              // channels 배열의 완전한 객체를 우선한다(공지 배너와 같은 회피)
              const full = channels.find(c => c.id === activeChannel.id) ?? activeChannel;
              const partner = full.type === 'dm' ? findDmPartner(full, onlineUsers) : undefined;
              // 목록에 없으면 오프라인이다 — 서버가 오프라인 사용자를 onlineUsers에서 지운다
              const partnerStatus: PresenceStatus = partner?.status ?? 'offline';
              const partnerOnline = partnerStatus !== 'offline';
              const subtitle =
                full.type === 'dm'
                  ? [partnerOnline ? partner?.depart : null, STATUS_LABEL[partnerStatus]].filter(Boolean).join(' · ')
                  : full.type === 'self'
                    ? '나만 보는 메모·파일'
                    : full.type === 'public'
                      ? `공지 · 멤버 ${full.memberCount ?? 0}명`
                      : `멤버 ${full.memberCount ?? 0}명`;
              const headerTitle = full.type === 'self' ? '나와의 채팅' : full.type === 'dm' ? (dmPartnerName || full.name || '개인 메시지') : full.name;
              // 1:1 대화방은 헤더(사진+이름)를 누르면 상대 프로필. dmPartnerId가 없는 옛 DM(이름 매칭)은 대상을 몰라 정적 표시
              const partnerId = full.type === 'dm' && full.dmPartnerId != null ? String(full.dmPartnerId) : null;
              return (
                <div className="relative flex items-center gap-2.5 min-w-0">
                  {full.type === 'dm' ? (
                    <span className="relative shrink-0">
                      <img
                        src={resolveAvatarUrl(full.dmPartnerAvatar)}
                        onError={handleAvatarError}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover bg-slate-100"
                      />
                      <PresenceDot
                        status={partnerStatus}
                        className="absolute -right-0.5 -bottom-0.5 w-3 h-3 border-2 border-white"
                      />
                    </span>
                  ) : (
                    <ChannelTypeIcon type={full.type} size="sm" />
                  )}
                  <div className="flex flex-col min-w-0 leading-tight">
                    <h1 className="text-base md:text-lg font-bold text-slate-800 truncate">
                      {headerTitle}
                    </h1>
                    <span className="text-xs text-slate-500 truncate">{subtitle}</span>
                  </div>
                  {/* h1을 button 안에 넣을 수 없어(제목 요소는 버튼 내용이 될 수 없다) 사진+이름 위에 빈 버튼을 덮는다 */}
                  {partnerId && (
                    <button
                      type="button"
                      onClick={() => openUserProfile({
                        id: partnerId,
                        username: headerTitle,
                        avatar: full.dmPartnerAvatar || DEFAULT_AVATAR,
                        status: partnerStatus,
                        depart: partner?.depart,
                        affiliateId: partner?.affiliateId,
                      })}
                      aria-label={`${headerTitle} 프로필 보기`}
                      title="프로필 보기"
                      className="absolute -inset-1 rounded-lg hover:bg-slate-900/5 transition-colors"
                    />
                  )}
                </div>
              );
            })() : (
              <h1 className="text-base md:text-lg font-bold text-slate-400">채널을 선택하세요</h1>
            )}
          </div>
          <div className="flex items-center gap-0.5 md:gap-1.5 shrink-0">
            {/* 화상 통화 버튼 (DM/그룹 채팅에서만) — 아이콘 전용 */}
            {activeChannel && (activeChannel.type === 'dm' || activeChannel.type === 'group') && !activeCall && (
              <button
                onClick={() => handleStartCall(activeChannel)}
                className="hidden md:flex md:w-10 md:h-10 items-center justify-center text-slate-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
                title={channelCalls[String(activeChannel.id)]?.active ? '진행 중인 통화에 참여' : '화상 통화 시작'}
              >
                <Video size={20} />
              </button>
            )}

            {/* 그룹 채팅일 때만 멤버/초대 버튼 표시. 나가기는 대화방 목록 컨텍스트 메뉴로 이동 */}
            {activeChannel && activeChannel.type === 'group' && (
              <>
                <button
                  onClick={() => setIsMemberListModalOpen(true)}
                  className="hidden md:flex md:w-10 md:h-10 items-center justify-center text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title="멤버 목록"
                >
                  <Users size={20} />
                </button>
                <button
                  onClick={() => setIsInviteModalOpen(true)}
                  className="hidden md:flex md:w-10 md:h-10 items-center justify-center text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title="멤버 초대"
                >
                  <UserPlus size={20} />
                </button>
                <div className="hidden md:block w-px h-6 bg-slate-200" />
              </>
            )}
            {activeChannel && (
              <button
                onClick={() => setIsSearchOpen(prev => !prev)}
                className={`w-11 h-11 md:w-10 md:h-10 flex items-center justify-center rounded-lg transition-colors ${
                  isSearchOpen
                    ? 'text-brand-600 bg-brand-50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
                title="메시지 검색"
              >
                <Search size={20} />
              </button>
            )}
            {/* 사진·파일 서랍 (초안 DM은 서버에 없어 조회가 404) */}
            {activeChannel && !!activeChannel.id && (
              <button
                onClick={() => setIsDrawerOpen(prev => !prev)}
                className={`hidden md:flex md:w-10 md:h-10 items-center justify-center rounded-lg transition-colors ${
                  isDrawerOpen
                    ? 'text-brand-600 bg-brand-50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
                title="사진·파일 서랍"
              >
                <FolderOpen size={20} />
              </button>
            )}
            {/* 모바일: 검색 외 액션(통화·멤버·서랍·고정·알림·나가기)은 햄버거 드롭다운으로 묶음 */}
            {activeChannel && (
              <button
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setHeaderMenu({ x: r.right - 200, y: r.bottom + 4 });
                }}
                className={`md:hidden w-11 h-11 flex items-center justify-center rounded-lg transition-colors ${
                  headerMenu
                    ? 'text-brand-600 bg-brand-100'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
                aria-label="채팅방 메뉴"
                aria-haspopup="menu"
                aria-expanded={!!headerMenu}
              >
                <Menu size={20} />
              </button>
            )}
            {/* 데스크톱: 채팅 영역 닫기 (목록에서 방을 클릭하면 다시 열림) */}
            <button
              onClick={() => setIsDesktopChatOpen(false)}
              className="hidden md:flex w-10 h-10 items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="채팅 닫기"
            >
              <X size={20} />
            </button>
          </div>
        </header>

        {/* 진행 중인 통화 '통화 중 · 참여' — 벨을 놓쳤거나 거절했거나 연결이 끊긴 사람이 들어오는 길(서버 call:status) */}
        {activeChannel && (isMobile ? isMobileChatOpen : isDesktopChatOpen) && (() => {
          const st = channelCalls[String(activeChannel.id)];
          if (!st?.active || activeCall?.callId === st.callId) return null;
          return (
            <CallJoinBar
              status={st}
              meId={String(currentUser.id)}
              busy={!!activeCall}
              onJoin={() => { void handleJoinCall(activeChannel.id); }}
            />
          );
        })()}

        {/* 채널 공지 배너 — ChatWindow 밖(위)이라 내부 스크롤 로직에 영향 없음.
            notice는 channels에서 파생 (activeChannel state와 이중 동기화 방지) */}
        {activeChannel && (isMobile ? isMobileChatOpen : isDesktopChatOpen) && (() => {
          const notice = channels.find(c => c.id === activeChannel.id)?.notice;
          if (!notice) return null;
          const canManageNotice = activeChannel.type !== 'public' || !!currentUser.isAdmin;
          return (
            <NoticeBanner
              channelId={activeChannel.id}
              notice={notice}
              onJumpToMessage={jumpToMessage}
              onClearNotice={canManageNotice ? () => handleSetNotice(null) : undefined}
            />
          );
        })()}

        {/* 채팅방이 보일 때만 마운트 — display:none 상태에서 scrollHeight가 0으로 측정되어
            스크롤 위치가 깨지는 것을 fresh mount로 원천 차단 (모바일=전체화면 열림, 데스크톱=X 토글 열림) */}
        {activeChannel && (isMobile ? isMobileChatOpen : isDesktopChatOpen) ? (
          <ChatWindow
            messages={messages}
            currentUser={currentUser}
            channel={activeChannel}
            onSendMessage={handleSendMessage}
            onReactionToggle={handleReactionToggle}
            hasMore={hasMore}
            onLoadMore={loadOlderMessages}
            onDeleteMessage={handleDeleteMessage}
            isSearchOpen={isSearchOpen}
            onSearchOpenChange={setIsSearchOpen}
            onJumpToDate={jumpToDate}
            jumpTarget={jumpTarget}
            hasMoreNewer={hasMoreNewer}
            onLoadNewer={loadNewerMessages}
            onReturnToLatest={returnToLatest}
            onSetNotice={
              // 인라인 화살표를 쓰면 매 렌더 새 함수가 되어 React.memo(ChatWindow)가 항상 깨진다
              // — 다른 채널에 메시지 하나만 와도 현재 채널의 메시지 전체가 리렌더됐다.
              // handleSetNotice는 이미 useCallback이라 그대로 넘기면 참조가 안정적이다.
              activeChannel.type !== 'public' || currentUser.isAdmin
                ? handleSetNotice
                : undefined
            }
            onForward={handleOpenForward}
            forwardSelectRequest={forwardSelectRequest}
            scheduledMessages={myScheduled}
            onScheduleMessage={handleScheduleMessage}
            onUpdateScheduledMessage={handleUpdateScheduledMessage}
            onCancelScheduledMessage={handleCancelScheduledMessage}
            senderPresence={senderPresence}
            // 말풍선 아바타 → 프로필 모달(그 안의 '1:1 대화'가 기존 DM 또는 초안 DM을 연다).
            // 영상통화 사이드바의 ChatWindow에는 넘기지 않는다(통화 중 activeChannel 변경 금지)
            onOpenProfile={openUserProfile}
          />

        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            채널을 불러오는 중...
          </div>
        )}
        </>
        )}
      </main>
      {/* 모바일 채팅 헤더 햄버거 드롭다운 — 항목·라벨은 대화방 목록 롱프레스 메뉴(ChatListScreen)와 동일 */}
      {headerMenu && activeChannel && (() => {
        // 검색·조직도로 방금 연 DM은 activeChannel에 isPinned/isMuted가 없을 수 있어 channels 객체 우선(헤더 부제와 같은 회피)
        const full = channels.find(c => c.id === activeChannel.id) ?? activeChannel;
        const closeMenu = () => setHeaderMenu(null);
        const isGroup = full.type === 'group';
        const isDm = full.type === 'dm';
        return (
          <ContextMenu
            x={headerMenu.x}
            y={headerMenu.y}
            variant="light"
            backdrop
            onClose={closeMenu}
            items={[
              ...((isDm || isGroup) && !activeCall
                ? [{ key: 'call', label: channelCalls[String(full.id)]?.active ? '통화 참여' : '화상 통화', icon: Video, onSelect: () => { closeMenu(); handleStartCall(full); } }]
                : []),
              ...(isGroup
                ? [
                    { key: 'members', label: '멤버 목록', icon: Users, onSelect: () => { closeMenu(); setIsMemberListModalOpen(true); } },
                    { key: 'invite', label: '멤버 초대', icon: UserPlus, onSelect: () => { closeMenu(); setIsInviteModalOpen(true); } },
                  ]
                : []),
              // 초안 DM(id 0)은 서버에 없으므로 서랍·고정·알림·나가기를 모두 숨긴다
              ...(full.id
                ? [{ key: 'drawer', label: '사진·파일', icon: FolderOpen, onSelect: () => { closeMenu(); setIsDrawerOpen(true); } }]
                : []),
              // 고정 / 알림 / 이름 변경 / 나가기 — 대화방 목록·홈 컨텍스트 메뉴와 같은 빌더.
              // 나와의 채팅과 초안 DM(id 0)은 빌더가 빈 배열을 돌려준다
              ...buildChannelMenuItems(full, {
                onTogglePin: handleTogglePin,
                onToggleMute: handleToggleMute,
                onRenameChannel: handleRenameChannel,
                onLeaveChannel: handleLeaveChannel,
              }, closeMenu),
            ]}
          />
        );
      })()}
      {/* 사진·파일 서랍 (헤더 폴더 버튼 토글) */}
      {isDrawerOpen && activeChannel && !!activeChannel.id && (
        <AttachmentDrawer
          channelId={activeChannel.id}
          onClose={() => setIsDrawerOpen(false)}
          onJumpToMessage={(messageId) => {
            setIsDrawerOpen(false);
            jumpToMessage(messageId);
          }}
        />
      )}
      {/* 모바일 홈 탭 (콘텐츠 행 전체를 덮는 오버레이) */}
      {isMobile && mobileTab === 'home' && !isMobileChatOpen && (
        <PullToRefresh className="absolute inset-0 z-10 bg-slate-50 overflow-y-auto safe-area-top md:hidden">
          <HomeScreen
            currentUser={currentUser}
            channels={channels}
            unreadCounts={unreadCounts}
            onlineUsers={onlineUsers}
            onChannelSelect={handleChannelSelect}
            onSettingsClick={() => handleMobileTabChange('settings')}
            onUserClick={openUserProfile}
            onGoToChats={goToMobileChats}
            onCreateChat={openCreateGroupModal}
            channelsSynced={channelsSynced}
            onTogglePin={handleTogglePin}
            onToggleMute={handleToggleMute}
            onRenameChannel={handleRenameChannel}
            onLeaveChannel={handleLeaveChannel}
            onActivityClick={openMobileActivity}
            activityUnread={activityUnread}
            myStatus={myStatus}
            isMobileLayout
            onShowUnread={showMobileUnreadChats}
            onOpenOrg={openMobileOrg}
            onOpenHelp={openHelpAt}
            onOpenCalendar={openCalendarTab}
          />
        </PullToRefresh>
      )}
      {/* 모바일 '캘린더' 탭 — 월 격자(점) + 고른 날의 일정. 일정 추가는 머리의 + 버튼(빨강 FAB는 '새 대화' 하나뿐) */}
      {isMobile && mobileTab === 'calendar' && !isMobileChatOpen && (
        <div className="absolute inset-0 z-10 bg-white safe-area-top md:hidden">
          <CalendarScreen layout="stack" focusRequest={calendarFocus} />
        </div>
      )}
      {/* 모바일 '온라인' 탭 — 조직도(접속자). 탭이라 닫기 버튼 없음 */}
      {isMobile && mobileTab === 'org' && !isMobileChatOpen && (
        <div className="absolute inset-0 z-10 bg-white safe-area-top md:hidden">
          <OnlineUsersPanel
            onlineUsers={onlineUsers}
            currentUser={currentUser}
            onUserClick={openUserProfile}
          />
        </div>
      )}
      {/* 모바일 '설정' 탭 — 프로필·알림함·이용 안내·도움말·관리자·앱 정보·로그아웃 */}
      {isMobile && mobileTab === 'settings' && !isMobileChatOpen && (
        <div className="absolute inset-0 z-10 md:hidden">
          <MobileSettingsScreen
            currentUser={currentUser}
            activityUnread={activityUnread}
            onEditProfile={openAvatarSettings}
            onNotificationPrivacyChange={applyMyNotifyPrivacyLocally}
            myStatus={myStatus}
            myPresenceManual={myPresenceManual}
            onPresenceManualChange={applyMyPresenceManualLocally}
            onActivityClick={() => setIsMobileActivityOpen(true)}
            onRulesClick={openDisclaimerReview}
            onHelpClick={openHelp}
            onAdminConsoleClick={openAdminConsole}
            onLogout={requestLogout}
          />
        </div>
      )}
      {/* 모바일 통합 알림함 전체화면 — 홈 벨 아이콘에서 진입 (Phase 2, 조직도와 동일 depth 1) */}
      {isMobile && isMobileActivityOpen && !isMobileChatOpen && (
        <div className="absolute inset-0 z-20 bg-white safe-area-top md:hidden">
          <ActivityPanel
            onOpenTarget={handleOpenActivityTarget}
            requestedTab={activityTabRequest}
            onClose={() => setIsMobileActivityOpen(false)}
            onOpenSystemFeed={handleOpenSystemFeed}
          />
        </div>
      )}
      {/* 모바일 봇 대화 전체화면 — 알림함 위(depth 2), 뒤로가기는 알림함으로 */}
      {isMobile && systemFeed && !isMobileChatOpen && (
        <div className="absolute inset-0 z-30 bg-slate-50 safe-area-top md:hidden">
          <SystemFeedView
            key={systemFeed.source}
            source={systemFeed.source}
            focusId={systemFeed.focusId}
            focusSeq={systemFeed.seq}
            onClose={closeMobileSystemFeed}
            isMobile
          />
        </div>
      )}
      </div>
      {/* 모바일 하단 탭바 (채팅방 전체화면에서는 숨김) */}
      {isMobile && !isMobileChatOpen && !systemFeed && (
        <MobileTabBar active={mobileTab} onChange={handleMobileTabChange} unreadTotal={totalUnread} />
      )}

      {/* 일정 편집기·상세 창의 유일한 렌더 지점 — 홈 카드(PullToRefresh 안)·대화방 카드 어디서 열어도 탭바 위에 뜬다 */}
      <CalendarModalsHost currentUser={currentUser} channels={channels} />

      {/* 메시지 전달 대상 선택 모달 (Phase 2) */}
      {forwardSource && forwardSource.length > 0 && (
        <ForwardTargetModal
          sourceMessages={forwardSource}
          channels={channels}
          currentUser={currentUser}
          onConfirm={handleForwardConfirm}
          onClose={() => setForwardSource(null)}
          onSelectMore={forwardSource.length === 1 ? handleForwardSelectMore : undefined}
        />
      )}

      {/* 채팅방 나가기 확인 모달 */}
      {leaveTarget && (
        <ConfirmDialog
          isOpen
          title="채팅방 나가기"
          message={leaveTarget.type === 'dm'
            ? `'${leaveTarget.name}' 님과의 대화를 나가시겠습니까?\n대화 목록에서 사라지며, 이전 대화 내용은 다시 보이지 않습니다.`
            : `'${leaveTarget.name}' 채팅방을 나가시겠습니까?`}
          confirmLabel="나가기"
          danger
          onConfirm={confirmLeaveChannel}
          onCancel={() => setLeaveTarget(null)}
        />
      )}

      {/* 대화방 이름 변경 모달 (group만 — 컨텍스트 메뉴에서 진입) */}
      {renameTarget && (
        <RenameChannelModal
          isOpen
          channel={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSubmit={submitRename}
        />
      )}

      {/* 로그아웃 확인 모달 */}
      <ConfirmDialog
        isOpen={isLogoutConfirmOpen}
        title="로그아웃"
        message="로그아웃 하시겠습니까?"
        confirmLabel="로그아웃"
        danger
        checkbox={
          // 웹/PWA 전용 옵션 — Tauri는 device_tokens 기반 익명 알림 체계가 별도로 있음
          !isTauri()
            ? { label: '로그아웃 후에도 알림 받기', checked: keepPushOnLogout, onChange: setKeepPushOnLogout }
            : undefined
        }
        onConfirm={confirmLogout}
        onCancel={() => setIsLogoutConfirmOpen(false)}
      />

      {/* 채팅방 개설 모달 (1명 선택 시 DM, 2명 이상 그룹) */}
      <CreateGroupModal
        isOpen={isCreateGroupModalOpen}
        onClose={() => setIsCreateGroupModalOpen(false)}
        currentUser={currentUser}
        onlineUsers={onlineUsers}
        onGroupCreated={handleGroupCreated}
        onStartDM={handleUserClick}
      />

      {/* 멤버 초대 모달 */}
      {activeChannel && (
        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          channel={activeChannel}
          currentUser={currentUser}
          onlineUsers={onlineUsers}
          onMembersInvited={(members) => {
            console.log('Members invited:', members);
          }}
        />
      )}

      {/* 멤버 목록 모달 */}
      {activeChannel && (
        <MemberListModal
          isOpen={isMemberListModalOpen}
          onClose={() => setIsMemberListModalOpen(false)}
          channel={activeChannel}
          currentUser={currentUser}
          onlineUsers={onlineUsers}
          onUserClick={openUserProfile}
        />
      )}

      {/* 상대방 프로필 모달. '1:1 대화' 버튼은 그 사람과의 1:1 대화방이 이미 화면에 보일 때만 숨긴다 —
          모바일 조직도 탭처럼 같은 DM이 선택돼 있어도 채팅창이 안 보이면 눌러야 들어갈 수 있다 */}
      <UserProfileModal
        user={profileUser}
        status={
          profileUser
            ? onlineUsers.find(u => String(u.userId) === String(profileUser.id))?.status ?? 'offline'
            : 'offline'
        }
        onClose={() => setProfileUser(null)}
        currentUserId={String(currentUser.id)}
        allowRemoteSupport={!isMobile}
        onStartDm={
          // activeChannel에는 dmPartnerId가 없을 수 있어 channels의 완전한 객체를 우선한다(채팅 헤더와 같은 회피)
          profileUser &&
          (isMobile ? isMobileChatOpen : isDesktopChatOpen) &&
          activeChannel?.type === 'dm' &&
          String(
            channels.find(c => c.id === activeChannel.id)?.dmPartnerId ?? activeChannel.dmPartnerId
          ) === String(profileUser.id)
            ? undefined
            : startDmFromProfile
        }
      />

      {/* 아바타 설정 모달 */}
      <AvatarSettingsModal
        isOpen={isAvatarSettingsOpen}
        onClose={() => setIsAvatarSettingsOpen(false)}
        currentUser={currentUser}
        onAvatarChange={handleAvatarChange}
        onNotificationPrivacyChange={applyMyNotifyPrivacyLocally}
        onRulesClick={openDisclaimerReview}
      />

      {/* 채팅 이용 안내 면책 모달 — 동의(강제)와 재열람(닫기 가능)을 한 컴포넌트로 처리.
          동의가 필요한 상태(isDisclaimerOpen)가 우선이며, 그때는 onClose를 넘기지 않아 닫을 수 없다 */}
      <ChatDisclaimerModal
        isOpen={isDisclaimerOpen || isDisclaimerReviewOpen}
        onAgree={handleDisclaimerAgree}
        onClose={isDisclaimerOpen ? undefined : closeDisclaimerReview}
      />

      {/* 사용 설명서 */}
      {isHelpOpen && <HelpPage onClose={closeHelp} initialSectionId={helpSectionId} />}

      {/* Admin Console */}
      {currentUser.isAdmin && (
        <AdminConsole
          isOpen={isAdminConsoleOpen}
          onClose={() => setIsAdminConsoleOpen(false)}
          currentUser={currentUser}
        />
      )}

      {/* Notification Banners */}
      <NotificationSettingsGuide />
      {/* 감사 조회 통지는 super_admin에게만 간다(피감사자 통지는 2026-09에 제거). 서버 라우트도 requireSuperAdmin이라
          구세션(role 미저장)은 마운트해도 403만 받으므로 아예 마운트하지 않는다 */}
      {currentUser.role === 'super_admin' && (
        <AuditNotificationBanner userId={String(currentUser.id)} />
      )}

      {/* 수신 통화 배너 */}
      {incomingCall && (
        <IncomingCallBanner
          callInfo={incomingCall}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      {/* 원격지원 — 직원 쪽(PC 앱: 동의·송출·제어)과 담당자 쪽(뷰어). 세션 상태는 전부 이 안에 있다 */}
      <RemoteSupportRoot onOpenDm={handleUserClick} />

      {/* 화상 통화 창 — 채팅·녹화는 '지금 고른 방'이 아니라 **통화 중인 방** 기준이다(2026-11-04).
          예전엔 activeChannel을 그대로 넘겨, 통화 중 다른 방을 고르면 통화 창 채팅·녹화 영상이 그 방으로 갔다.
          게스트는 그 방의 멤버가 아니라 채팅·녹화·초대가 없다 */}
      {activeCall && (() => {
        const callRoom = activeCall.guest ? undefined : channels.find(c => String(c.id) === String(activeCall.channelId));
        const chatMatches = !!callRoom && !!activeChannel && String(activeChannel.id) === String(callRoom.id);
        return (
          <VideoCallWindow
            call={activeCall}
            currentUserId={String(currentUser.id)}
            onHangup={handleHangup}
            statusMessage={callMessage}
            onRetryParticipant={(userId) => callController.retry(userId)}
            messages={chatMatches ? messages : undefined}
            currentUser={currentUser}
            channel={chatMatches ? activeChannel ?? undefined : undefined}
            onSendMessage={chatMatches ? handleSendMessage : undefined}
            onOpenChat={callRoom && !chatMatches ? () => handleChannelSelect(callRoom) : undefined}
            onSendRecording={activeCall.guest ? undefined : (attachments) => {
              socketService.sendMessage(activeCall.channelId, '녹화 영상', 'file', undefined, attachments);
            }}
            onInvite={activeCall.canInvite && !activeCall.guest && activeCall.status === 'connected'
              ? () => setIsCallInviteOpen(true)
              : undefined}
            hasMore={hasMore}
            onLoadMore={loadOlderMessages}
            hasMoreNewer={hasMoreNewer}
            onLoadNewer={loadNewerMessages}
            onReturnToLatest={returnToLatest}
          />
        );
      })()}
      {activeCall && isCallInviteOpen && (
        <CallInviteModal
          isOpen
          onClose={() => setIsCallInviteOpen(false)}
          call={activeCall}
          currentUser={currentUser}
          onlineUsers={onlineUsers}
        />
      )}
    </div>
  );
};

export default App;